import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  Trash2,
  Loader2,
  AlertCircle,
  ChevronUp,
  Send,
  Clock,
  FileCheck,
  Download,
  User,
  RefreshCw,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import TrustpilotReviewModal from '../components/TrustpilotReviewModal'
import WizardStepNav from '../components/wizard/WizardStepNav'
import WizardSidebar, { type WizardSidebarRow } from '../components/wizard/WizardSidebar'
import WizardDetailsModal from '../components/wizard/WizardDetailsModal'
import SelfAssessmentPlanSelectionModal from '../components/SelfAssessmentPlanSelectionModal'
import HelpTooltip from '../components/HelpTooltip'
import { HELP_TEXT } from '../constants/helpText'
import { replaceYearReferences, summarizeHelpText } from '../utils/helpTextUtils'
import selfAssessmentApiService from '../utils/selfAssessmentApiService'
import { selfAssessmentService } from '../utils/selfAssessmentService'
import saOnlineService from '../utils/saOnlineService'
import paymentService from '../utils/paymentService'
import { hmrcAuthService } from '../utils/hmrcAuthService'
import type {
  SelfAssessmentIndividualDetailsResponse,
  SelfAssessmentObligationsResponse,
  SelfAssessmentCalculationResponse,
} from '../types'
import type {
  PersonalDetailsForm,
  TailorReturnForm,
  EmployerDetailsForm,
  SelfEmploymentAnnualData,
  SelfEmploymentPeriodData,
  PropertyPeriod,
  BenefitInKindKey,
  WizardStepId,
} from '../types/selfAssessment'
import { benefitsInKindFields, defaultBenefitsInKind } from '../types/selfAssessment'

// Extract the most specific error message from an API error response
function extractApiError(err: any, fallback: string = 'An error occurred. Please try again.'): string {
  // ApiError from our api client: err.response holds the raw JSON body
  // e.g. { response: { success: false, message: "...", errors: [{ code, message }] } }
  const apiErrBody = err?.response
  if (apiErrBody) {
    // Check for specific errors array (e.g. HMRC/SA Online validation errors)
    const errors = apiErrBody.response?.errors || apiErrBody.errors
    if (Array.isArray(errors) && errors.length > 0) {
      const messages = errors.map((e: any) => e.message).filter(Boolean)
      if (messages.length > 0) return messages.join('. ')
    }
    // Backend wraps errors in response.response.hmrcResponse.message
    if (apiErrBody.response?.hmrcResponse?.message) return apiErrBody.response.hmrcResponse.message
    // response.response.message
    if (apiErrBody.response?.message) return apiErrBody.response.message
    // Direct message on the body
    if (apiErrBody.message) return apiErrBody.message
    // data.error string
    if (typeof apiErrBody.error === 'string') return apiErrBody.error
  }
  // Axios-style error with nested response.data
  const data = err?.response?.data
  if (data) {
    if (data.response?.hmrcResponse?.message) return data.response.hmrcResponse.message
    if (data.response?.message) return data.response.message
    if (data.message) return data.message
    if (typeof data.error === 'string') return data.error
  }
  // Standard Error object
  if (err?.message) return err.message
  // Plain string
  if (typeof err === 'string') return err
  return fallback
}

/** Format a raw numeric string with commas for display (e.g. "114526325" → "114,526,325") */
function formatAmountDisplay(value: string): string {
  if (!value) return ''
  // Strip everything except digits and decimal point
  const clean = value.replace(/[^0-9.]/g, '')
  if (!clean) return ''
  const parts = clean.split('.')
  // Add commas to the integer part
  parts[0] = (parts[0] ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.length > 1 ? `${parts[0]}.${parts[1]}` : (parts[0] ?? '')
}

/** Strip commas from a formatted string for storage (e.g. "114,526,325.00" → "114526325.00") */
function stripCommas(value: string): string {
  return value.replace(/,/g, '')
}

/** Format with 2 decimal places on blur */
function formatAmountOnBlur(value: string): string {
  if (!value) return ''
  const clean = value.replace(/[^0-9.]/g, '')
  const num = parseFloat(clean)
  if (isNaN(num)) return value
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// --- SA Online (Government Gateway) submission config ---------------------
// The frontend always requests 'live'. The real target (HMRC test service vs
// live) is decided by the backend from its own config (HMRC_SA_ENVIRONMENT /
// NODE_ENV), so test builds can't hit HMRC live by accident.
const SA_SUBMISSION_ENVIRONMENT: 'live' | 'test' | 'test-in-live' = 'live'
// Polling bounds while HMRC processes an acknowledged submission.
const SA_POLL_MIN_INTERVAL_MS = 3000
const SA_POLL_MAX_INTERVAL_MS = 30000
const SA_POLL_DEFAULT_INTERVAL_MS = 10000
const SA_POLL_MAX_ATTEMPTS = 60 // hard cap so we never poll forever
// HMRC can take several minutes at busy times. Bound the wait by wall-clock
// rather than attempt count, so honouring a long PollInterval from HMRC doesn't
// shrink the window we actually wait (20 x 10s was only ~3 minutes).
const SA_POLL_MAX_TOTAL_MS = 10 * 60 * 1000
// Stop early if HMRC/our API is failing outright rather than simply pending —
// otherwise a hard failure is indistinguishable from "still processing".
const SA_POLL_MAX_CONSECUTIVE_ERRORS = 4

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// Keep a poll interval within sane bounds (HMRC sends its suggestion in seconds).
const clampPollInterval = (ms: number): number => {
  if (!ms || Number.isNaN(ms)) return SA_POLL_DEFAULT_INTERVAL_MS
  return Math.min(SA_POLL_MAX_INTERVAL_MS, Math.max(SA_POLL_MIN_INTERVAL_MS, ms))
}

// Extract a list of human-readable error strings from an SA submit/poll payload
// or a thrown API error, for surfacing to the user.
function extractSaErrorList(source: any): string[] {
  const raw =
    source?.errors ||
    source?.response?.errors ||
    source?.response?.response?.errors ||
    source?.data?.errors ||
    []
  if (!Array.isArray(raw)) return []
  return raw
    .map((e: any) => (typeof e === 'string' ? e : e?.message || e?.text || e?.code))
    .filter((m: any): m is string => typeof m === 'string' && m.length > 0)
}

type StepStatus = 'pending' | 'active' | 'completed'

interface WizardStep {
  number: number
  title: string
  status: StepStatus
}

// CollapsibleSection Component
interface CollapsibleSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
}

// CollapsibleSection component - currently unused but kept for potential future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CollapsibleSection({ title, children, defaultOpen = false, className = '' }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <div className="flex-shrink-0 ml-4">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  )
}

interface UserProfile {
  fullName: string
  utr: string
  nino: string
  address: string
}

// Types imported from ../types/selfAssessment

export default function SelfAssessmentFilingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [currentStep, setCurrentStep] = useState<WizardStepId>(() => {
    // Resume on the step recorded in the URL (?step=) after a refresh.
    const s = parseInt(new URLSearchParams(window.location.search).get('step') || '', 10)
    return (Number.isFinite(s) && s >= 1 && s <= 6 ? s : 1) as WizardStepId
  })
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Get UTR, NINO, taxYear, and planSlug from URL params
  const utrFromParams = searchParams.get('utr')
  const ninoFromParams = searchParams.get('nino')
  const taxYearFromParams = searchParams.get('taxYear') || '2024-25' // Default fallback
  const planSlugFromParams = searchParams.get('planSlug') || ''
  const assessmentIdFromParams = searchParams.get('assessmentId')
  const flowFromParams = searchParams.get('flow')
  const saReturnIdFromParams = searchParams.get('saReturnId')
  const submittedFromParams = searchParams.get('submitted') === 'true'

  // Smart flow detection: Prioritize explicit flow param, fallback to plan slug detection
  // If flow is explicitly 'traditional', it's Traditional MTD
  // If flow is explicitly 'sa', it's SA Online
  // Otherwise, detect from plan slug (if plan slug includes 'sa-', it's SA Online)
  const isSaFlow = flowFromParams === 'traditional' 
    ? false 
    : flowFromParams === 'sa' 
    ? true 
    : planSlugFromParams.includes('sa-')
  
  // Tax year and plan state
  const [taxYear, setTaxYear] = useState<string>(taxYearFromParams)
  const [planSlug, setPlanSlug] = useState<string>(planSlugFromParams)
  // Pricing plan picker — shown at the payment step when no plan was chosen
  // upstream (mirrors the CT600 flow's plan-selection modal).
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false)
  
  // API state
  const [assessmentId, setAssessmentId] = useState<string | null>(assessmentIdFromParams || null)
  const [saReturnId, setSaReturnId] = useState<string | null>(saReturnIdFromParams || null)
  const [businessId, setBusinessId] = useState<string | null>(null)
  const [calculationId, setCalculationId] = useState<string | null>(null)
  const [_individualDetails, _setIndividualDetails] = useState<SelfAssessmentIndividualDetailsResponse | null>(null)
  const [_obligations, setObligations] = useState<SelfAssessmentObligationsResponse | null>(null)
  const [calculationResults, setCalculationResults] = useState<SelfAssessmentCalculationResponse | null>(null)
  // Raw SA tax-calculation payload (the full breakdown from GET /calculate-tax:
  // incomeBreakdown, incomeTax, nationalInsurance, capitalGainsTax, studentLoan,
  // totalTaxDue, effectiveTaxRate). Kept alongside the MTD-shaped mapping so the
  // SA flow can show the complete breakdown.
  const [saCalculation, setSaCalculation] = useState<any>(null)
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  
  // User profile data (will be populated from API)
  // Always use URL params as source of truth for UTR and NINO
  const [userProfile, setUserProfile] = useState<UserProfile>({
    fullName: '',
    utr: utrFromParams || '',
    nino: ninoFromParams || '',
    address: ''
  })

  // Authentication state
  const [isHmrcAuthorized, setIsHmrcAuthorized] = useState(false)
  const [authCheckComplete, setAuthCheckComplete] = useState(false)
  const [_authError, setAuthError] = useState<string | null>(null)

  // Government Gateway credentials (for SA submission)
  const [governmentGatewayUserId, setGovernmentGatewayUserId] = useState('')
  const [governmentGatewayPassword, setGovernmentGatewayPassword] = useState('')

  // Payment and submission state
  const [isPaymentVerified, setIsPaymentVerified] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(submittedFromParams)
  const [submissionReceipt, setSubmissionReceipt] = useState<{
    referenceNumber: string
    submittedAt: string
    taxpayerName: string
  } | null>(null)
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false)
  // Live status while HMRC processes an acknowledged submission (polling).
  const [pollMessage, setPollMessage] = useState('')
  const [pollAttempt, setPollAttempt] = useState(0)
  // Validation / rejection errors returned by HMRC on submit or poll.
  const [showReviewPrompt, setShowReviewPrompt] = useState(false)
  // True when this tax year has already been paid for — a second return for the
  // same year (e.g. after discarding a draft) must not be charged again.
  const [isTaxYearCovered, setIsTaxYearCovered] = useState(false)
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([])
  // Set when a return was submitted (e.g. auto-submitted after payment) and is
  // still awaiting HMRC's final result — the user can poll for it by re-entering
  // their Government Gateway credentials.
  const [submissionNeedsPoll, setSubmissionNeedsPoll] = useState(false)

  // (Draft data is loaded from the backend via getReturn — see the
  // initialize-assessment effect below. No localStorage draft restore.)

  // Check payment status via API. This runs on load as soon as the form id is
  // known — NOT only on the submit step — because a *paid but unsubmitted*
  // draft must resume on the Submit step after a refresh. Otherwise the load
  // recomputes the first-incomplete step (the backend doesn't record that the
  // calculate/save steps are "done") and drops a paid user back on step 4,
  // where the payment check never fires and they're stuck.
  const paidJumpDoneRef = useRef(false)
  useEffect(() => {
    const submitStep = 6
    const formId = isSaFlow ? saReturnId : assessmentId
    if (!formId || isPaymentVerified) return

    let active = true
    const checkPaymentApi = async () => {
      try {
        const result = await paymentService.checkFormPayment(formId)
        if (!active) return
        if (result.success && result.data) {
          const paid = result.data.status === 'paid' || result.data.status === 'completed'
          if (paid) {
            setIsPaymentVerified(true)
            // A paid draft has completed the wizard up to payment — send the
            // user straight to the Submit step (once) so they enter their
            // Government Gateway credentials and submit. The one-time guard lets
            // them still navigate back to earlier steps afterwards.
            if (isSaFlow && !isSubmitted && !paidJumpDoneRef.current) {
              paidJumpDoneRef.current = true
              setCurrentStep(submitStep as WizardStepId)
              setSteps(prev =>
                prev.map(s => ({
                  ...s,
                  status:
                    s.number < submitStep
                      ? ('completed' as const)
                      : s.number === submitStep
                        ? ('active' as const)
                        : ('pending' as const),
                })),
              )
            }
          }
        }
      } catch {
        // 404 or error means not paid — leave isPaymentVerified as false
      }
    }
    checkPaymentApi()
    return () => { active = false }
  }, [isSaFlow, saReturnId, assessmentId, isPaymentVerified, isSubmitted])

  // Tax-year payment coverage. Mirrors the CT600 wizard, where coverage is
  // keyed on the company; for Self Assessment the period itself is the key.
  useEffect(() => {
    if (!taxYear) return
    let active = true
    const loadCoverage = async () => {
      const coverage = await paymentService.getTaxYearCoverage(taxYear)
      if (active) setIsTaxYearCovered(coverage.paid === true)
    }
    void loadCoverage()
    return () => {
      active = false
    }
  }, [taxYear])

  // Either this specific return is paid, or the tax year is already covered by
  // an earlier payment. Mirrors the CT600 wizard's `isPaid || isCovered`.
  const isPaidOrCovered = isPaymentVerified || isTaxYearCovered

  // (Post-payment redirect + submission status are reconciled from the backend
  //  via getReturn in the effect below — no localStorage flow-state.)

  // Reconcile submission status from the backend on load. The backend SAReturn is
  // the source of truth: this fetches the authoritative submission outcome
  // (submitted / processing / accepted / rejected + HMRC reference) so the result
  // is shown even after a refresh. Draft returns are left in edit mode.
  useEffect(() => {
    if (!isSaFlow || !saReturnId) return
    let active = true
    ;(async () => {
      try {
        const res = await saOnlineService.getReturn(saReturnId)
        if (!active) return
        const ret: any = res?.response?.data || res?.data || res || {}
        const status = (ret.status || '').toLowerCase()
        if (!['submitted', 'processing', 'accepted', 'rejected'].includes(status)) return // still a draft

        setIsSubmitted(true)
        setIsSubmitting(false)
        setCurrentStep(6 as WizardStepId)
        setSteps(prev =>
          prev.map(s => ({
            ...s,
            status: s.number < 6 ? ('completed' as const) : s.number === 6 ? ('active' as const) : ('pending' as const),
          })),
        )

        if (status === 'accepted' && ret.hmrcReference) {
          setSubmissionReceipt({
            referenceNumber: ret.hmrcReference,
            submittedAt: ret.acceptedAt || ret.submittedAt || new Date().toISOString(),
            taxpayerName: userProfile.fullName || 'Taxpayer',
          })
        } else if (status === 'rejected') {
          const errs = (ret.submissionErrors || [])
            .map((e: any) => (typeof e === 'string' ? e : e?.message))
            .filter(Boolean)
          if (errs.length) setSubmissionErrors(errs)
          setIsSubmitted(false) // rejected → back to the submit view with the errors
        } else if ((status === 'submitted' || status === 'processing') && ret.correlationId) {
          // Acknowledged by HMRC but not yet finalised — offer a status check.
          setSubmissionNeedsPoll(true)
        }
      } catch (e) {
        // Non-critical: leave the current in-memory state as-is.
        console.warn('Failed to sync SA submission status:', e)
      }
    })()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaFlow, saReturnId])

  // Check authentication based on flow type
  useEffect(() => {
    const checkAuthentication = async () => {
      setAuthCheckComplete(false)
      setAuthError(null)

      // SA Online Flow: Already authenticated via AuthenticatedLayout
      // No additional checks needed - just mark as complete
      if (isSaFlow) {
        setAuthCheckComplete(true)
        setIsHmrcAuthorized(false) // SA doesn't use HMRC auth
        return
      }

      // Traditional MTD Flow: Requires HMRC OAuth authorization
      if (!isSaFlow) {
        // Check HMRC OAuth authorization by attempting to get refresh token
        try {
          await hmrcAuthService.getRefreshToken()
          // If successful, user has HMRC authorization
          setIsHmrcAuthorized(true)
          setAuthCheckComplete(true)
        } catch (error) {
          // If error, user needs to authorize with HMRC
          console.log('HMRC authorization not found, requiring authorization')
          setIsHmrcAuthorized(false)
          setAuthError('Making Tax Digital (MTD) requires HMRC authorization. Please authorize with HMRC to continue.')
          setAuthCheckComplete(true)
        }
      }
    }

    checkAuthentication()
  }, [isSaFlow, navigate])

  // Update userProfile when URL params change (e.g., when user goes back and enters UTR)
  useEffect(() => {
    const currentUtr = searchParams.get('utr') || ''
    const currentNino = searchParams.get('nino') || ''
    setUserProfile(prev => {
      // Only update if values actually changed
      if (currentUtr !== prev.utr || currentNino !== prev.nino) {
        return {
          ...prev,
          utr: currentUtr,
          nino: currentNino
        }
      }
      return prev
    })
  }, [searchParams])


  // Auto-calculate tax when user reaches Step 4 (View your Calculation)
  useEffect(() => {
    const autoCalculateTax = async () => {
      // Only for SA flow and when on Step 5
      if (!isSaFlow || currentStep !== 4) {
        return
      }

      // Don't calculate if already calculated
      if (calculationResults) {
        return
      }

      // Need saReturnId to calculate
      if (!saReturnId) {
        toast.error('SA Return ID is required to calculate tax. Please go back and complete the previous steps.')
        return
      }

      try {
        setIsLoading(true)
        const calc = await saOnlineService.calculateTax(saReturnId)
        setSaCalculation(calc); setCalculationResults(mapSaCalculationToMtd(calc))
      } catch (calcErr: any) {
        console.error('Tax calculation failed:', calcErr)
        toast.error(extractApiError(calcErr, 'Failed to calculate tax. Please try again.'))
      } finally {
        setIsLoading(false)
      }
    }

    autoCalculateTax()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isSaFlow, saReturnId]) // Depend on currentStep, isSaFlow, and saReturnId

  // Wizard steps configuration - dynamic based on flow type
  const [steps, setSteps] = useState<WizardStep[]>(() => {
    const baseSteps = [
      { number: 1, title: 'Tell Us About You', status: 'active' as StepStatus },
      { number: 2, title: 'Tailor Your Return', status: 'pending' as StepStatus },
      { number: 3, title: 'Fill in Your Return', status: 'pending' as StepStatus },
      { number: 4, title: 'View your Calculation', status: 'pending' as StepStatus },
      { number: 5, title: 'Save Your Return', status: 'pending' as StepStatus },
    ]

    // Final submit step. Government Gateway credentials are collected here for
    // the SA flow (no separate HMRC Credentials step), so both flows end at 6.
    baseSteps.push({
      number: 6,
      title: 'Submit Your Return',
      status: 'pending' as StepStatus
    })
    
    return baseSteps
  })

  // Step 1 form state - Personal Details
  const [personalDetails, setPersonalDetails] = useState<PersonalDetailsForm>({
    firstName: '',
    middleName: '',
    lastName: '',
    telephoneNumber: '',
    emailAddress: '',
    companyType: '',
    address: {
      line1: '',
      line2: '',
      line3: '',
      line4: '',
      postcode: '',
      country: 'GB'
    },
    isAddressCorrect: 'yes',
    taxPayerResidency: 'english',
    dateOfBirth: '',
    maritalStatus: 'married',
    isBlind: 'no',
    blindLocalAuthority: '',
    hasStudentLoan: 'no',
    studentLoanPlanType: undefined,
    hasPostgraduateLoan: 'no'
  })

  // Helper to create a default employer entry
  const createDefaultEmployer = (name: string = '', index: number = 0): EmployerDetailsForm => ({
    employerId: `employer-${Date.now()}-${index}`,
    employerName: name,
    payeReference: '',
    payrollId: '',
    totalPay: '',
    totalTax: '',
    startDate: '',
    cessationDate: '',
    payFrequency: 'monthly',
    occupationalPension: 'no',
    studentLoanDeductions: { uglDeductionAmount: '', pglDeductionAmount: '' },
    benefitsInKind: { ...defaultBenefitsInKind },
    offPayrollWorker: 'no',
  })

  // Step 2 form state - Tailor Your Return
  const [tailorReturn, setTailorReturn] = useState<TailorReturnForm>({
    wasEmployee: 'yes',
    wasSelfEmployed: 'no',
    hasProperty: 'no',
    hasDividends: 'no',
    taxPayerResidency: 'english',
    numberOfEmployments: '1',
    employerNames: ['']
  })

  // Step 3 form state - Fill in Your Return (Employer details - one per employer)
  const [employerDetailsList, setEmployerDetailsList] = useState<EmployerDetailsForm[]>([createDefaultEmployer()])
  const [activeEmployerIndex, setActiveEmployerIndex] = useState(0)

  // Step 3 - Self Employment Reporting Type (Quarterly or Annual)
  const [selfEmploymentReportingType, setSelfEmploymentReportingType] = useState<'quarterly' | 'annual'>('annual')

  // Step 3 - Self Employment Period Data (for quarterly reporting)
  const [selfEmploymentPeriods, setSelfEmploymentPeriods] = useState<SelfEmploymentPeriodData[]>([])

  // Step 3 - Self Employment Annual Submission Data (for annual reporting)
  const [selfEmploymentAnnualData, setSelfEmploymentAnnualData] = useState<SelfEmploymentAnnualData>({
    adjustments: {
      includedNonTaxableProfits: '',
      basisAdjustment: '',
      overlapReliefUsed: '',
      accountingAdjustment: '',
      averagingAdjustment: ''
    },
    allowances: {
      annualInvestmentAllowance: '',
      otherCapitalAllowance: '',
      businessPremisesRenovationAllowance: '',
      costOfReplacingDomesticGoods: '',
      zeroEmissionGoodsVehicleAllowance: '',
      enhancedCapitalAllowance: '',
      allowanceOnSales: '',
      capitalAllowanceMainPool: '',
      capitalAllowanceSpecialRatePool: '',
      tradingIncomeAllowance: ''
    },
    nonFinancials: {
      class4NicExempt: 'no',
      exemptionCode: '',
      payeEmployed: 'no'
    }
  })

  // Step 3 - UK Property Period Data
  const [propertyPeriods, setPropertyPeriods] = useState<PropertyPeriod[]>([])

  // Step 3 - Dividends Data
  const [dividendsIncome, setDividendsIncome] = useState({
    ukDividends: '',
    otherUkDividends: ''
  })

  // studentLoanDeductions, benefitsInKind, offPayrollWorker are now per-employer inside employerDetailsList

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Helper function to get help text with dynamic year replacement
  const getHelpText = (key: keyof typeof HELP_TEXT, useSummary: boolean = true): string => {
    const text = HELP_TEXT[key] || ''
    const withYear = replaceYearReferences(text, taxYear)
    return useSummary ? summarizeHelpText(withYear, 300) : withYear
  }

  // For SA Online, self-employment income/expenses are captured once (annual),
  // not per quarter. We store them in a single period entry so they flow through
  // the existing SA payload builder (buildSaReturnPayload reads selfEmploymentPeriods).
  const updateAnnualSePeriod = (field: keyof SelfEmploymentPeriodData, value: string) => {
    setSelfEmploymentPeriods(prev => {
      if (prev.length === 0) {
        return [{
          periodId: 'se-period-annual',
          fromDate: '',
          toDate: '',
          turnover: '',
          otherIncome: '',
          costOfGoods: '',
          staffCosts: '',
          travelCosts: '',
          premisesCosts: '',
          [field]: value,
        }]
      }
      return prev.map((p, i) => (i === 0 ? { ...p, [field]: value } : p))
    })
  }

  /**
   * Tax year as ISO dates: '2025-26' -> 6 Apr 2025 .. 5 Apr 2026.
   *
   * The SA Online flow captures self-employment annually, so it has no period
   * date inputs — the accounting period IS the tax year. Without this the
   * payload sent empty strings, which fail the backend's @IsDateString() and
   * made every save fail with "Failed to save your return".
   */
  const getTaxYearIsoDates = (year: string): { start: string; end: string } => {
    const [startYear] = (year || '2024-25').split('-')
    const startYearNum = parseInt(startYear || '2024', 10)
    return {
      start: `${startYearNum}-04-06`,
      end: `${startYearNum + 1}-04-05`,
    }
  }

  // Helper function to get tax year dates for display
  const getTaxYearDates = () => {
    const [startYear] = (taxYear || '2024-25').split('-')
    const startYearNum = parseInt(startYear || '2024')
    const endYearNum = startYearNum + 1
    return {
      startDate: `6 April ${startYearNum}`,
      endDate: `5 April ${endYearNum}`,
      yearRange: `6 April ${startYearNum} to 5 April ${endYearNum}`
    }
  }

  // Initialize the assessment when component mounts
  useEffect(() => {
    const initializeAssessment = async () => {
      // Backend-first: whenever we have an id in the URL, load the return from
      // the server (source of truth) — on refresh, resume, or from History.
      if (saReturnIdFromParams || assessmentIdFromParams) {
        try {
          setIsLoading(true)
          setError(null)

          // Branch: SA Online return (fetched via saOnlineService)
          if (saReturnIdFromParams && isSaFlow) {
            const res = await saOnlineService.getReturn(saReturnIdFromParams)
            const saReturn = res?.response?.data || res?.data || res || {}

            // Set core IDs
            setSaReturnId(saReturnIdFromParams)
            if (saReturn.assessmentId) setAssessmentId(saReturn.assessmentId)
            if (saReturn.taxYear) setTaxYear(saReturn.taxYear)

            // Set NINO/UTR from SA online return data
            const nino = saReturn.personalDetails?.nationalInsuranceNumber || ninoFromParams || ''
            const utr = saReturn.uniqueTaxpayerReference || searchParams.get('utr') || ''
            setUserProfile(prev => ({ ...prev, nino, utr }))

            // ── Map personal details ──
            if (saReturn.personalDetails) {
              const pd = saReturn.personalDetails
              const taxpayerStatusMap: Record<string, 'english' | 'scottish' | 'welsh'> = { C: 'english', S: 'scottish', U: 'welsh' }
              const senderTypeMap: Record<string, string> = { Individual: 'sole-trader', Company: 'limited', Partnership: 'partnership' }
              setPersonalDetails(prev => ({
                ...prev,
                firstName: pd.firstName || prev.firstName,
                middleName: pd.middleName || prev.middleName,
                lastName: pd.lastName || prev.lastName,
                dateOfBirth: pd.dateOfBirth || prev.dateOfBirth,
                telephoneNumber: pd.telephoneNumber || prev.telephoneNumber,
                emailAddress: pd.email || prev.emailAddress,
                companyType: senderTypeMap[saReturn.senderType] || prev.companyType,
                taxPayerResidency: taxpayerStatusMap[pd.taxpayerStatus] || prev.taxPayerResidency,
                address: pd.address ? {
                  line1: pd.address.line1 || '',
                  line2: pd.address.line2 || '',
                  line3: pd.address.line3 || '',
                  line4: pd.address.line4 || '',
                  postcode: pd.address.postcode || '',
                  country: pd.address.country || '',
                } : prev.address,
              }))
            }

            // ── Map employments (SA Online format) ──
            if (Array.isArray(saReturn.employments) && saReturn.employments.length > 0) {
              const employers = saReturn.employments.map((emp: any, idx: number) => {
                const bik: Record<string, string> = { ...defaultBenefitsInKind }
                if (emp.benefits) {
                  Object.entries(emp.benefits).forEach(([k, v]) => {
                    if (v !== undefined && v !== null) bik[k] = String(v)
                  })
                }
                return {
                  employerId: emp._id || emp.id || `employer-${Date.now()}-${idx}`,
                  employerName: emp.employerName || '',
                  payeReference: emp.employerPAYEReference || '',
                  payrollId: emp.payrollId || '',
                  totalPay: emp.payFromEmployment != null ? String(emp.payFromEmployment) : '',
                  totalTax: emp.taxDeducted != null ? String(emp.taxDeducted) : '',
                  startDate: emp.employmentStartDate || '',
                  cessationDate: emp.employmentEndDate || '',
                  payFrequency: emp.payFrequency || 'monthly',
                  occupationalPension: emp.occupationalPension ? 'yes' : 'no' as const,
                  studentLoanDeductions: {
                    uglDeductionAmount: emp.uglDeductionAmount != null ? String(emp.uglDeductionAmount) : '',
                    pglDeductionAmount: emp.pglDeductionAmount != null ? String(emp.pglDeductionAmount) : '',
                  },
                  benefitsInKind: bik,
                  offPayrollWorker: emp.offPayrollWorker ? 'yes' : 'no' as const,
                } as EmployerDetailsForm
              })
              setEmployerDetailsList(employers)
              setTailorReturn(prev => ({
                ...prev,
                wasEmployee: 'yes',
                numberOfEmployments: String(employers.length),
                employerNames: employers.map((e: EmployerDetailsForm) => e.employerName),
              }))
            }

            // ── Map self-employments (SA Online format) ──
            if (Array.isArray(saReturn.selfEmployments) && saReturn.selfEmployments.length > 0) {
              setTailorReturn(prev => ({ ...prev, wasSelfEmployed: 'yes' }))
              const periods = saReturn.selfEmployments.map((se: any, idx: number) => ({
                periodId: se._id || se.id || `period-${Date.now()}-${idx}`,
                fromDate: se.accountingPeriodStartDate || se.businessStartDate || '',
                toDate: se.accountingPeriodEndDate || '',
                turnover: se.turnover != null ? String(se.turnover) : '',
                otherIncome: se.otherBusinessIncome != null ? String(se.otherBusinessIncome) : '',
                costOfGoods: se.costOfGoodsBought != null ? String(se.costOfGoodsBought) : '',
                staffCosts: se.wages != null ? String(se.wages) : '',
                travelCosts: se.travelCosts != null ? String(se.travelCosts) : '',
                premisesCosts: se.premisesRunningCosts != null ? String(se.premisesRunningCosts) : '',
              } as SelfEmploymentPeriodData))
              setSelfEmploymentPeriods(periods)
            }

            // ── Map property business (SA Online format — aggregated, not per-period) ──
            if (saReturn.propertyBusiness) {
              const pb = saReturn.propertyBusiness
              if (pb.ukPropertyIncome != null || pb.ukPropertyExpenses != null) {
                setTailorReturn(prev => ({ ...prev, hasProperty: 'yes' }))
                setPropertyPeriods([{
                  periodId: `property-${Date.now()}`,
                  fromDate: '',
                  toDate: '',
                  rentIncome: pb.ukPropertyIncome != null ? String(pb.ukPropertyIncome) : '',
                  premiums: '',
                  premisesRunningCosts: pb.ukPropertyExpenses != null ? String(pb.ukPropertyExpenses) : '',
                  repairsMaintenance: '',
                  legalManagementCosts: '',
                } as PropertyPeriod])
              }
            }

            // ── Map dividends (SA Online format) ──
            if (saReturn.dividendsIncome) {
              const div = saReturn.dividendsIncome
              if (div.ukDividends != null || div.otherUkDividends != null) {
                setTailorReturn(prev => ({ ...prev, hasDividends: 'yes' }))
                setDividendsIncome({
                  ukDividends: div.ukDividends != null ? String(div.ukDividends) : '',
                  otherUkDividends: div.otherUkDividends != null ? String(div.otherUkDividends) : '',
                })
              }
            }

            // ── Check payment status ──
            let paidVerified = false
            try {
              const paymentResult = await paymentService.checkFormPayment(saReturnIdFromParams)
              if (paymentResult.success && paymentResult.data) {
                paidVerified = paymentResult.data.status === 'paid' || paymentResult.data.status === 'completed'
                if (paidVerified) setIsPaymentVerified(true)
              }
            } catch {
              // Not paid or error — leave as false
            }

            // ── Determine completed steps (SA Online) ──
            const completed = new Set<number>()
            completed.add(1)
            const hasEmployments = Array.isArray(saReturn.employments) && saReturn.employments.length > 0
            const hasSelfEmployment = Array.isArray(saReturn.selfEmployments) && saReturn.selfEmployments.length > 0
            const hasProperty = saReturn.propertyBusiness && (saReturn.propertyBusiness.ukPropertyIncome != null || saReturn.propertyBusiness.ukPropertyExpenses != null)
            const hasDividends = saReturn.dividendsIncome && (saReturn.dividendsIncome.ukDividends != null || saReturn.dividendsIncome.otherUkDividends != null)
            if (hasEmployments || hasSelfEmployment || hasProperty || hasDividends) {
              completed.add(2)
              completed.add(3)
            }
            if (hasEmployments || hasSelfEmployment || hasProperty) {
              completed.add(3)
            }
            // SA online returns may have calculation data
            if (saReturn.calculation) {
              completed.add(4)
              completed.add(5)
              setSaCalculation(saReturn.calculation); setCalculationResults(mapSaCalculationToMtd(saReturn.calculation))
            }
            // Check submitted status — rely ONLY on the backend status. (The
            // declaration flag is always true in the saved payload, even for
            // drafts, so it must NOT be treated as "submitted".)
            const saStatus = (saReturn.status || '').toLowerCase()
            const isAlreadySubmitted = ['submitted', 'processing', 'accepted', 'rejected'].includes(saStatus)
            if (isAlreadySubmitted) {
              completed.add(6)
              setIsSubmitted(true)
            }

            const firstIncomplete = [...steps.map(s => s.number)].find(n => !completed.has(n)) || steps[steps.length - 1]!.number
            // Where to resume, in priority order:
            //  1. Paid OR already-submitted → the Submit step (6). Payment
            //     happens at submit, so a paid draft has completed the wizard;
            //     a submitted return obviously belongs on the submit/result
            //     view. The backend doesn't record the calc/save steps as
            //     "done", so firstIncomplete would otherwise wrongly drop the
            //     user on step 4 in both cases (and race the submitted-reconcile
            //     effect below).
            //  2. An explicit ?step= from the URL.
            //  3. Otherwise the first incomplete step.
            const urlStep = parseInt(new URLSearchParams(window.location.search).get('step') || '', 10)
            const urlLanding = Number.isFinite(urlStep) && urlStep >= 1 && urlStep <= 6 ? urlStep : firstIncomplete
            const goToSubmit = paidVerified || isAlreadySubmitted
            const landing = goToSubmit ? 6 : urlLanding
            if (goToSubmit) {
              paidJumpDoneRef.current = true
              // Everything up to submit is behind them — mark it complete.
              for (let n = 1; n < 6; n++) completed.add(n)
            }
            setCurrentStep(landing as WizardStepId)
            setSteps(prev => prev.map(s => ({
              ...s,
              status: completed.has(s.number)
                ? 'completed' as const
                : s.number === landing
                ? 'active' as const
                : 'pending' as const,
            })))

          } else if (assessmentIdFromParams) {
            // Branch: MTD assessment (fetched via selfAssessmentService)
            const res = await selfAssessmentService.getAssessment(assessmentIdFromParams)
            const assessment = res?.response?.data || res?.response || {}

            // Set core IDs
            setAssessmentId(assessmentIdFromParams)
            if (assessment.saReturnId) setSaReturnId(assessment.saReturnId)
            if (assessment.taxYear) setTaxYear(assessment.taxYear)

            // Set NINO/UTR from assessment data
            const nino = assessment.nino || ninoFromParams || ''
            const utr = assessment.utr || searchParams.get('utr') || ''
            setUserProfile(prev => ({ ...prev, nino, utr }))

            // ── Map employments ──
            if (Array.isArray(assessment.employments) && assessment.employments.length > 0) {
              const employers = assessment.employments.map((emp: any, idx: number) => {
                const p = emp.payload || emp
                // Employment financial data is nested under payload.employment
                const empData = p.employment || {}
                const pay = empData.pay || {}
                const deductions = empData.deductions || {}
                const studentLoans = deductions.studentLoans || {}
                const bik: Record<string, string> = { ...defaultBenefitsInKind }
                if (empData.benefitsInKind) {
                  Object.entries(empData.benefitsInKind).forEach(([k, v]) => {
                    if (v !== undefined && v !== null) bik[k] = String(v)
                  })
                }
                return {
                  employerId: emp.id || emp._id || `employer-${Date.now()}-${idx}`,
                  employerName: p.employerName || '',
                  payeReference: p.employerRef || p.payeReference || '',
                  payrollId: p.payrollId || '',
                  totalPay: pay.taxablePayToDate != null ? String(pay.taxablePayToDate) : (p.totalPayToDate != null ? String(p.totalPayToDate) : ''),
                  totalTax: pay.totalTaxToDate != null ? String(pay.totalTaxToDate) : (p.totalTaxToDate != null ? String(p.totalTaxToDate) : ''),
                  startDate: p.startDate || '',
                  cessationDate: p.cessationDate || '',
                  payFrequency: p.payFrequency || 'monthly',
                  occupationalPension: p.occupationalPension ? 'yes' : 'no' as const,
                  studentLoanDeductions: {
                    uglDeductionAmount: studentLoans.uglDeductionAmount != null ? String(studentLoans.uglDeductionAmount) : '',
                    pglDeductionAmount: studentLoans.pglDeductionAmount != null ? String(studentLoans.pglDeductionAmount) : '',
                  },
                  benefitsInKind: bik,
                  offPayrollWorker: empData.offPayrollWorker ? 'yes' : 'no' as const,
                } as EmployerDetailsForm
              })
              setEmployerDetailsList(employers)
              setTailorReturn(prev => ({
                ...prev,
                wasEmployee: 'yes',
                numberOfEmployments: String(employers.length),
                employerNames: employers.map((e: EmployerDetailsForm) => e.employerName),
              }))
            }

            // ── Map self-employment ──
            if (Array.isArray(assessment.selfEmployment) && assessment.selfEmployment.length > 0) {
              setTailorReturn(prev => ({ ...prev, wasSelfEmployed: 'yes' }))

              // Separate period entries from annual entries
              const periodEntries = assessment.selfEmployment.filter((se: any) => !se.meta?.annual && se.payload?.periodDates)
              const annualEntry = assessment.selfEmployment.find((se: any) => se.meta?.annual)

              // Map period entries (expense fields are flat numbers, not nested objects)
              if (periodEntries.length > 0) {
                const periods = periodEntries.map((se: any, idx: number) => {
                  const p = se.payload || se
                  const dates = p.periodDates || {}
                  const income = p.periodIncome || {}
                  const expenses = p.periodExpenses || {}
                  return {
                    periodId: se.id || se._id || `period-${Date.now()}-${idx}`,
                    fromDate: dates.periodStartDate || '',
                    toDate: dates.periodEndDate || '',
                    turnover: income.turnover != null ? String(income.turnover) : '',
                    otherIncome: income.other != null ? String(income.other) : '',
                    costOfGoods: expenses.costOfGoods != null ? String(expenses.costOfGoods) : '',
                    staffCosts: expenses.wagesAndStaffCosts != null ? String(expenses.wagesAndStaffCosts) : '',
                    travelCosts: expenses.carVanTravelExpenses != null ? String(expenses.carVanTravelExpenses) : '',
                    premisesCosts: expenses.premisesRunningCosts != null ? String(expenses.premisesRunningCosts) : '',
                  } as SelfEmploymentPeriodData
                })
                setSelfEmploymentPeriods(periods)
              }

              // Map annual data from annual entry
              if (annualEntry) {
                const ap = annualEntry.payload || annualEntry
                const adj = ap.adjustments || {}
                const all = ap.allowances || {}
                setSelfEmploymentAnnualData(prev => ({
                  adjustments: {
                    includedNonTaxableProfits: adj.includedNonTaxableProfits != null ? String(adj.includedNonTaxableProfits) : prev.adjustments.includedNonTaxableProfits,
                    basisAdjustment: adj.basisAdjustment != null ? String(adj.basisAdjustment) : prev.adjustments.basisAdjustment,
                    overlapReliefUsed: adj.overlapReliefUsed != null ? String(adj.overlapReliefUsed) : prev.adjustments.overlapReliefUsed,
                    accountingAdjustment: adj.accountingAdjustment != null ? String(adj.accountingAdjustment) : prev.adjustments.accountingAdjustment,
                    averagingAdjustment: adj.averagingAdjustment != null ? String(adj.averagingAdjustment) : prev.adjustments.averagingAdjustment,
                  },
                  allowances: {
                    annualInvestmentAllowance: all.annualInvestmentAllowance != null ? String(all.annualInvestmentAllowance) : prev.allowances.annualInvestmentAllowance,
                    otherCapitalAllowance: all.otherCapitalAllowance != null ? String(all.otherCapitalAllowance) : prev.allowances.otherCapitalAllowance,
                    businessPremisesRenovationAllowance: all.businessPremisesRenovationAllowance != null ? String(all.businessPremisesRenovationAllowance) : prev.allowances.businessPremisesRenovationAllowance,
                    costOfReplacingDomesticGoods: all.costOfReplacingDomesticGoods != null ? String(all.costOfReplacingDomesticGoods) : prev.allowances.costOfReplacingDomesticGoods,
                    zeroEmissionGoodsVehicleAllowance: (all.zeroEmissionsGoodsVehicleAllowance ?? all.zeroEmissionGoodsVehicleAllowance) != null ? String(all.zeroEmissionsGoodsVehicleAllowance ?? all.zeroEmissionGoodsVehicleAllowance) : prev.allowances.zeroEmissionGoodsVehicleAllowance,
                    enhancedCapitalAllowance: all.enhancedCapitalAllowance != null ? String(all.enhancedCapitalAllowance) : prev.allowances.enhancedCapitalAllowance,
                    allowanceOnSales: all.allowanceOnSales != null ? String(all.allowanceOnSales) : prev.allowances.allowanceOnSales,
                    capitalAllowanceMainPool: all.capitalAllowanceMainPool != null ? String(all.capitalAllowanceMainPool) : prev.allowances.capitalAllowanceMainPool,
                    capitalAllowanceSpecialRatePool: all.capitalAllowanceSpecialRatePool != null ? String(all.capitalAllowanceSpecialRatePool) : prev.allowances.capitalAllowanceSpecialRatePool,
                    tradingIncomeAllowance: all.tradingIncomeAllowance != null ? String(all.tradingIncomeAllowance) : prev.allowances.tradingIncomeAllowance,
                  },
                  nonFinancials: prev.nonFinancials,
                }))
              }
            }

            // ── Map property business ──
            if (Array.isArray(assessment.propertyBusiness) && assessment.propertyBusiness.length > 0) {
              setTailorReturn(prev => ({ ...prev, hasProperty: 'yes' }))
              const props = assessment.propertyBusiness.map((pb: any, idx: number) => {
                const p = pb.payload || pb
                const income = p.income || {}
                const expenses = p.expenses || {}
                return {
                  periodId: pb._id || `property-${Date.now()}-${idx}`,
                  fromDate: p.fromDate || p.periodStartDate || '',
                  toDate: p.toDate || p.periodEndDate || '',
                  rentIncome: income.rentIncome != null ? String(income.rentIncome) : '',
                  premiums: income.premiumsOfLeaseGrant != null ? String(income.premiumsOfLeaseGrant) : '',
                  premisesRunningCosts: expenses.premisesRunningCosts != null ? String(expenses.premisesRunningCosts) : '',
                  repairsMaintenance: expenses.repairsAndMaintenance != null ? String(expenses.repairsAndMaintenance) : '',
                  legalManagementCosts: expenses.professionalFees != null ? String(expenses.professionalFees) : '',
                } as PropertyPeriod
              })
              setPropertyPeriods(props)
            }

            // ── Map dividends ──
            if (assessment.dividends?.payload) {
              const divPayload = assessment.dividends.payload
              setTailorReturn(prev => ({ ...prev, hasDividends: 'yes' }))
              setDividendsIncome({
                ukDividends: divPayload.ukDividends != null ? String(divPayload.ukDividends) : '',
                otherUkDividends: divPayload.otherUkDividends != null ? String(divPayload.otherUkDividends) : '',
              })
            }

            // ── Map calculation results ──
            if (assessment.calculation?.payload) {
              const calc = assessment.calculation.payload
              setSaCalculation(calc); setCalculationResults(mapSaCalculationToMtd(calc))
            }

            // ── Check payment status ──
            try {
              const paymentResult = await paymentService.checkFormPayment(assessmentIdFromParams)
              if (paymentResult.success && paymentResult.data) {
                const paid = paymentResult.data.status === 'paid' || paymentResult.data.status === 'completed'
                if (paid) setIsPaymentVerified(true)
              }
            } catch {
              // Not paid or error — leave as false
            }

            // ── Determine completed steps ──
            const completed = new Set<number>()
            completed.add(1)
            const hasEmployments = Array.isArray(assessment.employments) && assessment.employments.length > 0
            const hasSelfEmployment = Array.isArray(assessment.selfEmployment) && assessment.selfEmployment.length > 0
            const hasProperty = Array.isArray(assessment.propertyBusiness) && assessment.propertyBusiness.length > 0
            if (hasEmployments || hasSelfEmployment || hasProperty || assessment.dividends) {
              completed.add(2)
            }
            if (hasEmployments || hasSelfEmployment || hasProperty || assessment.dividends) {
              completed.add(3)
            }
            if (hasEmployments || hasSelfEmployment || hasProperty) {
              completed.add(3)
            }
            if (assessment.calculation?.payload) {
              completed.add(4)
            }
            if (assessment.calculation?.payload) {
              completed.add(5)
            }
            const assessmentStatus = assessment.status || ''
            const isFinalDeclared = assessment.calculation?.payload?.finalDeclaration
            const isAlreadySubmitted = isFinalDeclared || assessmentStatus === 'submitted' || assessmentStatus === 'accepted' || assessmentStatus === 'rejected'
            if (isAlreadySubmitted) {
              completed.add(6)
              setIsSubmitted(true)
            }

            const firstIncomplete = [...steps.map(s => s.number)].find(n => !completed.has(n)) || steps[steps.length - 1]!.number
            setCurrentStep(firstIncomplete as WizardStepId)
            setSteps(prev => prev.map(s => ({
              ...s,
              status: completed.has(s.number)
                ? 'completed' as const
                : s.number === firstIncomplete
                ? 'active' as const
                : 'pending' as const,
            })))
          }

        } catch (err: any) {
          console.error('Error loading assessment from history:', err)
          toast.error(extractApiError(err, 'Failed to load assessment. Please try again.'))
        } finally {
          setIsLoading(false)
          setIsInitializing(false)
        }
        return
      }

      // Use URL params as source of truth
      const currentUtr = searchParams.get('utr') || userProfile.utr || ''
      const currentNino = searchParams.get('nino') || userProfile.nino || ''

      if (!currentNino || !currentUtr) {
        toast.error('Missing NINO or UTR. Please go back and try again.')
        setIsInitializing(false)
        return
      }
      
      // Update userProfile with current values
      if (currentUtr !== userProfile.utr || currentNino !== userProfile.nino) {
        setUserProfile(prev => ({
          ...prev,
          utr: currentUtr,
          nino: currentNino
        }))
      }

      // Check if assessmentId exists in URL params but not in state
      const assessmentIdFromUrl = searchParams.get('assessmentId')
      if (assessmentIdFromUrl && !assessmentId) {
        console.log('Found assessmentId in URL params, setting it:', assessmentIdFromUrl)
        setAssessmentId(assessmentIdFromUrl)
        localStorage.setItem('currentSelfAssessmentId', assessmentIdFromUrl)
      }

      try {
        setIsLoading(true)
        setError(null)

        // Get individual details from HMRC
        // Note: The API doesn't have a specific endpoint for individual details by UTR
        // We'll use the business details endpoint with NINO instead
        // For now, we'll skip this call and use the data from URL params
        // const details = await selfAssessmentApiService.getIndividualDetails(userProfile.utr)
        // setIndividualDetails(details)

        // Populate personal details from user profile (from previous page)
        setPersonalDetails(prev => ({
          ...prev,
          firstName: userProfile.fullName.split(' ')[0] || '',
          lastName: userProfile.fullName.split(' ').slice(-1)[0] || '',
          // Note: userProfile.address is a string, so we only populate line1 if it exists
          address: userProfile.address 
            ? { 
                line1: userProfile.address, 
                line2: '', 
                line3: '', 
                line4: '', 
                postcode: '', 
                country: 'GB' 
              }
            : prev.address,
          }))
  
         // Start a new assessment with the selected plan (Traditional MTD flow only)
         // The assessmentId serves as the formId for the payment service
         // This endpoint requires HMRC OAuth authorization, so only call for Traditional MTD
         // For SA Online flow, we skip this - the saReturnId from saOnlineService.createReturn will be used instead
         // Use URL params directly to ensure values are available
         const currentPlanSlug = planSlugFromParams || planSlug
         const currentNinoForAssessment = currentNino || userProfile.nino

         // Only call startAssessment for Traditional MTD flow (not SA Online)
         if (!isSaFlow && !assessmentId && currentPlanSlug && currentNinoForAssessment) {
          try {
            console.log('Creating assessment for payment tracking (Traditional MTD)...', { nino: currentNinoForAssessment, planSlug: currentPlanSlug, isSaFlow })
            const startResponse = await selfAssessmentApiService.startAssessment(currentNinoForAssessment, currentPlanSlug)
            console.log('Assessment creation response:', startResponse)
            
            // Extract assessmentId from response - handle different response structures
            const newAssessmentId = (startResponse as any)?.id || 
                                   (startResponse as any)?.data?.id || 
                                   (startResponse as any)?.response?.data?.id ||
                                   startResponse?.id
  
             if (newAssessmentId) {
              console.log('Assessment created successfully:', newAssessmentId)
               setAssessmentId(newAssessmentId)
  
               // Persist assessmentId in URL so the rest of the SA wizard flow can use it
               const params = new URLSearchParams(location.search)
               params.set('assessmentId', newAssessmentId)
               navigate(
                 {
                   pathname: location.pathname,
                   search: `?${params.toString()}`,
                   },
                 { replace: true }
               )
  
               // Also persist in localStorage for auxiliary pages/utilities
               localStorage.setItem('currentSelfAssessmentId', newAssessmentId)
            } else {
              console.error('Assessment ID not found in response:', startResponse)
              toast.error('Failed to create assessment record. The response did not include an assessment ID.')
             }
          } catch (err: any) {
            console.error('Error starting assessment:', err)
            console.error('Error details:', {
              message: err?.message,
              response: err?.response,
              status: err?.status,
              data: err?.response?.data
            })
            
            // For both flows, show an error but don't logout
            // This is critical for payment, so we should inform the user
            toast.error(extractApiError(err, 'Failed to create assessment record for payment tracking. Please try refreshing the page or contact support if the issue persists.'))
            
            // Don't block the flow completely, but user will see the error
            // They can try refreshing or we can retry later
          }
         } else {
           // Log why assessment creation was skipped
           console.log('Assessment creation skipped:', {
             hasAssessmentId: !!assessmentId,
             hasPlanSlug: !!currentPlanSlug,
             hasNino: !!currentNinoForAssessment,
             assessmentId,
             planSlug: currentPlanSlug,
             nino: currentNinoForAssessment,
             planSlugFromParams,
             currentNino
           })
         }
  
         // Fetch business details to get businessId for self-employment/property flows (Traditional MTD only)
         if (!isSaFlow && userProfile.nino) {
           try {
             const businesses = await selfAssessmentApiService.getBusinessDetails(userProfile.nino)
             const firstBusiness = businesses[0]
             if (firstBusiness?.businessId) {
               setBusinessId(firstBusiness.businessId)
             }
           } catch (err) {
             console.error('Error fetching business details:', err)
           }
          }

      } catch (err: any) {
        console.error('Error initializing assessment:', err)
        toast.error(extractApiError(err, 'Failed to initialize assessment. Please try again.'))
      } finally {
        setIsLoading(false)
        setIsInitializing(false)
      }
    }

    initializeAssessment()
  }, [])

  // Automatically load calculation when reaching step 5
  useEffect(() => {
    const loadCalculation = async () => {
      if (currentStep !== 4) return
      if (calculationResults) return // Already loaded
      if (isLoading) return // Already loading

      setIsLoading(true)
      setError(null)

      try {
        if (isSaFlow) {
          if (saReturnId) {
            const calc = await saOnlineService.calculateTax(saReturnId)
            setSaCalculation(calc); setCalculationResults(mapSaCalculationToMtd(calc))
          } else {
            toast.error('SA Return ID is required to calculate tax.')
          }
        } else {
          // Traditional flow: First trigger calculation if needed, then retrieve
          if (userProfile.nino && assessmentId) {
            // If we don't have a calculationId yet, trigger calculation first
            if (!calculationId) {
              const calcResponse = await selfAssessmentApiService.triggerCalculation(
                userProfile.nino,
                taxYear,
                false,
                assessmentId
              )
              setCalculationId(calcResponse.calculationId)
              
              // Now retrieve the calculation results
              const calcResults = await selfAssessmentApiService.getCalculation(
                userProfile.nino,
                taxYear,
                calcResponse.calculationId
              )
              const wrappedResults: SelfAssessmentCalculationResponse = {
                statusCode: 200,
                response: {
                  calculationId: calcResponse.calculationId,
                  calculation: {
                    taxCalculation: calcResults as any
                  }
                }
              }
              setCalculationResults(wrappedResults)
            } else {
              // We already have a calculationId, just retrieve results
              const calcResults = await selfAssessmentApiService.getCalculation(
                userProfile.nino,
                taxYear,
                calculationId
              )
              const wrappedResults: SelfAssessmentCalculationResponse = {
                statusCode: 200,
                response: {
                  calculationId: calculationId,
                  calculation: {
                    taxCalculation: calcResults as any
                  }
                }
              }
              setCalculationResults(wrappedResults)
            }
          } else {
            toast.error('NINO and Assessment ID are required to calculate tax.')
          }
        }
      } catch (err: any) {
        console.error('Error loading calculation:', err)
        toast.error(extractApiError(err, 'Failed to load calculation. Please try again.'))
      } finally {
        setIsLoading(false)
      }
    }

    loadCalculation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  const handleStepClick = (stepNumber: number) => {
    const step = steps.find(s => s.number === stepNumber)
    if (step && step.status === 'completed') {
      // Allow navigation to completed steps
      setSteps(prev => prev.map(s => {
        if (s.number === stepNumber) {
          return { ...s, status: 'active' }
        }
        if (s.number === currentStep) {
          // Keep current step as completed if it was completed
          return { ...s, status: s.status === 'active' ? 'completed' : s.status }
        }
        return s
      }))
      setCurrentStep(stepNumber as WizardStepId)
    }
  }

  // Validate current step before proceeding
  const validateCurrentStep = (): { isValid: boolean; error?: string; fieldErrors?: Record<string, string> } => {
    const fieldErrors: Record<string, string> = {}
    
    switch (currentStep) {
      case 1:
        // Use URL param as source of truth, fallback to userProfile
        const currentUtr = searchParams.get('utr') || userProfile.utr || ''
        if (!/^\d{10}$/.test(currentUtr.replace(/\s/g, ''))) {
          fieldErrors.utr = 'Please provide a valid 10-digit UTR.'
        }
        if (!personalDetails.firstName || !personalDetails.lastName) {
          fieldErrors.name = 'Please enter your first and last name.'
        }
        if (!personalDetails.emailAddress) {
          fieldErrors.emailAddress = 'Please enter your email address.'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalDetails.emailAddress)) {
          fieldErrors.emailAddress = 'Please enter a valid email address.'
        }
        if (!personalDetails.companyType) {
          fieldErrors.companyType = 'Please select a company type.'
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(personalDetails.dateOfBirth)) {
          fieldErrors.dateOfBirth = 'Please enter your date of birth in YYYY-MM-DD format.'
        }
        if (!personalDetails.address.line1?.trim()) {
          fieldErrors.addressLine1 = 'Please enter address line 1.'
        }
        if (!personalDetails.address.postcode?.trim()) {
          fieldErrors.postcode = 'Please enter postcode.'
        }
        if (!personalDetails.address.country) {
          fieldErrors.country = 'Please select country.'
        }
        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors)
          const firstError = Object.values(fieldErrors)[0] || 'Please complete all required fields.'
          return { isValid: false, error: firstError, fieldErrors }
        }
        setValidationErrors({})
        return { isValid: true }
      
      case 2:
        if (tailorReturn.wasEmployee === 'yes') {
          if (!tailorReturn.numberOfEmployments || parseInt(tailorReturn.numberOfEmployments) < 1) {
            fieldErrors.numberOfEmployments = 'Please enter a valid number of employments.'
          }
          const numEmployments = parseInt(tailorReturn.numberOfEmployments) || 1
          for (let i = 0; i < numEmployments; i++) {
            if (!tailorReturn.employerNames[i]?.trim()) {
              fieldErrors[`employerName_${i}`] = `Please enter the name for employer ${i + 1}.`
            }
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors)
          const firstError = Object.values(fieldErrors)[0] || 'Please complete all required fields.'
          return { isValid: false, error: firstError, fieldErrors }
        }
        setValidationErrors({})
        return { isValid: true }
      
      case 3:
        if (tailorReturn.wasEmployee === 'yes') {
          for (let i = 0; i < employerDetailsList.length; i++) {
            const emp = employerDetailsList[i]!
            const prefix = employerDetailsList.length > 1 ? `Employer ${i + 1}: ` : ''
            if (!emp.employerName) {
              fieldErrors[`employerName_${i}`] = `${prefix}Please enter the employer name.`
            }
            if (!emp.startDate) {
              fieldErrors[`startDate_${i}`] = `${prefix}Please enter the employment start date.`
            }
            if (!emp.payrollId) {
              fieldErrors[`payrollId_${i}`] = `${prefix}Please enter the payroll ID.`
            }
            if (!emp.totalPay) {
              fieldErrors[`totalPay_${i}`] = `${prefix}Please enter the total pay amount.`
            } else if (isNaN(parseFloat(emp.totalPay))) {
              fieldErrors[`totalPay_${i}`] = `${prefix}Please enter a valid number for total pay.`
            }
          }
        }
        if (tailorReturn.wasSelfEmployed === 'yes') {
        if (selfEmploymentReportingType === 'quarterly' && selfEmploymentPeriods.length === 0) {
          fieldErrors.selfEmployment = 'Please add at least one self-employment period for quarterly reporting.'
          }
        }
        if (tailorReturn.hasProperty === 'yes') {
          if (propertyPeriods.length === 0) {
            fieldErrors.property = 'Please add at least one property period.'
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors)
          const firstError = Object.values(fieldErrors)[0] || 'Please complete all required fields.'
          return { isValid: false, error: firstError, fieldErrors }
        }
        setValidationErrors({})
        return { isValid: true }
      
      case 6:
        // Validate HMRC Government Gateway credentials (SA Flow only)
        if (isSaFlow) {
          if (!governmentGatewayUserId.trim()) {
            fieldErrors.governmentGatewayUserId = 'Please enter your Government Gateway User ID.'
          }
          if (!governmentGatewayPassword.trim()) {
            fieldErrors.governmentGatewayPassword = 'Please enter your Government Gateway Password.'
          }
          if (Object.keys(fieldErrors).length > 0) {
            setValidationErrors(fieldErrors)
            const firstError = Object.values(fieldErrors)[0] || 'Please complete all required credentials.'
            return { isValid: false, error: firstError, fieldErrors }
          }
        }
        setValidationErrors({})
        return { isValid: true }
      
      default:
        setValidationErrors({})
        return { isValid: true }
    }
  }

  const buildSaReturnPayload = () => {
    // Use URL param as source of truth for UTR, fallback to userProfile
    const utrFromUrl = searchParams.get('utr') || userProfile.utr || ''
    const normalizedUtr = utrFromUrl.replace(/\s/g, '')
    
    const taxpayerStatus = (() => {
      switch (personalDetails.taxPayerResidency) {
        case 'scottish':
          return 'S'
        case 'welsh':
          return 'U'
        case 'english':
        default:
          return 'C'
      }
    })()
    const senderType = (() => {
      switch (personalDetails.companyType) {
        case 'limited':
          return 'Company'
        case 'partnership':
          return 'Partnership'
        case 'sole-trader':
        default:
          return 'Individual'
      }
    })()
    const employment =
      tailorReturn.wasEmployee === 'yes'
        ? employerDetailsList.map(emp => {
            const totalPay = parseFloat(emp.totalPay) || 0
            const totalTax = parseFloat(emp.totalTax) || 0
            const uglDeductionAmount = parseFloat(emp.studentLoanDeductions.uglDeductionAmount) || 0
            const pglDeductionAmount = parseFloat(emp.studentLoanDeductions.pglDeductionAmount) || 0

            const empBenefitsInKind = benefitsInKindFields.reduce(
              (acc, field) => {
                const rawValue = emp.benefitsInKind[field.key]
                acc[field.key] = rawValue ? parseFloat(rawValue) || 0 : 0
                return acc
              },
              {} as Record<BenefitInKindKey, number>
            )

            return {
              employerName: emp.employerName,
              employerRef: emp.payeReference || undefined,
              startDate: emp.startDate || undefined,
              cessationDate: emp.cessationDate || undefined,
              payrollId: emp.payrollId || undefined,
              payFrequency: emp.payFrequency,
              occupationalPension: emp.occupationalPension === 'yes',
              pay: {
                taxablePayToDate: totalPay,
                totalTaxToDate: totalTax,
              },
              deductions: {
                studentLoans: {
                  uglDeductionAmount,
                  pglDeductionAmount,
                },
              },
              benefitsInKind: empBenefitsInKind,
              offPayrollWorker: emp.offPayrollWorker === 'yes',
            }
          })
        : []

    const selfEmployment =
      tailorReturn.wasSelfEmployed === 'yes'
        ? selfEmploymentPeriods.map(period => ({
            periodDates: {
              from: period.fromDate,
              to: period.toDate,
            },
            periodIncome: {
              turnover: parseFloat(period.turnover) || 0,
              other: parseFloat(period.otherIncome) || 0,
            },
            periodExpenses: {
              costOfGoodsBought: { amount: parseFloat(period.costOfGoods) || 0 },
              staffCosts: { amount: parseFloat(period.staffCosts) || 0 },
              travelCosts: { amount: parseFloat(period.travelCosts) || 0 },
              premisesRunningCosts: { amount: parseFloat(period.premisesCosts) || 0 },
            },
          }))
        : []

    const ukProperty =
      tailorReturn.hasProperty === 'yes'
        ? propertyPeriods.map(period => ({
            from: period.fromDate,
            to: period.toDate,
            financials: {
              incomes: {
                rentIncome: { amount: parseFloat(period.rentIncome) || 0 },
                premiumsOfLeaseGrant: { amount: parseFloat(period.premiums) || 0 },
              },
              expenses: {
                premisesRunningCosts: { amount: parseFloat(period.premisesRunningCosts) || 0 },
                repairsAndMaintenance: { amount: parseFloat(period.repairsMaintenance) || 0 },
                professionalFees: { amount: parseFloat(period.legalManagementCosts) || 0 },
              },
            },
          }))
        : []

    const dividends =
      tailorReturn.hasDividends === 'yes'
        ? {
            ukDividends: parseFloat(dividendsIncome.ukDividends) || 0,
            otherUkDividends: parseFloat(dividendsIncome.otherUkDividends) || 0,
          }
        : undefined

    // Only include income sources if user has indicated they have them AND provided valid data
    
    // Transform employment into SA Online format - only if user said they were an employee AND has valid employer name
    const employmentsFormatted = 
      tailorReturn.wasEmployee === 'yes' && 
      employment.length > 0 && 
      employment[0]?.employerName?.trim()
        ? employment
            .filter(emp => emp.employerName?.trim()) // Filter out empty employer names
            .map(emp => ({
              employerName: emp.employerName,
              employerPAYEReference: emp.employerRef || undefined,
              payFromEmployment: emp.pay.taxablePayToDate || 0,
              taxDeducted: emp.pay.totalTaxToDate || 0,
              employmentStartDate: emp.startDate || undefined,
              employmentEndDate: emp.cessationDate || undefined,
              benefits: emp.benefitsInKind || {},
            }))
        : undefined

    // Transform self-employment periods into SA Online format - only if user said they were self-employed AND has periods
    const selfEmploymentsFormatted = 
      tailorReturn.wasSelfEmployed === 'yes' && 
      selfEmployment.length > 0
        ? selfEmployment.map(period => {
            // Annual (SA Online) capture has no per-period dates, so fall back
            // to the tax year itself — the correct basis period for the return.
            const taxYearDates = getTaxYearIsoDates(taxYear)
            const fromDate = period.periodDates.from || taxYearDates.start
            const toDate = period.periodDates.to || taxYearDates.end
            return {
            businessName: 'Self Employment Business',
            businessDescription: 'Self Employment Income',
            businessStartDate: fromDate,
            accountingPeriodStartDate: fromDate,
            accountingPeriodEndDate: toDate,
            turnover: period.periodIncome.turnover,
            otherBusinessIncome: period.periodIncome.other,
            costOfGoodsBought: period.periodExpenses.costOfGoodsBought.amount,
            wages: period.periodExpenses.staffCosts.amount,
            travelCosts: period.periodExpenses.travelCosts.amount,
            premisesRunningCosts: period.periodExpenses.premisesRunningCosts.amount,
            }
          })
        : undefined

    // Transform property into SA Online format - only if user said they have property AND has periods
    const propertyBusinessFormatted = 
      tailorReturn.hasProperty === 'yes' && 
      ukProperty.length > 0
        ? {
            ukPropertyIncome: ukProperty.reduce((sum, p) => sum + (p.financials?.incomes?.rentIncome?.amount || 0), 0),
            ukPropertyExpenses: ukProperty.reduce((sum, p) => {
              const expenses = p.financials?.expenses || {}
              return sum + 
                (expenses.premisesRunningCosts?.amount || 0) +
                (expenses.repairsAndMaintenance?.amount || 0) +
                (expenses.professionalFees?.amount || 0)
            }, 0),
          }
        : undefined

    // Only include dividends if user said they have them AND provided data
    const dividendsIncomeFormatted = 
      tailorReturn.hasDividends === 'yes' && 
      dividends
        ? dividends
        : undefined

    // Build base payload with required fields
    const payload: any = {
      uniqueTaxpayerReference: normalizedUtr,
      taxYear,
      senderType,
      personalDetails: {
        firstName: personalDetails.firstName,
        middleName: personalDetails.middleName || undefined,
        lastName: personalDetails.lastName,
        dateOfBirth: personalDetails.dateOfBirth,
        nationalInsuranceNumber: userProfile.nino || undefined,
        telephoneNumber: personalDetails.telephoneNumber,
        email: personalDetails.emailAddress,
        address: personalDetails.address,
        taxpayerStatus,
      },
    }

    // Only add income sources if they have valid data
    if (employmentsFormatted && employmentsFormatted.length > 0) {
      payload.employments = employmentsFormatted
    }
    if (selfEmploymentsFormatted && selfEmploymentsFormatted.length > 0) {
      payload.selfEmployments = selfEmploymentsFormatted
    }
    if (propertyBusinessFormatted) {
      payload.propertyBusiness = propertyBusinessFormatted
    }
    if (dividendsIncomeFormatted) {
      payload.dividendsIncome = dividendsIncomeFormatted
    }

    // Add declaration object
    const today = new Date()
    const dateOfDeclaration = today.toISOString().split('T')[0] // Format: YYYY-MM-DD
    payload.declaration = {
      declaration: true,
      dateOfDeclaration: dateOfDeclaration,
      capacityInWhichSigning: 'Taxpayer'
    }

    return payload
  }

  const mapSaCalculationToMtd = (data: any): SelfAssessmentCalculationResponse => {
    const totalIncome = data?.incomeBreakdown?.totalIncome ?? 0
    const allowances = data?.personalAllowance ?? 0
    const taxableIncome = data?.taxableIncome ?? 0
    const incomeTaxTotal = data?.incomeTax?.totalTax ?? 0
    const class2 = data?.nationalInsurance?.class2NI ?? 0
    const class4 = data?.nationalInsurance?.class4NI ?? 0
    const totalTaxDue = data?.totalTaxDue ?? 0

    return {
      statusCode: 200,
      response: {
        calculationId: saReturnId || 'sa-online',
        calculation: {
          taxCalculation: {
            incomeTax: {
              totalIncomeReceivedFromAllSources: totalIncome,
              totalAllowancesAndDeductions: allowances,
              totalTaxableIncome: taxableIncome,
              incomeTaxCharged: incomeTaxTotal,
              incomeTaxDueAfterDeductions: incomeTaxTotal,
            },
            nationalInsurance: {
              class2Nics: { amount: class2 },
              class4Nics: { amount: class4 },
            },
            totalIncomeTaxAndNicsDue: totalTaxDue,
          },
        },
      },
    }
  }

  // Keep the wizard position in the URL (replaces the localStorage draft's
  // currentStep) so a refresh resumes on the same step.
  const setStepInUrl = (step: number) => {
    const params = new URLSearchParams(location.search)
    params.set('step', String(step))
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true })
  }

  // Persist the SA return to the backend from the current form state. Creates it
  // on the first call (and pins saReturnId into the URL so a refresh reloads from
  // the server), updates it thereafter. Best-effort: never throws — returns the
  // return id, or null if it couldn't be created yet (e.g. early, incomplete data).
  // Reason the last persist attempt failed, surfaced by handleNext.
  const lastPersistErrorRef = useRef<string | null>(null)

  const persistReturn = async (): Promise<string | null> => {
    if (!isSaFlow) return saReturnId
    let payload: any
    try {
      payload = buildSaReturnPayload()
    } catch (buildErr) {
      // Was silent: a throw here produced the same generic "failed to save"
      // message as an API rejection, with nothing to tell them apart.
      console.error('Failed to build SA return payload:', buildErr)
      lastPersistErrorRef.current = 'We could not assemble your return from the details entered.'
      return saReturnId
    }
    lastPersistErrorRef.current = null
    const pinIdToUrl = (id: string) => {
      const params = new URLSearchParams(location.search)
      params.set('saReturnId', id)
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true })
    }
    try {
      if (saReturnId) {
        await saOnlineService.updateReturn(saReturnId, payload)
        return saReturnId
      }
      const created = await saOnlineService.createReturn(payload)
      const newId =
        created?.response?.data?._id || created?.data?._id || created?._id || created?.id || created?.response?.id
      if (newId) {
        setSaReturnId(newId)
        pinIdToUrl(newId)
        return newId
      }
      return null
    } catch (err: any) {
      // Duplicate return for this UTR+taxYear → adopt the existing draft.
      const msg = extractApiError(err, '')
      if (!saReturnId && /already exists/i.test(msg)) {
        try {
          const drafts = await saOnlineService.listDraftReturns(taxYear)
          const wantUtr = (userProfile.utr || '').replace(/\s/g, '')
          const match = drafts.find((d: any) => d?.uniqueTaxpayerReference === wantUtr) || drafts[0]
          if (match?._id) {
            setSaReturnId(match._id)
            pinIdToUrl(match._id)
            try {
              await saOnlineService.updateReturn(match._id, payload)
            } catch {
              /* keep the adopted id even if the immediate update fails */
            }
            return match._id
          }
        } catch {
          /* fall through */
        }
      }
      console.error('Failed to persist SA return:', err)
      // Hold the server's reason so the caller can show it. Validation errors
      // name the exact field, which the generic message hid completely.
      lastPersistErrorRef.current = extractApiError(err, '') || null
      return saReturnId
    }
  }

  const handleNext = async () => {
    // Validate current step
    const validation = validateCurrentStep()
    if (!validation.isValid) {
      toast.error(validation.error || 'Please complete all required fields.')
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      if (isSaFlow) {
        // Backend is the source of truth: persist the return on every step
        // (creates it on the first forward move, updates it thereafter).
        const returnId = await persistReturn()

        // Step 4: the return must be saved + calculated before the calc step.
        if (currentStep === 3) {
          if (!returnId) {
            toast.error(
              lastPersistErrorRef.current ||
                'Failed to save your return. Please check your inputs and try again.'
            )
            setIsLoading(false)
            return
          }
          try {
            const calc = await saOnlineService.calculateTax(returnId)
            setSaCalculation(calc); setCalculationResults(mapSaCalculationToMtd(calc))
          } catch (calcErr: any) {
            toast.error(extractApiError(calcErr, 'Failed to calculate tax. Please try again.'))
            setIsLoading(false)
            return
          }
        } else if (currentStep === 4) {
          // Step 5: calculate tax if not already done.
          if (!returnId) {
            toast.error('Please go back and complete the previous steps.')
            setIsLoading(false)
            return
          }
          if (!calculationResults) {
            try {
              const calc = await saOnlineService.calculateTax(returnId)
              setSaCalculation(calc); setCalculationResults(mapSaCalculationToMtd(calc))
            } catch (calcErr: any) {
              console.error('Tax calculation failed:', calcErr)
              toast.error(extractApiError(calcErr, 'Failed to calculate tax. Please try again.'))
              setIsLoading(false)
              return
            }
          }
        } else if (currentStep === 6) {
          // Submit step: stash GG credentials for the post-payment auto-submit.
          if (governmentGatewayUserId && governmentGatewayPassword && returnId) {
            try {
              sessionStorage.setItem('gg_credentials', JSON.stringify({
                userId: governmentGatewayUserId,
                password: governmentGatewayPassword,
                saReturnId: returnId,
                timestamp: Date.now(),
              }))
            } catch (storageErr) {
              console.warn('Failed to store credentials in sessionStorage:', storageErr)
            }
          }
        }
      } else {
      // Handle API calls for each step before proceeding
      if (currentStep === 2) {
        // Fetch obligations after tailoring the return
        if (userProfile.nino) {
            const obligationParams =
              tailorReturn.wasSelfEmployed === 'yes' && businessId
                ? {
                    typeOfBusiness: 'self-employment' as const,
                    businessId: businessId,
                  }
                : tailorReturn.wasSelfEmployed === 'yes'
                ? {
                    typeOfBusiness: 'self-employment' as const,
                  }
                : undefined
            const incomeObligations = await selfAssessmentApiService.getIncomeAndExpenditureObligations(
              userProfile.nino,
              obligationParams
            )
          const crystObligations = await selfAssessmentApiService.getCrystallisationObligations(userProfile.nino)
          // Combine both obligation types into a single obligations response
          const combinedObligations: SelfAssessmentObligationsResponse = {
            statusCode: 200,
            response: {
              nino: userProfile.nino,
              obligations: [
                {
                  typeOfBusiness: 'self-employment',
                  businessId: null,
                  obligationDetails: incomeObligations.map((ob: any) => ({
                    periodStartDate: ob.periodStartDate,
                    periodEndDate: ob.periodEndDate,
                    dueDate: ob.dueDate,
                    receivedDate: ob.receivedDate,
                    status: ob.status
                  }))
                },
                ...(crystObligations.map((ob: any) => ({
                  typeOfBusiness: 'other',
                  businessId: null,
                  obligationDetails: [{
                    periodStartDate: ob.periodStartDate,
                    periodEndDate: ob.periodEndDate,
                    dueDate: ob.dueDate,
                    receivedDate: ob.receivedDate,
                    status: ob.status
                  }]
                })))
              ]
            }
          }
          setObligations(combinedObligations)
        }
      } else if (currentStep === 3) {
        // Submit employment data if applicable (one API call per employer)
        if (tailorReturn.wasEmployee === 'yes' && assessmentId && userProfile.nino) {
          try {
            for (const emp of employerDetailsList) {
              const totalPay = parseFloat(emp.totalPay) || 0
              const totalTax = parseFloat(emp.totalTax) || 0
              const uglDeductionAmount = parseFloat(emp.studentLoanDeductions.uglDeductionAmount) || 0
              const pglDeductionAmount = parseFloat(emp.studentLoanDeductions.pglDeductionAmount) || 0

              const empBenefitsInKind = benefitsInKindFields.reduce(
                (acc, field) => {
                  const rawValue = emp.benefitsInKind[field.key]
                  acc[field.key] = rawValue ? parseFloat(rawValue) || 0 : 0
                  return acc
                },
                {} as Record<BenefitInKindKey, number>
              )

              const employmentPayload: any = {
                assessmentId: assessmentId,
                employerName: emp.employerName,
                startDate: emp.startDate,
                payFrequency: emp.payFrequency,
                occupationalPension: emp.occupationalPension === 'yes',
                totalPayToDate: totalPay,
                totalTaxToDate: totalTax,
              }
              if (emp.payeReference) {
                employmentPayload.employerRef = emp.payeReference
              }
              if (emp.cessationDate) {
                employmentPayload.cessationDate = emp.cessationDate
              }
              if (emp.payrollId) {
                employmentPayload.payrollId = emp.payrollId
              }
              const employmentResponse = await selfAssessmentApiService.createEmployment(userProfile.nino, taxYear, employmentPayload)

              const employmentId =
                (employmentResponse as any)?.employmentId ||
                (employmentResponse as any)?.id ||
                (employmentResponse as any)?.data?.employmentId

              if (employmentId) {
                await selfAssessmentApiService.addEmploymentFinancialDetails(
                  userProfile.nino,
                  taxYear,
                  employmentId,
                  {
                    assessmentId: assessmentId,
                    employment: {
                      pay: {
                        taxablePayToDate: totalPay,
                        totalTaxToDate: totalTax,
                      },
                      deductions: {
                        studentLoans: {
                          uglDeductionAmount,
                          pglDeductionAmount,
                        },
                      },
                      benefitsInKind: empBenefitsInKind,
                      offPayrollWorker: emp.offPayrollWorker === 'yes',
                    },
                  }
                )
              }
            }
          } catch (err: any) {
            console.error('Error submitting employment data:', err)
            toast.error(extractApiError(err, 'Failed to save employment data. Please try again.'))
            setIsLoading(false)
            return
          }
        }

        // Submit self-employment data if applicable
        if (tailorReturn.wasSelfEmployed === 'yes' && assessmentId && userProfile.nino && businessId) {
          try {
            if (selfEmploymentReportingType === 'quarterly') {
              // Submit quarterly period summaries
              if (selfEmploymentPeriods.length === 0) {
                toast.error('Please add at least one self-employment period for quarterly reporting.')
                setIsLoading(false)
                return
              }

            for (const period of selfEmploymentPeriods) {
              if (!period.fromDate || !period.toDate) {
                toast.error('Please complete all period dates for self-employment periods.')
                setIsLoading(false)
                return
              }
              
              await selfAssessmentApiService.createSelfEmploymentPeriod(userProfile.nino, businessId, {
                assessmentId: assessmentId,
                taxYear: taxYear,
                periodDates: {
                  from: period.fromDate,
                  to: period.toDate
                },
                periodIncome: {
                  turnover: parseFloat(period.turnover) || 0,
                  other: parseFloat(period.otherIncome) || 0
                },
                periodExpenses: {
                  costOfGoodsBought: { amount: parseFloat(period.costOfGoods) || 0 },
                  staffCosts: { amount: parseFloat(period.staffCosts) || 0 },
                  travelCosts: { amount: parseFloat(period.travelCosts) || 0 },
                  premisesRunningCosts: { amount: parseFloat(period.premisesCosts) || 0 }
                  }
                })
              }
            } else if (selfEmploymentReportingType === 'annual') {
              // Submit annual submission
              await selfAssessmentApiService.createSelfEmploymentAnnual(userProfile.nino, businessId, taxYear, {
                assessmentId: assessmentId,
                adjustments: {
                  includedNonTaxableProfits: parseFloat(selfEmploymentAnnualData.adjustments.includedNonTaxableProfits) || 0,
                  basisAdjustment: parseFloat(selfEmploymentAnnualData.adjustments.basisAdjustment) || 0,
                  overlapReliefUsed: parseFloat(selfEmploymentAnnualData.adjustments.overlapReliefUsed) || 0,
                  accountingAdjustment: parseFloat(selfEmploymentAnnualData.adjustments.accountingAdjustment) || 0,
                  averagingAdjustment: parseFloat(selfEmploymentAnnualData.adjustments.averagingAdjustment) || 0
                },
                allowances: {
                  annualInvestmentAllowance: parseFloat(selfEmploymentAnnualData.allowances.annualInvestmentAllowance) || 0,
                  otherCapitalAllowance: parseFloat(selfEmploymentAnnualData.allowances.otherCapitalAllowance) || 0,
                  businessPremisesRenovationAllowance: parseFloat(selfEmploymentAnnualData.allowances.businessPremisesRenovationAllowance) || 0,
                  costOfReplacingDomesticGoods: parseFloat(selfEmploymentAnnualData.allowances.costOfReplacingDomesticGoods) || 0,
                  zeroEmissionGoodsVehicleAllowance: parseFloat(selfEmploymentAnnualData.allowances.zeroEmissionGoodsVehicleAllowance) || 0,
                  enhancedCapitalAllowance: parseFloat(selfEmploymentAnnualData.allowances.enhancedCapitalAllowance) || 0,
                  allowanceOnSales: parseFloat(selfEmploymentAnnualData.allowances.allowanceOnSales) || 0,
                  capitalAllowanceMainPool: parseFloat(selfEmploymentAnnualData.allowances.capitalAllowanceMainPool) || 0,
                  capitalAllowanceSpecialRatePool: parseFloat(selfEmploymentAnnualData.allowances.capitalAllowanceSpecialRatePool) || 0,
                  tradingIncomeAllowance: parseFloat(selfEmploymentAnnualData.allowances.tradingIncomeAllowance) || 0
                },
                nonFinancials: {
                  class4NicInfo: {
                    isExempt: selfEmploymentAnnualData.nonFinancials.class4NicExempt === 'yes',
                    ...(selfEmploymentAnnualData.nonFinancials.exemptionCode ? { exemptionCode: selfEmploymentAnnualData.nonFinancials.exemptionCode } : {})
                  },
                  payeEmployed: selfEmploymentAnnualData.nonFinancials.payeEmployed === 'yes'
                }
              })
            }
          } catch (err: any) {
            console.error('Error submitting self-employment data:', err)
            toast.error(extractApiError(err, `Failed to save self-employment ${selfEmploymentReportingType === 'quarterly' ? 'periods' : 'annual submission'}. Please try again.`))
            setIsLoading(false)
            return
          }
        }

        // Submit property periods if applicable
        if (tailorReturn.hasProperty === 'yes' && assessmentId && userProfile.nino && businessId) {
          try {
            for (const period of propertyPeriods) {
              if (!period.fromDate || !period.toDate) {
                toast.error('Please complete all period dates for property periods.')
                setIsLoading(false)
                return
              }
              
              await selfAssessmentApiService.createUKPropertyPeriod(userProfile.nino, businessId, taxYear, {
                assessmentId: assessmentId,
                from: period.fromDate,
                to: period.toDate,
                financials: {
                  incomes: {
                    rentIncome: { amount: parseFloat(period.rentIncome) || 0 },
                    premiumsOfLeaseGrant: { amount: parseFloat(period.premiums) || 0 }
                  },
                  expenses: {
                    premisesRunningCosts: { amount: parseFloat(period.premisesRunningCosts) || 0 },
                    repairsAndMaintenance: { amount: parseFloat(period.repairsMaintenance) || 0 },
                      financialCosts: { amount: 0 },
                      professionalFees: { amount: parseFloat(period.legalManagementCosts) || 0 },
                      costOfServices: { amount: 0 },
                      otherExpenses: { amount: 0 }
                  }
                }
              })
            }
          } catch (err: any) {
            console.error('Error submitting property periods:', err)
            toast.error(extractApiError(err, 'Failed to save property periods. Please try again.'))
            setIsLoading(false)
            return
          }
        }

        // Submit dividends income if applicable
        if (tailorReturn.hasDividends === 'yes' && assessmentId && userProfile.nino) {
          try {
            await selfAssessmentApiService.createDividendsIncome(userProfile.nino, taxYear, {
              assessmentId: assessmentId,
              ukDividends: parseFloat(dividendsIncome.ukDividends) || 0,
              otherUkDividends: parseFloat(dividendsIncome.otherUkDividends) || 0
            })
          } catch (err: any) {
            console.error('Error submitting dividends income:', err)
            toast.error(extractApiError(err, 'Failed to save dividends income. Please try again.'))
            setIsLoading(false)
            return
          }
        }
        // MTD: step 3 (Fill in Your Return) also triggers the tax calculation
        // before the calculation step (the Overpaid Tax step was removed).
        // Trigger calculation
          if (userProfile.nino && assessmentId && !calculationId) {
          const calcResponse = await selfAssessmentApiService.triggerCalculation(
            userProfile.nino,
            taxYear,
              false,
              assessmentId
          )
          setCalculationId(calcResponse.calculationId)
          } else if (userProfile.nino && !assessmentId) {
            toast.error('Assessment ID is required to calculate your tax. Please try again.')
            setIsLoading(false)
            return
        }
      } else if (currentStep === 4) {
        // Retrieve calculation results
        if (userProfile.nino && calculationId) {
          const calcResults = await selfAssessmentApiService.getCalculation(
            userProfile.nino,
            taxYear,
            calculationId
          )
          // Wrap the calculation result into the expected format
          const wrappedResults: SelfAssessmentCalculationResponse = {
            statusCode: 200,
            response: {
              calculationId: calculationId,
              calculation: {
                taxCalculation: calcResults as any // Cast to avoid type mismatch between API and types
              }
            }
          }
          setCalculationResults(wrappedResults)
          }
        }
      }

      // Proceed to next step
      // SA flow has 8 steps, Traditional MTD has 7 steps
      const maxStep = 6
      if (currentStep < maxStep) {
        setSteps(prev => prev.map(step => {
          if (step.number === currentStep) {
            return { ...step, status: 'completed' }
          }
          if (step.number === currentStep + 1) {
            return { ...step, status: 'active' }
          }
          return step
        }))
        setCurrentStep((currentStep + 1) as WizardStepId)
        // Remember the wizard position in the URL (data is already saved to the backend).
        setStepInUrl(currentStep + 1)
      }
    } catch (err: any) {
      console.error('Error proceeding to next step:', err)

      toast.error(extractApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setError(null)
      // Mark current step back to pending (or keep as completed if it was) and previous step as active
      setSteps(prev => prev.map(step => {
        if (step.number === currentStep) {
          return { ...step, status: 'pending' }
        }
        if (step.number === currentStep - 1) {
          return { ...step, status: 'active' }
        }
        return step
      }))
      setCurrentStep((currentStep - 1) as WizardStepId)
      setStepInUrl(currentStep - 1)
      // Persist current form state to the backend (best-effort, non-blocking).
      void persistReturn()
    }
  }


  const handleSaveAndClose = async () => {
    // Persist current form state to the backend before navigating away.
    try {
      await persistReturn()
    } catch (e) {
      console.warn('Save & Close: persist failed (non-critical):', e)
    }
    const backUrl = isSaFlow
      ? '/add-self-assessment?flow=sa'
      : '/add-self-assessment?flow=traditional'
    navigate(backUrl)
  }

  const handleSubmitReturn = async (planSlugOverride?: string) => {
    // Plan may be pre-selected upstream (AddSelfAssessmentPage) or chosen just
    // now in the pricing modal and passed in directly (state update is async).
    const effectivePlanSlug = planSlugOverride || planSlug
    setIsLoading(true)
    setError(null)

      try {
       // Use appropriate ID as formId for payment based on flow type
       // - Traditional MTD: uses assessmentId (from selfAssessmentApiService.startAssessment)
       // - SA Online: uses saReturnId (from saOnlineService.createReturn) since it doesn't require HMRC auth
       const formId = isSaFlow ? saReturnId : assessmentId

       if (!formId) {
         const errorMsg = isSaFlow
           ? 'Cannot proceed to payment without a valid SA return ID. Please go back and complete the previous steps.'
           : 'Cannot proceed to payment without a valid assessment ID. Please try refreshing the page.'
         toast.error(errorMsg)
         setIsLoading(false)
         return
       }

       // For SA flow, ensure we have a saReturnId for submission
       if (isSaFlow && !saReturnId) {
         toast.error('Cannot proceed to payment without a valid SA return ID. Please go back and complete the previous steps.')
         setIsLoading(false)
         return
       }

      if (!effectivePlanSlug) {
        // No plan chosen yet — open the pricing modal to pick one, then retry.
        setIsLoading(false)
        setIsPlanModalOpen(true)
        return
      }

      // NOTE: Government Gateway credentials are collected AFTER payment (on the
      // submit step, once it shows as paid), so there's nothing to stash here.
      // The user submits manually with the "Submit to HMRC" button post-payment.

      // Build success URL with all necessary parameters
      const successParams = new URLSearchParams({
        formId: formId, // assessmentId is used as formId for payment (both flows)
        taxYear: taxYear,
        planSlug: effectivePlanSlug,
        utr: userProfile.utr || '',
        nino: userProfile.nino || '',
      })
      
      // Always include assessmentId in success URL for reference
      if (formId) {
        successParams.set('assessmentId', formId)
      }
      
      if (isSaFlow) {
        successParams.set('flow', 'sa')
        if (saReturnId) {
          successParams.set('saReturnId', saReturnId)
        }
      } else {
        successParams.set('flow', 'traditional')
        if (calculationId) {
          successParams.set('calculationId', calculationId)
        }
      }
      
      // Persist current form state to the backend before the Stripe redirect so a
      // payment-cancel return reloads it from the server (cancel URL carries saReturnId).
      if (isSaFlow) {
        try {
          await persistReturn()
        } catch (e) {
          console.warn('Pre-payment persist failed (non-critical):', e)
        }
      }

      // Build cancel URL with all necessary parameters
      const cancelParams = new URLSearchParams({
        utr: userProfile.utr || '',
        nino: userProfile.nino || '',
        taxYear: taxYear,
        planSlug: effectivePlanSlug,
        fromDraft: 'true',
      })
      if (formId) {
        cancelParams.set('assessmentId', formId)
      }
      if (isSaFlow) {
        cancelParams.set('flow', 'sa')
        if (saReturnId) {
          cancelParams.set('saReturnId', saReturnId)
        }
      } else {
        cancelParams.set('flow', 'traditional')
        if (calculationId) {
          cancelParams.set('calculationId', calculationId)
      }
      }

      // Initiate Stripe checkout session
      const checkoutResponse = await paymentService.createCheckout(effectivePlanSlug, {
        successUrl: `${window.location.origin}/payment/success?${successParams.toString()}`,
        cancelUrl: `${window.location.origin}/self-assessment/filing?${cancelParams.toString()}`,
        customerEmail: personalDetails.emailAddress || '',
        formId: formId, // assessmentId is used as formId for payment validation (both flows)
        // Tags the payment with the year it covers so a later return for the
        // same year is recognised as already paid.
        ...(taxYear ? { taxYear } : {}),
      })

      // Redirect to Stripe checkout
      if (checkoutResponse.url) {
        window.location.href = checkoutResponse.url
      } else {
        toast.error('Failed to create payment session. Please try again.')
        setIsLoading(false)
      }
    } catch (err: any) {
      console.error('Error initiating payment:', err)
      toast.error(extractApiError(err, 'Failed to initiate payment. Please try again.'))
      setIsLoading(false)
    }
  }

  // Poll HMRC for the final result of an acknowledged SA submission. Updates the
  // live status (attempt/message) as it goes and resolves once HMRC returns a
  // definitive accept/reject, or after the attempt cap (treated as still pending).
  const pollForSubmissionResult = async (
    returnId: string,
    creds: { governmentGatewayUserId: string; governmentGatewayPassword: string },
    initialIntervalSeconds?: number,
  ): Promise<{ outcome: 'accepted' | 'rejected' | 'timeout'; hmrcReference?: string | undefined; errors: string[] }> => {
    let intervalMs = clampPollInterval((initialIntervalSeconds ?? 0) * 1000)
    const startedAt = Date.now()
    let consecutiveErrors = 0
    let lastError: string | null = null

    for (let attempt = 1; attempt <= SA_POLL_MAX_ATTEMPTS; attempt++) {
      const elapsedMin = Math.floor((Date.now() - startedAt) / 60000)
      setPollAttempt(attempt)
      setPollMessage(
        elapsedMin >= 1
          ? `Waiting for HMRC to process your return… (${elapsedMin} min so far)`
          : 'Waiting for HMRC to process your return…'
      )
      await delay(intervalMs)

      try {
        const poll = await saOnlineService.pollSubmission(returnId, creds, { skipAuthLogout: true })
        consecutiveErrors = 0

        if (poll.status === 'completed') {
          return { outcome: 'accepted', hmrcReference: poll.hmrcReference, errors: [] }
        }
        if (poll.status === 'error') {
          const errs = extractSaErrorList(poll)
          return {
            outcome: 'rejected',
            errors: errs.length ? errs : [poll.message || 'HMRC rejected the submission.'],
          }
        }
        // status 'pending' — honour HMRC's suggested next interval, then retry.
        if (poll.pollInterval) intervalMs = clampPollInterval(poll.pollInterval * 1000)
      } catch (pollErr) {
        // A failing poll is NOT the same as HMRC still processing. Swallowing
        // these made a broken connection look identical to a slow return, so
        // keep the message and give up once it's clearly not transient.
        consecutiveErrors += 1
        lastError = extractApiError(pollErr, 'Could not reach HMRC to check your return.')
        console.error(`Poll attempt ${attempt} failed:`, pollErr)
        if (consecutiveErrors >= SA_POLL_MAX_CONSECUTIVE_ERRORS) {
          return { outcome: 'timeout', errors: lastError ? [lastError] : [] }
        }
      }

      if (Date.now() - startedAt >= SA_POLL_MAX_TOTAL_MS) break
    }

    return { outcome: 'timeout', errors: lastError ? [lastError] : [] }
  }

  const handleSubmitToHmrc = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const formId = isSaFlow ? saReturnId : assessmentId

      if (isSaFlow) {
        // SA Online flow: submit via Government Gateway, then poll HMRC for the
        // final acknowledgement (HMRC acknowledges immediately, then processes
        // asynchronously and returns the result on a subsequent poll).
        if (!saReturnId) {
          toast.error('Cannot submit without a valid SA return ID.')
          setIsLoading(false)
          return
        }
        if (!governmentGatewayUserId || !governmentGatewayPassword) {
          toast.error('Please enter your Government Gateway credentials before submitting.')
          setIsLoading(false)
          return
        }

        const creds = { governmentGatewayUserId, governmentGatewayPassword }

        // Enter the submitting state and clear any previous errors.
        setSubmissionErrors([])
        setPollAttempt(0)
        setPollMessage('Sending your return to HMRC…')
        setIsSubmitted(false)
        setIsSubmitting(true)

        // Submission status is persisted server-side by the submit/poll endpoints
        // and reconciled from the backend on load — no localStorage flow-state.
        const persistFlow = (_patch: Record<string, any>) => {
          void _patch
          void formId
        }

        const submitResponse = await saOnlineService.submitReturn(
          saReturnId,
          {
            saReturnId,
            governmentGatewayUserId,
            governmentGatewayPassword,
            environment: SA_SUBMISSION_ENVIRONMENT,
          },
          { skipAuthLogout: true }
        )

        // Backend captured a validation failure (returned success:false, not thrown).
        if (submitResponse.success === false) {
          const errs = extractSaErrorList(submitResponse)
          setSubmissionErrors(
            errs.length ? errs : [submitResponse.message || 'HMRC rejected the submission.']
          )
          setIsSubmitting(false)
          setPollMessage('')
          toast.error(
            submitResponse.message || 'HMRC rejected your submission. Please review the errors below.'
          )
          setIsLoading(false)
          return
        }

        const submitData = submitResponse.data || {}
        persistFlow({
          submitted: true,
          submittedAt: new Date().toISOString(),
          correlationId: submitData.correlationId,
          requiresPolling: submitData.requiresPolling,
        })

        if (submitData.requiresPolling && submitData.correlationId) {
          // HMRC acknowledged — poll until we get the final accept/reject.
          const finalResult = await pollForSubmissionResult(saReturnId, creds, submitData.pollInterval)
          setPollMessage('')

          if (finalResult.outcome === 'accepted') {
            const reference = finalResult.hmrcReference || submitData.correlationId || 'Confirmed'
            setIsSubmitting(false)
            setIsSubmitted(true)
            setSubmissionReceipt({
              referenceNumber: reference,
              submittedAt: new Date().toISOString(),
              taxpayerName: userProfile.fullName || 'Taxpayer',
            })
            persistFlow({ hmrcReference: reference, accepted: true })
            toast.success('HMRC has accepted your return!')
            setShowReviewPrompt(true)
          } else if (finalResult.outcome === 'rejected') {
            setSubmissionErrors(
              finalResult.errors.length ? finalResult.errors : ['HMRC rejected the submission.']
            )
            setIsSubmitting(false)
            setIsSubmitted(false)
            // Keep submitted/requiresPolling so a reload resumes into the
            // check-status view rather than a stuck spinner; just mark not-accepted.
            persistFlow({ accepted: false })
            toast.error('HMRC rejected your return. Please review the errors below.')
          } else {
            // Timed out — the return is lodged with HMRC but not yet processed.
            setIsSubmitting(false)
            setIsSubmitted(true) // shows the "Awaiting HMRC response" view
            toast.info('Your return was sent. HMRC is still processing it — you can check back shortly.')
          }
        } else {
          // No polling required — HMRC returned a final response immediately.
          const reference = submitData.hmrcReference || submitData.correlationId || 'Pending'
          setIsSubmitting(false)
          setIsSubmitted(true)
          setPollMessage('')
          setSubmissionReceipt({
            referenceNumber: reference,
            submittedAt: new Date().toISOString(),
            taxpayerName: userProfile.fullName || 'Taxpayer',
          })
          persistFlow({ hmrcReference: reference })
          toast.success('Your return has been successfully submitted to HMRC!')
          setShowReviewPrompt(true)
        }

      } else {
        // Traditional MTD flow: submit final declaration
        const nino = (userProfile.nino || '').replace(/\s/g, '').toUpperCase()
        if (!nino || !taxYear || !assessmentId) {
          toast.error('Missing required information for HMRC submission.')
          setIsLoading(false)
          return
        }

        setIsSubmitting(true)
        const finalDeclarationResult = await selfAssessmentApiService.triggerCalculation(
          nino,
          taxYear,
          true, // finalDeclaration = true
          assessmentId,
          { skipAuthLogout: true }
        )

        setIsSubmitted(true)
        setIsSubmitting(false)
        setSubmissionReceipt({
          referenceNumber: finalDeclarationResult.calculationId || 'Pending',
          submittedAt: new Date().toISOString(),
          taxpayerName: userProfile.fullName || 'Taxpayer',
        })
        toast.success('Your return has been successfully submitted to HMRC!')
          setShowReviewPrompt(true)
      }
    } catch (err: any) {
      console.error('Error submitting to HMRC:', err)
      // Surface HMRC validation errors (submit returns 400 with an errors array).
      const errs = extractSaErrorList(err?.response ?? err)
      if (errs.length) setSubmissionErrors(errs)
      toast.error(extractApiError(err, 'Failed to submit to HMRC. Please try again.'))
      setIsSubmitting(false)
      setPollMessage('')
    } finally {
      setIsLoading(false)
    }
  }

  // Resume polling for a return that was submitted earlier (e.g. auto-submitted
  // after payment) and is still awaiting HMRC's final result. Credentials are
  // re-collected here because they are cleared after the initial submission.
  const handleCheckStatus = async () => {
    const returnId = saReturnId || saReturnIdFromParams
    if (!returnId) {
      toast.error('Cannot check status without a valid SA return ID.')
      return
    }
    if (!governmentGatewayUserId || !governmentGatewayPassword) {
      toast.error('Please enter your Government Gateway credentials to check the status.')
      return
    }

    const creds = { governmentGatewayUserId, governmentGatewayPassword }
    // Status is persisted server-side by the poll endpoint; no localStorage.
    const persistFlow = (_patch: Record<string, any>) => {
      void _patch
    }

    setIsLoading(true)
    setSubmissionErrors([])
    setPollAttempt(0)
    setPollMessage('Checking your submission status with HMRC…')
    // Reuse the "Submitting" progress view while we poll.
    setIsSubmitted(false)
    setIsSubmitting(true)

    try {
      const finalResult = await pollForSubmissionResult(returnId, creds)
      setPollMessage('')

      if (finalResult.outcome === 'accepted') {
        const reference = finalResult.hmrcReference || 'Confirmed'
        setIsSubmitting(false)
        setIsSubmitted(true)
        setSubmissionNeedsPoll(false)
        setSubmissionReceipt({
          referenceNumber: reference,
          submittedAt: new Date().toISOString(),
          taxpayerName: userProfile.fullName || 'Taxpayer',
        })
        persistFlow({ submitted: true, hmrcReference: reference, accepted: true })
        toast.success('HMRC has accepted your return!')
            setShowReviewPrompt(true)
      } else if (finalResult.outcome === 'rejected') {
        setSubmissionErrors(
          finalResult.errors.length ? finalResult.errors : ['HMRC rejected the submission.']
        )
        setIsSubmitting(false)
        setIsSubmitted(false)
        setSubmissionNeedsPoll(false)
        persistFlow({ submitted: false, accepted: false })
        toast.error('HMRC rejected your return. Please review the errors and submit again.')
      } else {
        // Still pending — keep the awaiting view so the user can check again later.
        setIsSubmitting(false)
        setIsSubmitted(true)
        setSubmissionNeedsPoll(true)
        // Distinguish "HMRC is slow" from "we couldn't reach HMRC": the second
        // needs to be visible, not hidden behind a reassuring message.
        if (finalResult.errors.length > 0) {
          setSubmissionErrors(finalResult.errors)
          toast.error(
            'We could not confirm the result with HMRC. Your return was submitted — use "Check HMRC status" below to retry.'
          )
        } else {
          toast.info('HMRC is still processing your return. Please check again shortly.')
        }
      }
    } catch (err: any) {
      console.error('Error checking submission status:', err)
      setPollMessage('')
      setIsSubmitting(false)
      setIsSubmitted(true)
      toast.error(extractApiError(err, 'Failed to check submission status. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadReceipt = async () => {
    const receiptId = isSaFlow ? saReturnId : (saReturnId || assessmentId)
    if (!receiptId) return
    setIsDownloadingReceipt(true)
    try {
      const blob = await saOnlineService.downloadReceipt(receiptId)
      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `SA-Receipt-${receiptId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download receipt:', err)
    } finally {
      setIsDownloadingReceipt(false)
    }
  }

  // Submit step render (SA flow). Extracted so it can be reached at the merged
  // final step (step 7) after the standalone HMRC Credentials step was removed.
  const renderSaSubmitStep = () => {
        // Show different content based on submission state

        // SA Flow: Show submitted status view (loaded from history, no receipt)
        if (isSubmitted && !submissionReceipt) {
          return (
            <div className='space-y-6'>
              <div className='rounded-xl border-2 border-blue-200 bg-blue-50 p-10 text-center'>
                <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-100'>
                  <Send className='h-10 w-10 text-blue-600' />
                </div>
                <h2 className='mt-5 text-xl sm:text-2xl font-bold text-blue-900'>Return Submitted to HMRC</h2>
                <p className='mx-auto mt-2 max-w-md text-sm text-blue-700'>
                  Your self assessment tax return for {taxYear.replace('-', ' to ')} has been submitted to HMRC.
                  HMRC typically takes some time to review and process submissions.
                </p>
                <div className='mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-100 px-5 py-2.5'>
                  <Clock className='h-4 w-4 text-blue-600' />
                  <span className='text-sm font-medium text-blue-800'>Awaiting HMRC response</span>
                </div>
                <p className='mx-auto mt-4 max-w-md text-xs text-blue-600'>
                  You can come back to check the status at any time, or you will be notified once HMRC processes your return.
                </p>
              </div>

              {/* Awaiting HMRC's final result — poll for it by re-entering credentials */}
              {submissionNeedsPoll && (
                <div className='rounded-lg border border-amber-200 bg-amber-50 p-6'>
                  <h3 className='text-lg font-semibold text-amber-900'>Check your submission status</h3>
                  <p className='mt-1 text-sm text-amber-800'>
                    HMRC has acknowledged your return and is processing it. Re-enter your Government Gateway
                    credentials to fetch the final result.
                  </p>
                  <div className='mt-4 space-y-4'>
                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Government Gateway User ID <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='text'
                        value={governmentGatewayUserId}
                        onChange={(e) => setGovernmentGatewayUserId(e.target.value)}
                        placeholder='e.g. SA0239'
                        className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      />
                    </div>
                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Government Gateway Password <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='password'
                        value={governmentGatewayPassword}
                        onChange={(e) => setGovernmentGatewayPassword(e.target.value)}
                        placeholder='Your Government Gateway password'
                        className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      />
                    </div>
                    <button
                      onClick={handleCheckStatus}
                      disabled={isLoading || !governmentGatewayUserId || !governmentGatewayPassword}
                      className='flex items-center gap-2 rounded-lg bg-[#001B38] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className='h-4 w-4 animate-spin' />
                          Checking with HMRC…
                        </>
                      ) : (
                        <>
                          <RefreshCw className='h-4 w-4' />
                          Check HMRC status
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {calculationResults && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-6'>
                  <h3 className='text-base font-semibold text-gray-900 mb-4'>Summary</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Tax Year:</span>
                      <span className='text-sm font-semibold text-gray-900'>{taxYear.replace('-', ' to ')}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Total Tax Due:</span>
                      <span className='text-sm font-semibold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {userProfile.utr && (
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-600'>UTR:</span>
                        <span className='text-sm font-semibold text-gray-900'>{userProfile.utr}</span>
                      </div>
                    )}
                    {userProfile.nino && (
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-600'>NINO:</span>
                        <span className='text-sm font-semibold text-gray-900'>{userProfile.nino}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <div className='flex items-start gap-3'>
                  <FileCheck className='h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-blue-900'>Declaration confirmed</p>
                    <p className='mt-0.5 text-xs text-blue-700 leading-relaxed'>
                      The information in this return was declared correct and complete at the time of submission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Show "Submitting" state while return is being submitted to HMRC
        if (isSubmitting && !isSubmitted) {
          return (
            <div className='space-y-6'>
              <div className='text-gray-500 text-sm mb-2'>
                {userProfile.fullName}'s tax return: {taxYear.replace('-', ' to ')}
              </div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-8'>Submit your return</h1>

              <div className='space-y-6'>
                <h2 className='text-xl sm:text-2xl font-bold text-gray-900'>Submitting</h2>

                <div className='flex justify-center py-8'>
                  <Loader2 className='h-12 w-12 animate-spin text-gray-400' />
                </div>

                {/* Live status from the submit/poll cycle */}
                {pollMessage && (
                  <div className='mx-auto max-w-lg rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-center'>
                    <p className='text-sm font-medium text-blue-900'>{pollMessage}</p>
                    {pollAttempt > 0 && (
                      <div className='mt-3 h-1.5 w-full overflow-hidden rounded-full bg-blue-100'>
                        <div
                          className='h-full rounded-full bg-blue-500 transition-all duration-500'
                          style={{ width: `${Math.min(100, Math.round((pollAttempt / SA_POLL_MAX_ATTEMPTS) * 100))}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className='space-y-4 text-gray-700'>
                  <p>
                    You may experience a short delay whilst your return is submitted. We are waiting for notification that your return has been successfully received by HM Revenue and Customs (HMRC).
                  </p>
                  <p>
                    This process may take a while. If you are not able to wait for a response, you can log out and return to your return later, to confirm that HMRC has received your return.
                  </p>
                </div>
              </div>
            </div>
          )
        }

        // Show "Submission receipt" after successful submission
        if (isSubmitted && submissionReceipt) {
          const submittedDate = new Date(submissionReceipt.submittedAt)
          const formattedDate = submittedDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })
          const formattedTime = submittedDate.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          })

          return (
            <div className='space-y-6'>
              <div className='text-gray-500 text-sm mb-2'>
                {submissionReceipt.taxpayerName}'s tax return: {taxYear.replace('-', ' to ')}
              </div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-8'>Submit your return</h1>

              <div className='space-y-6'>
                <h2 className='text-xl sm:text-2xl font-bold text-green-700'>Submission receipt</h2>

                <p className='text-lg text-gray-700'>
                  The {taxYear.replace('-', ' to ')} tax return for {submissionReceipt.taxpayerName} was successfully submitted and was received by HM Revenue and Customs at {formattedTime} on {formattedDate}.
                </p>

                <div className='border-l-4 border-gray-300 pl-4 py-2 bg-gray-50'>
                  <p className='text-gray-700'>
                    Your <span className='font-semibold'>submission receipt reference number</span> is:
                  </p>
                  <p className='text-lg font-mono font-semibold text-gray-900 mt-1'>
                    {submissionReceipt.referenceNumber}
                  </p>
                </div>

                <div className='rounded-lg border border-blue-200 bg-blue-50 p-6'>
                  <h3 className='text-lg font-semibold text-blue-900 mb-3'>What happens next?</h3>
                  <ul className='space-y-2 text-sm text-blue-800'>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>Your return has been received by HMRC and is being processed</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>Keep your submission receipt reference number safe for your records</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>If you have tax to pay, HMRC will send you payment instructions separately</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>You can view your submission history in your dashboard</span>
                    </li>
                  </ul>
                </div>

                <div className='rounded-lg border border-gray-200 bg-white p-5'>
                  <div className='flex items-start gap-4'>
                    <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 shrink-0'>
                      <FileCheck className='h-5 w-5 text-gray-600' />
                    </div>
                    <div className='flex-1'>
                      <h3 className='text-sm font-semibold text-gray-900'>Submission Receipt</h3>
                      <p className='mt-0.5 text-xs text-gray-500'>
                        Download your Self Assessment submission receipt as a PDF document.
                      </p>
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      <button
                        onClick={handleDownloadReceipt}
                        disabled={isDownloadingReceipt}
                        className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50'
                      >
                        {isDownloadingReceipt ? (
                          <>
                            <Loader2 className='h-4 w-4 animate-spin' />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className='h-4 w-4' />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className='rounded-lg border border-amber-200 bg-amber-50 p-6 w-full'>
                  <h3 className='text-base font-semibold text-amber-900 mb-2'>Would you like to keep your draft?</h3>
                  <p className='text-sm text-amber-800 mb-4'>
                    Your return has been submitted. You can keep the draft for your records or delete it to clean up.
                  </p>
                  <div className='flex items-center gap-3 justify-center w-full'>
                    <button
                      onClick={() => {
                        toast.success('Draft kept for your records.')
                        const backUrl = isSaFlow
                          ? '/add-self-assessment?flow=sa'
                          : '/add-self-assessment?flow=traditional'
                        navigate(backUrl)
                      }}
                      className='flex items-center gap-2 rounded-lg bg-white border border-amber-300 px-5 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition'
                    >
                      Keep Draft
                    </button>
                    <button
                      onClick={() => {
                        toast.success('Draft deleted successfully.')
                        const backUrl = isSaFlow
                          ? '/add-self-assessment?flow=sa'
                          : '/add-self-assessment?flow=traditional'
                        navigate(backUrl)
                      }}
                      className='flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition'
                    >
                      Delete Draft
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Default: Show payment prompt or submit to HMRC
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Submit Your Return</h2>
            </div>

            <div className='space-y-6'>
              {isPaidOrCovered ? (
                <div className='rounded-lg border border-green-200 bg-green-50 p-6'>
                  <div className='flex items-start gap-3'>
                    <CheckCircle2 className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-green-900'>Payment Complete - Ready to Submit</h3>
                      <p className='mt-2 text-sm text-green-700 leading-relaxed'>
                        Your platform payment has been successfully processed. Please enter your Government Gateway credentials below if not already filled, then submit your return to HMRC.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='rounded-lg border border-blue-200 bg-blue-50 p-6'>
                  <div className='flex items-start gap-3'>
                    <AlertCircle className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-blue-900'>Next: Platform Service Payment Required</h3>
                      <p className='mt-2 text-sm text-blue-700 leading-relaxed'>
                        Complete payment for your selected BritFiler service plan to continue.
                        This payment covers our platform service fee for preparing and submitting your tax return.
                        After payment, you'll enter your HMRC Government Gateway details and submit.
                      </p>
                      <div className='mt-3 rounded-lg border border-green-200 bg-green-50 p-3'>
                        <p className='text-xs font-semibold text-green-900 mb-1'>Important: This is a service fee, not your tax payment</p>
                        <p className='text-xs text-green-800 leading-relaxed'>
                          The payment you're about to make is for the <strong>BritFiler platform service</strong> (filing assistance, calculation, and submission).
                          This is separate from any tax you may owe to HMRC. If you have tax due, HMRC will contact you separately with payment instructions after your return is processed.
                        </p>
                      </div>
                      <p className='mt-3 text-sm text-blue-700 leading-relaxed'>
                        Click "Proceed to Payment" below to securely pay via Stripe. After payment, your return will be ready for submission to HMRC using your credentials.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Government Gateway credentials — shown only AFTER payment, so the
                  user provides them once, right before submitting to HMRC. */}
              {isPaidOrCovered && (
              <div className='rounded-lg border border-amber-200 bg-amber-50 p-6'>
                <h3 className='text-lg font-semibold text-amber-900 mb-1'>Government Gateway Credentials</h3>
                <p className='mb-4 text-sm text-amber-800 leading-relaxed'>
                  These are your HMRC Government Gateway login details (not your BritFiler password).
                  They are used once to submit your return securely and are not stored permanently.
                </p>
                <div className='space-y-4'>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700'>
                      Government Gateway User ID <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      value={governmentGatewayUserId}
                      onChange={(e) => setGovernmentGatewayUserId(e.target.value)}
                      placeholder='e.g. SA0239'
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      required
                    />
                    <p className='mt-1 text-xs text-gray-500'>This is usually a combination of letters and numbers</p>
                  </div>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700'>
                      Government Gateway Password <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='password'
                      value={governmentGatewayPassword}
                      onChange={(e) => setGovernmentGatewayPassword(e.target.value)}
                      placeholder='Your Government Gateway password'
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      required
                    />
                  </div>
                </div>
                <div className='mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                  <div className='flex items-start gap-2'>
                    <HelpCircle className='h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5' />
                    <p className='text-xs text-blue-700 leading-relaxed'>
                      <strong>Don't have a Government Gateway account?</strong> You can create one at{' '}
                      <a href='https://www.gov.uk/log-in-register-hmrc-online-services' target='_blank' rel='noopener noreferrer' className='underline hover:text-blue-800'>
                        gov.uk/log-in-register-hmrc-online-services
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              )}

              <div className='rounded-lg border border-gray-200 bg-white p-6'>
                <h3 className='text-base font-semibold text-gray-900 mb-4'>Declaration</h3>
                <div className='space-y-4 text-sm text-gray-700 leading-relaxed'>
                  <p>I confirm that:</p>
                  <ul className='list-disc list-inside space-y-2 ml-4'>
                    <li>The information I have given is correct and complete to the best of my knowledge</li>
                    <li>I understand that giving false information can lead to penalties</li>
                    <li>I have reviewed all the information in my return</li>
                    <li>I understand that HMRC will use the information to calculate any tax due</li>
                  </ul>
                </div>
              </div>

              {calculationResults && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-6'>
                  <h3 className='text-base font-semibold text-gray-900 mb-4'>Summary</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Tax Year:</span>
                      <span className='text-sm font-semibold text-gray-900'>{taxYear.replace('-', ' to ')}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Total Tax Due:</span>
                      <span className='text-sm font-semibold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>UTR:</span>
                      <span className='text-sm font-semibold text-gray-900'>{userProfile.utr}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* HMRC rejected the submission — show the reasons so the user can fix and resubmit */}
              {submissionErrors.length > 0 && (
                <div className='rounded-lg border border-red-200 bg-red-50 p-5'>
                  <div className='flex items-start gap-3'>
                    <AlertCircle className='h-5 w-5 flex-shrink-0 text-red-600 mt-0.5' />
                    <div className='flex-1'>
                      <h3 className='text-sm font-semibold text-red-900'>HMRC could not accept this return</h3>
                      <p className='mt-1 text-xs text-red-700'>
                        Please correct the following, then submit again:
                      </p>
                      <ul className='mt-2 list-disc space-y-1 pl-5 text-sm text-red-800'>
                        {submissionErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className='flex items-center justify-center pt-4'>
                {isPaidOrCovered ? (
                  <button
                    onClick={handleSubmitToHmrc}
                    disabled={isLoading || !governmentGatewayUserId || !governmentGatewayPassword}
                    className='flex items-center gap-2 rounded-xl bg-green-600 px-8 py-4 text-base font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 transition shadow-lg'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        Submitting to HMRC...
                      </>
                    ) : (
                      <>
                        <ArrowRight className='h-5 w-5' />
                        Submit to HMRC
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubmitReturn()}
                    disabled={isLoading}
                    className='flex items-center gap-2 rounded-xl bg-blue-600 px-8 py-4 text-base font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 transition shadow-lg'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ArrowRight className='h-5 w-5' />
                        Proceed to Payment
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )
  }

  // Full SA tax breakdown from the calculate-tax endpoint (all sections).
  const renderSaTaxBreakdown = () => {
    if (!saCalculation) return null
    const c: any = saCalculation
    const gbp = (n: any) =>
      `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    const bandLabel: Record<string, string> = {
      basic: 'Basic rate (20%)',
      higher: 'Higher rate (40%)',
      additional: 'Additional rate (45%)',
    }
    const row = (label: string, value: string, opts?: { strong?: boolean; muted?: boolean; danger?: boolean }) => (
      <div
        key={label}
        className={`flex items-center justify-between gap-4 py-1.5 ${opts?.strong ? 'mt-1 border-t border-gray-200 pt-2.5' : ''}`}
      >
        <span className={`text-sm ${opts?.strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
        <span
          className={`text-sm tabular-nums ${
            opts?.danger
              ? 'font-bold text-red-600'
              : opts?.strong
                ? 'font-bold text-gray-900'
                : opts?.muted
                  ? 'text-gray-500'
                  : 'font-medium text-gray-900'
          }`}
        >
          {value}
        </span>
      </div>
    )
    const section = (title: string, children: React.ReactNode) => (
      <div className='min-w-0 rounded-xl border border-gray-200 bg-white p-5'>
        <h4 className='mb-3 text-sm font-semibold uppercase tracking-wide text-[#001B38]'>{title}</h4>
        <div className='space-y-0.5'>{children}</div>
      </div>
    )

    const ib = c.incomeBreakdown || {}
    const it = c.incomeTax || {}
    const ni = c.nationalInsurance || {}
    const cgt = c.capitalGainsTax || {}
    const sl = c.studentLoan || {}

    return (
      <div className='space-y-5'>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          {section(
            'Income breakdown',
            <>
              {ib.employmentIncome ? row('Employment', gbp(ib.employmentIncome)) : null}
              {ib.selfEmploymentIncome ? row('Self-employment', gbp(ib.selfEmploymentIncome)) : null}
              {ib.propertyIncome ? row('Property', gbp(ib.propertyIncome)) : null}
              {ib.dividendsIncome ? row('Dividends', gbp(ib.dividendsIncome)) : null}
              {ib.savingsIncome ? row('Savings interest', gbp(ib.savingsIncome)) : null}
              {ib.pensionsIncome ? row('Pensions', gbp(ib.pensionsIncome)) : null}
              {ib.otherIncome ? row('Other income', gbp(ib.otherIncome)) : null}
              {row('Total income', gbp(ib.totalIncome), { strong: true })}
            </>,
          )}

          {section(
            'Allowances & taxable income',
            <>
              {row('Personal allowance', gbp(c.personalAllowance))}
              {row('Taxable income', gbp(c.taxableIncome), { strong: true })}
            </>,
          )}

          {section(
            'Income tax',
            <>
              {row('Tax on earnings', gbp(it.taxOnEarnings))}
              {row('Tax on dividends', gbp(it.taxOnDividends))}
              {row('Tax on savings', gbp(it.taxOnSavings))}
              {it.topRateBand ? row('Top rate band', bandLabel[it.topRateBand] || it.topRateBand, { muted: true }) : null}
              {it.dividendAllowanceUsed
                ? row('Dividend allowance used', gbp(it.dividendAllowanceUsed), { muted: true })
                : null}
              {it.savingsAllowanceUsed
                ? row('Savings allowance used', gbp(it.savingsAllowanceUsed), { muted: true })
                : null}
              {row('Total income tax', gbp(it.totalTax), { strong: true })}
            </>,
          )}

          {section(
            'National Insurance',
            <>
              {row('Class 2 NI', gbp(ni.class2NI))}
              {row('Class 4 NI', gbp(ni.class4NI))}
              {row('Total NI', gbp(ni.totalNI), { strong: true })}
            </>,
          )}

          {(cgt.totalCGT || cgt.taxableGains) ? section(
            'Capital Gains Tax',
            <>
              {cgt.totalGains ? row('Total gains', gbp(cgt.totalGains)) : null}
              {cgt.annualExemption ? row('Annual exemption', gbp(cgt.annualExemption), { muted: true }) : null}
              {row('Taxable gains', gbp(cgt.taxableGains))}
              {cgt.cgtOnAssets ? row('CGT on assets', gbp(cgt.cgtOnAssets)) : null}
              {cgt.cgtOnProperty ? row('CGT on property', gbp(cgt.cgtOnProperty)) : null}
              {row('Total CGT', gbp(cgt.totalCGT), { strong: true })}
            </>,
          ) : null}

          {(sl.totalRepayment) ? section(
            'Student loan',
            <>
              {sl.plan1Repayment ? row('Plan 1 repayment', gbp(sl.plan1Repayment)) : null}
              {sl.plan2Repayment ? row('Plan 2 repayment', gbp(sl.plan2Repayment)) : null}
              {sl.postGraduateRepayment ? row('Postgraduate repayment', gbp(sl.postGraduateRepayment)) : null}
              {row('Total repayment', gbp(sl.totalRepayment), { strong: true })}
            </>,
          ) : null}
        </div>

        {/* Grand total */}
        <div className='rounded-xl border-2 border-[#001B38] bg-[#001B38] p-5 text-white sm:p-6'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <p className='text-xs font-medium uppercase tracking-wide text-blue-100'>Total tax due</p>
              <p className='mt-1 text-2xl font-bold tabular-nums sm:text-3xl'>{gbp(c.totalTaxDue)}</p>
            </div>
            <div className='sm:text-right'>
              <p className='text-xs font-medium uppercase tracking-wide text-blue-100'>Effective tax rate</p>
              <p className='mt-1 text-xl font-bold tabular-nums sm:text-2xl'>
                {Number(c.effectiveTaxRate || 0).toFixed(2)}%
              </p>
            </div>
          </div>
          <p className='mt-3 text-xs leading-relaxed text-blue-100'>
            This is an estimate for tax year {c.taxYear || taxYear.replace('-', '–')}. HMRC calculates the final figure
            when your return is submitted.
          </p>
        </div>
      </div>
    )
  }

  const renderStepContent = () => {
    // Show loading spinner for step content
    if (isLoading && currentStep === 1) {
      return (
        <div className='flex min-h-[400px] items-center justify-center'>
          <div className='text-center'>
            <Loader2 className='mx-auto h-12 w-12 animate-spin text-gray-400' />
            <p className='mt-4 text-sm text-gray-600'>Loading your details...</p>
          </div>
        </div>
      )
    }

    switch (currentStep) {
      case 1:
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Personal Details</h2>
            </div>

            <div className='space-y-4'>
              {/* Name Information Section */}
              <CollapsibleSection title="Name Information">
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4'>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>First Name</label>
                <input
                  type='text'
                  value={personalDetails.firstName}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, firstName: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>Middle Name</label>
                <input
                  type='text'
                  value={personalDetails.middleName}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, middleName: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700'>Last Name</label>
                <input
                  type='text'
                  value={personalDetails.lastName}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, lastName: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>
            </div>
              </CollapsibleSection>

              {/* Contact Information Section */}
              <CollapsibleSection title="Contact Information">
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4'>
              <div>
                    <div className='mb-2 flex items-center gap-2'>
                      <label className='text-sm font-medium text-gray-700'>Telephone Number</label>
                      <HelpTooltip content={getHelpText('telephoneNumber')} position="right" maxWidth="400px" />
                    </div>
                <div className='relative'>
                  <input
                    type='tel'
                    value={personalDetails.telephoneNumber}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, telephoneNumber: e.target.value })}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                </div>
              </div>
              <div>
                    <div className='mb-2 flex items-center gap-2'>
                      <label className='text-sm font-medium text-gray-700'>Email Address</label>
                      <HelpTooltip content={getHelpText('emailAddress')} position="right" maxWidth="400px" />
                    </div>
                <div className='relative'>
                  <input
                    type='email'
                    value={personalDetails.emailAddress}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, emailAddress: e.target.value })}
                        className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                </div>
              </div>
              <div>
                    <div className='mb-2 flex items-center gap-2'>
                      <label className='text-sm font-medium text-gray-700'>Which best describes you?</label>
                      <HelpTooltip content={getHelpText('companyType')} position="right" maxWidth="400px" />
                    </div>
                <div className='relative'>
                  <select
                    value={personalDetails.companyType}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, companyType: e.target.value })}
                    className='w-full appearance-none rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  >
                    <option value=''>Select company type</option>
                    <option value='sole-trader'>Sole Trader</option>
                    <option value='partnership'>Partnership</option>
                    <option value='limited'>Limited Company</option>
                  </select>
                  <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                </div>
              </div>
            </div>
              </CollapsibleSection>

              {/* Address Section */}
              <CollapsibleSection title="Address">
                <div className='space-y-4 pt-4'>
            <div>
                <label className='mb-2 block text-xs font-medium text-gray-600'>Address Line 1 <span className='text-red-500'>*</span></label>
                <input
                  type='text'
                  value={personalDetails.address.line1}
                  onChange={(e) => setPersonalDetails({ 
                    ...personalDetails, 
                    address: { ...personalDetails.address, line1: e.target.value }
                  })}
                  placeholder='e.g., 123 High Street'
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>

              <div>
                <label className='mb-2 block text-xs font-medium text-gray-600'>Address Line 2</label>
                <input
                  type='text'
                  value={personalDetails.address.line2}
                  onChange={(e) => setPersonalDetails({ 
                    ...personalDetails, 
                    address: { ...personalDetails.address, line2: e.target.value }
                  })}
                  placeholder='e.g., Apartment 4B'
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>

              <div>
                <label className='mb-2 block text-xs font-medium text-gray-600'>Address Line 3</label>
                <input
                  type='text'
                  value={personalDetails.address.line3}
                  onChange={(e) => setPersonalDetails({ 
                    ...personalDetails, 
                    address: { ...personalDetails.address, line3: e.target.value }
                  })}
                  placeholder='e.g., Westminster'
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>

              <div>
                <label className='mb-2 block text-xs font-medium text-gray-600'>Address Line 4</label>
                <input
                  type='text'
                  value={personalDetails.address.line4}
                  onChange={(e) => setPersonalDetails({ 
                    ...personalDetails, 
                    address: { ...personalDetails.address, line4: e.target.value }
                  })}
                  placeholder='e.g., Greater London'
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='mb-2 block text-xs font-medium text-gray-600'>Postcode <span className='text-red-500'>*</span></label>
                  <input
                    type='text'
                    value={personalDetails.address.postcode}
                    onChange={(e) => setPersonalDetails({ 
                      ...personalDetails, 
                      address: { ...personalDetails.address, postcode: e.target.value }
                    })}
                    placeholder='e.g., SW1A 1AA'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                </div>

                <div>
                  <label className='mb-2 block text-xs font-medium text-gray-600'>Country <span className='text-red-500'>*</span></label>
                  <select
                    value={personalDetails.address.country}
                    onChange={(e) => setPersonalDetails({ 
                      ...personalDetails, 
                      address: { ...personalDetails.address, country: e.target.value }
                    })}
                    className='w-full appearance-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  >
                    <option value='GB'>United Kingdom</option>
                    <option value='IE'>Ireland</option>
                    <option value='US'>United States</option>
                    <option value='CA'>Canada</option>
                    <option value='AU'>Australia</option>
                    <option value='NZ'>New Zealand</option>
                    <option value='FR'>France</option>
                    <option value='DE'>Germany</option>
                    <option value='ES'>Spain</option>
                    <option value='IT'>Italy</option>
                    <option value='NL'>Netherlands</option>
                    <option value='BE'>Belgium</option>
                    <option value='PT'>Portugal</option>
                    <option value='CH'>Switzerland</option>
                    <option value='AT'>Austria</option>
                    <option value='SE'>Sweden</option>
                    <option value='NO'>Norway</option>
                    <option value='DK'>Denmark</option>
                    <option value='FI'>Finland</option>
                    <option value='PL'>Poland</option>
                    <option value='GR'>Greece</option>
                    <option value='OTHER'>Other</option>
                  </select>
              </div>
            </div>

                  <div className='flex items-center justify-between pt-2'>
              <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-gray-700'>Is the address above correct?</span>
                      <HelpTooltip content={getHelpText('addressCorrect')} position="right" maxWidth="400px" />
              </div>
              <div className='flex items-center gap-6'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='addressCorrect'
                    value='yes'
                    checked={personalDetails.isAddressCorrect === 'yes'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, isAddressCorrect: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Yes</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='addressCorrect'
                    value='no'
                    checked={personalDetails.isAddressCorrect === 'no'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, isAddressCorrect: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>No</span>
                </label>
              </div>
            </div>
                </div>
              </CollapsibleSection>

              {/* Tax Information Section */}
              <CollapsibleSection title="Tax Information">
                <div className='pt-4'>
              <div className='mb-3 flex items-center gap-2'>
                <label className='text-sm font-medium text-gray-700'>Tax Payer Residency Status</label>
                    <HelpTooltip content={getHelpText('taxpayerResidency', false)} position="right" maxWidth="450px" />
              </div>
              <div className='flex items-center gap-6'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='taxPayerResidency'
                    value='english'
                    checked={personalDetails.taxPayerResidency === 'english'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>English and Northern Irish</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='taxPayerResidency'
                    value='scottish'
                    checked={personalDetails.taxPayerResidency === 'scottish'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Scottish</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='taxPayerResidency'
                    value='welsh'
                    checked={personalDetails.taxPayerResidency === 'welsh'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Welsh</span>
                </label>
              </div>
                </div>
              </CollapsibleSection>
            </div>

            {/* Date of Birth and Marital Status */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <div className='mb-2 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-700'>Date Of Birth</label>
                  <HelpTooltip content={getHelpText('dateOfBirth')} position="right" maxWidth="400px" />
                </div>
                <div className='relative'>
                  <input
                    type='date'
                    value={personalDetails.dateOfBirth}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, dateOfBirth: e.target.value })}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 pl-10 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                  <svg className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                  </svg>
                </div>
              </div>
              <div>
                <div className='mb-2 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-700'>Marital Status</label>
                  <HelpTooltip content={getHelpText('maritalStatus')} position="right" maxWidth="400px" />
                </div>
                <div className='relative'>
                  <select
                    value={personalDetails.maritalStatus}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, maritalStatus: e.target.value })}
                    className='w-full appearance-none rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  >
                    <option value='single'>Single</option>
                    <option value='married'>Married</option>
                    <option value='divorced'>Divorced</option>
                    <option value='widowed'>Widowed</option>
                  </select>
                  <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                </div>
              </div>
            </div>

            {/* Blind Person's Allowance */}
            <div>
              <div className='mb-3 flex items-center gap-2'>
                <label className='text-sm font-medium text-gray-700'>
                  Are you or your spouse or civil partner registered blind on a local authority or other register?
                </label>
                <HelpTooltip content={getHelpText('blindPersonAllowance', false)} position="right" maxWidth="400px" />
              </div>
              <div className='flex items-center gap-6'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='isBlind'
                    value='yes'
                    checked={personalDetails.isBlind === 'yes'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, isBlind: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Yes</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='isBlind'
                    value='no'
                    checked={personalDetails.isBlind === 'no'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, isBlind: e.target.value as 'yes' | 'no', blindLocalAuthority: '' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>No</span>
                </label>
              </div>

              {/* Conditional: Local authority name when blind = yes */}
              {personalDetails.isBlind === 'yes' && (
                <div className='mt-4 pl-4 border-l-2 border-blue-200'>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-700'>
                      Name of local authority or other register
                    </label>
                    <HelpTooltip content="Enter the name of the local authority or other body with which you or your spouse/civil partner are registered as blind or severely sight impaired." position="right" maxWidth="400px" />
                  </div>
                  <input
                    type='text'
                    value={personalDetails.blindLocalAuthority}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, blindLocalAuthority: e.target.value })}
                    placeholder='e.g. Birmingham City Council'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                </div>
              )}
            </div>

            {/* Student Loan ICR */}
            <div>
              <div className='mb-3 flex items-center gap-2'>
                <label className='text-sm font-medium text-gray-700'>
                  Did you receive notification from Student Loans Company that repayment of an Income Contingent Student Loan began before {getTaxYearDates().endDate}?
                </label>
                <HelpTooltip content={getHelpText('studentLoanICR', false)} position="right" maxWidth="450px" />
              </div>
              <div className='flex items-center gap-6'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='hasStudentLoan'
                    value='yes'
                    checked={personalDetails.hasStudentLoan === 'yes'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, hasStudentLoan: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Yes</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='hasStudentLoan'
                    value='no'
                    checked={personalDetails.hasStudentLoan === 'no'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, hasStudentLoan: e.target.value as 'yes' | 'no', studentLoanPlanType: undefined })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>No</span>
                </label>
              </div>
            </div>

            {/* Student Loan Plan Type - Conditional */}
            {personalDetails.hasStudentLoan === 'yes' && (
              <div className='ml-6 rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <label className='mb-2 block text-sm font-medium text-gray-900'>Student Loan Plan Type</label>
                <div className='space-y-2'>
                  <label className='flex items-start gap-2'>
                    <input
                      type='radio'
                      name='studentLoanPlanType'
                      value='1'
                      checked={personalDetails.studentLoanPlanType === '1'}
                      onChange={(e) => setPersonalDetails({ ...personalDetails, studentLoanPlanType: e.target.value as '1' | '2' | '4' })}
                      className='mt-0.5 h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm text-gray-700'>
                      <strong>Plan 1</strong> - Threshold £24,990 (9% repayment rate)
                    </span>
                  </label>
                  <label className='flex items-start gap-2'>
                    <input
                      type='radio'
                      name='studentLoanPlanType'
                      value='2'
                      checked={personalDetails.studentLoanPlanType === '2'}
                      onChange={(e) => setPersonalDetails({ ...personalDetails, studentLoanPlanType: e.target.value as '1' | '2' | '4' })}
                      className='mt-0.5 h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm text-gray-700'>
                      <strong>Plan 2</strong> - Threshold £27,295 (9% repayment rate)
                    </span>
                  </label>
                  <label className='flex items-start gap-2'>
                    <input
                      type='radio'
                      name='studentLoanPlanType'
                      value='4'
                      checked={personalDetails.studentLoanPlanType === '4'}
                      onChange={(e) => setPersonalDetails({ ...personalDetails, studentLoanPlanType: e.target.value as '1' | '2' | '4' })}
                      className='mt-0.5 h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm text-gray-700'>
                      <strong>Plan 4</strong> - Threshold £31,395 (9% repayment rate)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Postgraduate Loan */}
            <div>
              <div className='mb-3 flex items-center gap-2'>
                <label className='text-sm font-medium text-gray-700'>
                  Did you receive notification from Student Loans Company that repayment of a Postgraduate Loan began before {getTaxYearDates().endDate}?
                </label>
                <HelpTooltip content={getHelpText('studentLoanPostgraduate', false)} position="right" maxWidth="450px" />
              </div>
              <div className='flex items-center gap-6'>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='hasPostgraduateLoan'
                    value='yes'
                    checked={personalDetails.hasPostgraduateLoan === 'yes'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, hasPostgraduateLoan: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>Yes</span>
                </label>
                <label className='flex items-center gap-2'>
                  <input
                    type='radio'
                    name='hasPostgraduateLoan'
                    value='no'
                    checked={personalDetails.hasPostgraduateLoan === 'no'}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, hasPostgraduateLoan: e.target.value as 'yes' | 'no' })}
                    className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                  />
                  <span className='text-sm font-medium text-gray-700'>No</span>
                </label>
              </div>
            </div>

            {/* Postgraduate Loan Info - Conditional */}
            {personalDetails.hasPostgraduateLoan === 'yes' && (
              <div className='ml-6 rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <p className='text-sm text-gray-700'>
                  <strong>Postgraduate Loan:</strong> Threshold £21,000 - Repayment rate 6% on income over threshold
                </p>
              </div>
            )}
          </div>
        )

      case 2:
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Tailor Your Return</h2>
            </div>

            <p className='text-sm text-gray-600 leading-relaxed mb-3'>
              Answer a few questions about your income and tax circumstances for the selected tax year. We'll personalise your Self Assessment return so you only complete the sections that apply to you.
            </p>
            <p className='text-sm text-gray-600 leading-relaxed mb-6'>
              For the tax year <span className='font-semibold'>{getTaxYearDates().yearRange}</span>, select everything that applies to you.
            </p>

            <div className='space-y-4'>
              {/* Income Sources Section */}
              <CollapsibleSection title="Income Sources">
                <div className='space-y-6 pt-4'>
              {/* Was Employee Question */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>
                    Were you an employee (or director or office holder) or agency worker in the year to {getTaxYearDates().endDate}?
                  </label>
                  <HelpTooltip content={getHelpText('wasEmployee', false)} position="right" maxWidth="450px" />
                </div>
                <div className='flex items-center gap-6'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='wasEmployee'
                      value='yes'
                      checked={tailorReturn.wasEmployee === 'yes'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, wasEmployee: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>Yes</span>
                  </label>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='wasEmployee'
                      value='no'
                      checked={tailorReturn.wasEmployee === 'no'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, wasEmployee: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>No</span>
                  </label>
                </div>
              </div>

              {/* Self Employment Question */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>
                    Were you self-employed or in a partnership during the year?
                  </label>
                  <HelpTooltip content={getHelpText('wasSelfEmployed', false)} position="right" maxWidth="450px" />
                </div>
                <div className='flex items-center gap-6'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='wasSelfEmployed'
                      value='yes'
                      checked={tailorReturn.wasSelfEmployed === 'yes'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, wasSelfEmployed: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>Yes</span>
                  </label>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='wasSelfEmployed'
                      value='no'
                      checked={tailorReturn.wasSelfEmployed === 'no'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, wasSelfEmployed: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>No</span>
                  </label>
                </div>
                
                {/* Trading Income Allowance Info */}
                {tailorReturn.wasSelfEmployed === 'yes' && (
                  <div className='mt-3 rounded-lg border border-green-200 bg-green-50 p-4'>
                    <div className='flex items-start gap-2'>
                      <AlertCircle className='h-5 w-5 flex-shrink-0 text-green-600 mt-0.5' />
                      <div>
                        <h4 className='text-sm font-semibold text-green-900 mb-1'>Trading Income Allowance</h4>
                        <p className='text-xs text-green-800 leading-relaxed'>
                          If your turnover from all self-employments plus miscellaneous income was up to £1,000, it is exempt from tax and does not need to be reported on a tax return. The trading income allowance of up to £1,000 can be split across more than one business and against miscellaneous income.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Property Income Question */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>
                    Did you receive any UK property income?
                  </label>
                  <HelpTooltip content={getHelpText('hasProperty', false)} position="right" maxWidth="400px" />
                </div>
                <div className='flex items-center gap-6'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='hasProperty'
                      value='yes'
                      checked={tailorReturn.hasProperty === 'yes'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, hasProperty: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>Yes</span>
                  </label>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='hasProperty'
                      value='no'
                      checked={tailorReturn.hasProperty === 'no'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, hasProperty: e.target.value as 'yes' | 'no' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>No</span>
                  </label>
                </div>
                
                {/* Property Income Allowance Info */}
                {tailorReturn.hasProperty === 'yes' && (
                  <div className='mt-3 rounded-lg border border-green-200 bg-green-50 p-4'>
                    <div className='flex items-start gap-2'>
                      <AlertCircle className='h-5 w-5 flex-shrink-0 text-green-600 mt-0.5' />
                      <div>
                        <h4 className='text-sm font-semibold text-green-900 mb-1'>Property Income Allowance</h4>
                        <p className='text-xs text-green-800 leading-relaxed'>
                          All income from property, including any foreign property, up to £1,000 is exempt from tax and does not need to be reported on a tax return, unless the income is from a connected party. If your total property income was less than £1,000, you may still choose to fill in the UK property section if your allowable expenses are higher than your turnover.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

                </div>
              </CollapsibleSection>

              {/* Additional Information Section */}
              <CollapsibleSection title="Additional Information">
                <div className='space-y-6 pt-4'>
              {/* Tax Payer Residency Status */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>Tax Payer Residency Status</label>
                      <HelpTooltip content="Select your tax residency status. This determines which tax rates apply to your income. Scottish taxpayers have different income tax rates, and Welsh taxpayers may have different rates in the future." position="right" maxWidth="400px" />
                </div>
                <div className='flex items-center gap-6'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='taxPayerResidency2'
                      value='english'
                      checked={tailorReturn.taxPayerResidency === 'english'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>English and Northern Irish</span>
                  </label>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='taxPayerResidency2'
                      value='scottish'
                      checked={tailorReturn.taxPayerResidency === 'scottish'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>Scottish</span>
                  </label>
                  <label className='flex items-center gap-2'>
                    <input
                      type='radio'
                      name='taxPayerResidency2'
                      value='welsh'
                      checked={tailorReturn.taxPayerResidency === 'welsh'}
                      onChange={(e) => setTailorReturn({ ...tailorReturn, taxPayerResidency: e.target.value as 'english' | 'scottish' | 'welsh' })}
                      className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                    />
                    <span className='text-sm font-medium text-gray-700'>Welsh</span>
                  </label>
                </div>
              </div>

              {/* Employment details — only relevant if the user was an employee (Q1 = Yes).
                  When they were not an employee, the only field here is residency status. */}
              {tailorReturn.wasEmployee === 'yes' && (
                <>
              {/* Number of Employments */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>How many employments or directorships did you have?</label>
                      <HelpTooltip content="Enter the total number of different employers or directorships you held during the tax year." position="right" maxWidth="400px" />
                </div>
                <input
                  type='text'
                  value={tailorReturn.numberOfEmployments}
                  onChange={(e) => {
                    const val = e.target.value
                    const num = parseInt(val) || 0
                    // Resize employerNames array to match
                    const newNames = [...tailorReturn.employerNames]
                    if (num > newNames.length) {
                      for (let i = newNames.length; i < num; i++) newNames.push('')
                    } else if (num < newNames.length && num > 0) {
                      newNames.length = num
                    }
                    // Resize employerDetailsList to match
                    setEmployerDetailsList(prev => {
                      if (num > prev.length) {
                        const extended = [...prev]
                        for (let i = prev.length; i < num; i++) {
                          extended.push(createDefaultEmployer(newNames[i] || '', i))
                        }
                        return extended
                      } else if (num < prev.length && num > 0) {
                        return prev.slice(0, num)
                      }
                      return prev
                    })
                    // Reset active tab if out of bounds
                    if (activeEmployerIndex >= num && num > 0) {
                      setActiveEmployerIndex(num - 1)
                    }
                    setTailorReturn({ ...tailorReturn, numberOfEmployments: val, employerNames: newNames })
                  }}
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                />
              </div>

              {/* Employer Names - one input per declared employment */}
              <div>
                <div className='mb-3 flex items-center gap-2'>
                  <label className='text-sm font-medium text-gray-900'>Please provide us with the name of your employer(s):</label>
                      <HelpTooltip content="Enter the name(s) of your employer(s) or the company(ies) where you held directorships during the tax year." position="right" maxWidth="400px" />
                </div>
                <div className='space-y-3'>
                  {tailorReturn.employerNames.map((name, idx) => (
                    <div key={idx}>
                      {tailorReturn.employerNames.length > 1 && (
                        <label className='text-xs font-medium text-gray-500 mb-1 block'>Employer {idx + 1}</label>
                      )}
                      <input
                        type='text'
                        placeholder={`Employer ${idx + 1} name`}
                        value={name}
                        onChange={(e) => {
                          const newNames = [...tailorReturn.employerNames]
                          newNames[idx] = e.target.value
                          setTailorReturn({ ...tailorReturn, employerNames: newNames })
                          // Also sync the name into the corresponding employer detail
                          setEmployerDetailsList(prev => prev.map((emp, i) =>
                            i === idx ? { ...emp, employerName: e.target.value } : emp
                          ))
                          if (validationErrors[`employerName_${idx}`]) {
                            setValidationErrors(prev => {
                              const next = { ...prev }
                              delete next[`employerName_${idx}`]
                              return next
                            })
                          }
                        }}
                        className={`w-full rounded-lg border px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-1 ${
                          validationErrors[`employerName_${idx}`]
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-[#001B38] focus:ring-[#001B38]'
                        }`}
                      />
                      {validationErrors[`employerName_${idx}`] && (
                        <p className='mt-1 text-sm text-red-600'>{validationErrors[`employerName_${idx}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Info Box */}
              <div className='flex items-start gap-3 rounded-lg border border-gray-300 bg-gray-50 p-4'>
                <HelpCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400' />
                <p className='text-sm text-gray-700'>
                  If you are self employed or in a partnership, you must register to pay Class 2 National Insurance.
                </p>
              </div>
                </>
              )}
                </div>
              </CollapsibleSection>
            </div>
          </div>
        )

      case 3:
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Fill in Your Return</h2>
            </div>


            <div className='space-y-4'>
            {/* Employment Income Section - Show if wasEmployee === 'yes' */}
            {tailorReturn.wasEmployee === 'yes' && (
              <CollapsibleSection title="Employment Income">
                <div className='space-y-6 pt-4'>
                <p className='text-sm text-gray-700'>
                  You have said that you were employed in the tax year {taxYear.replace('-', ' to ')}. Please complete the following question(s)
                </p>

                {/* Employer Tabs - show if multiple employers */}
                {employerDetailsList.length > 1 && (
                  <div className='flex gap-1 border-b border-gray-200 overflow-x-auto'>
                    {employerDetailsList.map((emp, idx) => (
                      <button
                        key={emp.employerId}
                        type='button'
                        onClick={() => setActiveEmployerIndex(idx)}
                        className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                          activeEmployerIndex === idx
                            ? 'border-gray-900 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Employer {idx + 1}{emp.employerName ? `: ${emp.employerName}` : ''}
                      </button>
                    ))}
                  </div>
                )}

                {/* Per-employer Employment Income Form */}
                {(() => {
                  const currentEmp = employerDetailsList[activeEmployerIndex] || employerDetailsList[0]
                  if (!currentEmp) return null
                  const empIdx = activeEmployerIndex

                  const updateEmployer = (field: string, value: any) => {
                    setEmployerDetailsList(prev => prev.map((emp, i) =>
                      i === empIdx ? { ...emp, [field]: value } : emp
                    ))
                  }

                  return (
                    <div className='space-y-6'>
                {/* Employer Name */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>Employer name</label>
                    <HelpTooltip content={getHelpText('employerName')} position="right" maxWidth="400px" />
                  </div>
                  <input
                    type='text'
                    value={currentEmp.employerName}
                    onChange={(e) => {
                      updateEmployer('employerName', e.target.value)
                      // Sync name back to tailorReturn.employerNames
                      setTailorReturn(prev => {
                        const newNames = [...prev.employerNames]
                        newNames[empIdx] = e.target.value
                        return { ...prev, employerNames: newNames }
                      })
                      if (validationErrors[`employerName_${empIdx}`]) {
                        setValidationErrors(prev => {
                          const next = { ...prev }
                          delete next[`employerName_${empIdx}`]
                          return next
                        })
                      }
                    }}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-1 ${
                      validationErrors[`employerName_${empIdx}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#001B38] focus:ring-[#001B38]'
                    }`}
                  />
                  {validationErrors[`employerName_${empIdx}`] && (
                    <p className='mt-1 text-sm text-red-600'>{validationErrors[`employerName_${empIdx}`]}</p>
                  )}
                </div>

                {/* Employment Dates */}
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <div className='mb-2 flex items-center gap-2'>
                      <label className='text-sm font-medium text-gray-900'>Employment start date</label>
                      <HelpTooltip content={getHelpText('startDate')} position="right" maxWidth="400px" />
                    </div>
                    <input
                      type='date'
                      value={currentEmp.startDate}
                      onChange={(e) => {
                        updateEmployer('startDate', e.target.value)
                        if (validationErrors[`startDate_${empIdx}`]) {
                          setValidationErrors(prev => {
                            const next = { ...prev }
                            delete next[`startDate_${empIdx}`]
                            return next
                          })
                        }
                      }}
                      className={`w-full rounded-lg border px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-1 ${
                        validationErrors[`startDate_${empIdx}`]
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-[#001B38] focus:ring-[#001B38]'
                      }`}
                    />
                    {validationErrors[`startDate_${empIdx}`] && (
                      <p className='mt-1 text-sm text-red-600'>{validationErrors[`startDate_${empIdx}`]}</p>
                    )}
                  </div>
                  <div>
                    <div className='mb-2 flex items-center gap-2'>
                      <label className='text-sm font-medium text-gray-900'>Employment cessation date (optional)</label>
                      <HelpTooltip content={getHelpText('cessationDate')} position="right" maxWidth="400px" />
                    </div>
                    <input
                      type='date'
                      value={currentEmp.cessationDate}
                      onChange={(e) => updateEmployer('cessationDate', e.target.value)}
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                    />
                  </div>
                </div>

                {/* Pay Frequency */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>Pay frequency</label>
                    <HelpTooltip content={getHelpText('payFrequency')} position="right" maxWidth="400px" />
                  </div>
                  <div className='relative'>
                    <select
                      value={currentEmp.payFrequency}
                      onChange={(e) => updateEmployer('payFrequency', e.target.value as 'monthly' | 'weekly')}
                      className='w-full appearance-none rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                    >
                      <option value='monthly'>Monthly</option>
                      <option value='weekly'>Weekly</option>
                    </select>
                    <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                  </div>
                </div>

            {/* Employer's PAYE Reference */}
            <div>
              <div className='mb-2 flex items-center gap-2'>
                <label className='text-sm font-medium text-gray-900'>Employer's PAYE Reference</label>
                <HelpTooltip content={getHelpText('payeReference')} position="right" maxWidth="400px" />
              </div>
              <input
                type='text'
                value={currentEmp.payeReference}
                onChange={(e) => updateEmployer('payeReference', e.target.value)}
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
              />
            </div>

                {/* Payroll ID */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>Payroll ID</label>
                    <HelpTooltip content={getHelpText('payrollId')} position="right" maxWidth="400px" />
                  </div>
                  <input
                    type='text'
                    value={currentEmp.payrollId}
                    onChange={(e) => {
                      updateEmployer('payrollId', e.target.value)
                      if (validationErrors[`payrollId_${empIdx}`]) {
                        setValidationErrors(prev => {
                          const next = { ...prev }
                          delete next[`payrollId_${empIdx}`]
                          return next
                        })
                      }
                    }}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-1 ${
                      validationErrors[`payrollId_${empIdx}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#001B38] focus:ring-[#001B38]'
                    }`}
                  />
                  {validationErrors[`payrollId_${empIdx}`] && (
                    <p className='mt-1 text-sm text-red-600'>{validationErrors[`payrollId_${empIdx}`]}</p>
                  )}
            </div>

                {/* Pay from Employer */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>
                      Total from your P45 or P60. Enter the amount before tax taken off
                    </label>
                    <HelpTooltip content={getHelpText('totalPay')} position="right" maxWidth="400px" />
                  </div>
                  <input
                    type='text'
                    value={formatAmountDisplay(currentEmp.totalPay)}
                    onChange={(e) => {
                      updateEmployer('totalPay', stripCommas(e.target.value))
                      if (validationErrors[`totalPay_${empIdx}`]) {
                        setValidationErrors(prev => {
                          const next = { ...prev }
                          delete next[`totalPay_${empIdx}`]
                          return next
                        })
                      }
                    }}
                    onBlur={(e) => {
                      const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                      if (formatted) updateEmployer('totalPay', stripCommas(formatted))
                    }}
                    placeholder='0.00'
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-1 ${
                      validationErrors[`totalPay_${empIdx}`]
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-[#001B38] focus:ring-[#001B38]'
                    }`}
                  />
                  {validationErrors[`totalPay_${empIdx}`] && (
                    <p className='mt-1 text-sm text-red-600'>{validationErrors[`totalPay_${empIdx}`]}</p>
                  )}
                </div>

                {/* Total Tax Taken Off */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>
                      Total tax taken off
                    </label>
                    <HelpTooltip content={getHelpText('totalTax')} position="right" maxWidth="400px" />
                  </div>
                  <input
                    type='text'
                    value={formatAmountDisplay(currentEmp.totalTax)}
                    onChange={(e) => updateEmployer('totalTax', stripCommas(e.target.value))}
                    onBlur={(e) => {
                      const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                      if (formatted) updateEmployer('totalTax', stripCommas(formatted))
                    }}
                    placeholder='0.00'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                  />
                </div>

                {/* Occupational Pension */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>
                      Did this employment include an occupational pension?
                    </label>
                    <HelpTooltip content={getHelpText('occupationalPension')} position="right" maxWidth="400px" />
                  </div>
                  <div className='flex items-center gap-6'>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name={`occupationalPension_${empIdx}`}
                        value='yes'
                        checked={currentEmp.occupationalPension === 'yes'}
                        onChange={() => updateEmployer('occupationalPension', 'yes')}
                        className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                      />
                      <span className='text-sm font-medium text-gray-700'>Yes</span>
                    </label>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name={`occupationalPension_${empIdx}`}
                        value='no'
                        checked={currentEmp.occupationalPension === 'no'}
                        onChange={() => updateEmployer('occupationalPension', 'no')}
                        className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                      />
                      <span className='text-sm font-medium text-gray-700'>No</span>
                    </label>
                  </div>
                </div>

                {/* Student Loan Deductions — collapsed by default */}
                <CollapsibleSection title="Student Loan Deductions">
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div>
                      <div className='mb-2 flex items-center gap-2'>
                        <label className='text-sm font-medium text-gray-900'>UGL deduction amount</label>
                        <HelpTooltip content="Undergraduate Loan (UGL) deduction amount from your payslip. This is the total amount deducted for student loan repayments during the tax year." position="right" maxWidth="400px" />
                      </div>
                      <input
                        type='text'
                        value={formatAmountDisplay(currentEmp.studentLoanDeductions.uglDeductionAmount)}
                        onChange={(e) =>
                          setEmployerDetailsList(prev => prev.map((emp, i) =>
                            i === empIdx ? { ...emp, studentLoanDeductions: { ...emp.studentLoanDeductions, uglDeductionAmount: stripCommas(e.target.value) } } : emp
                          ))
                        }
                        onBlur={(e) => {
                          const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                          if (formatted) setEmployerDetailsList(prev => prev.map((emp, i) =>
                            i === empIdx ? { ...emp, studentLoanDeductions: { ...emp.studentLoanDeductions, uglDeductionAmount: stripCommas(formatted) } } : emp
                          ))
                        }}
                        className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      />
                    </div>
                    <div>
                      <div className='mb-2 flex items-center gap-2'>
                        <label className='text-sm font-medium text-gray-900'>PGL deduction amount</label>
                        <HelpTooltip content="Postgraduate Loan (PGL) deduction amount from your payslip. This is the total amount deducted for postgraduate loan repayments during the tax year." position="right" maxWidth="400px" />
                      </div>
                      <input
                        type='text'
                        value={formatAmountDisplay(currentEmp.studentLoanDeductions.pglDeductionAmount)}
                        onChange={(e) =>
                          setEmployerDetailsList(prev => prev.map((emp, i) =>
                            i === empIdx ? { ...emp, studentLoanDeductions: { ...emp.studentLoanDeductions, pglDeductionAmount: stripCommas(e.target.value) } } : emp
                          ))
                        }
                        onBlur={(e) => {
                          const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                          if (formatted) setEmployerDetailsList(prev => prev.map((emp, i) =>
                            i === empIdx ? { ...emp, studentLoanDeductions: { ...emp.studentLoanDeductions, pglDeductionAmount: stripCommas(formatted) } } : emp
                          ))
                        }}
                        className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Benefits in Kind — collapsed by default */}
                <CollapsibleSection title="Benefits in Kind">
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    {benefitsInKindFields.map(field => (
                      <div key={field.key}>
                        <div className='mb-2 flex items-center gap-2'>
                          <label className='text-sm font-medium text-gray-900'>{field.label}</label>
                          <HelpTooltip content={`Enter the value of ${field.label.toLowerCase()} benefits received from your employer during the tax year. This includes any non-cash benefits that are taxable.`} position="right" maxWidth="400px" />
                        </div>
                        <input
                          type='text'
                          value={formatAmountDisplay(currentEmp.benefitsInKind[field.key] || '')}
                          onChange={(e) =>
                            setEmployerDetailsList(prev => prev.map((emp, i) =>
                              i === empIdx ? { ...emp, benefitsInKind: { ...emp.benefitsInKind, [field.key]: stripCommas(e.target.value) } } : emp
                            ))
                          }
                          onBlur={(e) => {
                            const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                            if (formatted) setEmployerDetailsList(prev => prev.map((emp, i) =>
                              i === empIdx ? { ...emp, benefitsInKind: { ...emp.benefitsInKind, [field.key]: stripCommas(formatted) } } : emp
                            ))
                          }}
                          className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                        />
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>

                {/* Off-payroll worker */}
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <label className='text-sm font-medium text-gray-900'>
                      Off-payroll worker
                    </label>
                    <HelpTooltip content="An off-payroll worker is someone who provides services through an intermediary (like a personal service company) rather than being directly employed. Select 'Yes' if this applies to you." position="right" maxWidth="400px" />
                  </div>
                  <div className='flex items-center gap-6'>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name={`offPayrollWorker_${empIdx}`}
                        value='yes'
                        checked={currentEmp.offPayrollWorker === 'yes'}
                        onChange={() => updateEmployer('offPayrollWorker', 'yes')}
                        className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                      />
                      <span className='text-sm font-medium text-gray-700'>Yes</span>
                    </label>
                    <label className='flex items-center gap-2'>
                      <input
                        type='radio'
                        name={`offPayrollWorker_${empIdx}`}
                        value='no'
                        checked={currentEmp.offPayrollWorker === 'no'}
                        onChange={() => updateEmployer('offPayrollWorker', 'no')}
                        className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                      />
                      <span className='text-sm font-medium text-gray-700'>No</span>
                    </label>
                  </div>
                </div>
                    </div>
                  )
                })()}
              </div>
              </CollapsibleSection>
            )}

            {/* Self Employment Section - Show if wasSelfEmployed === 'yes' */}
            {tailorReturn.wasSelfEmployed === 'yes' && (
              <CollapsibleSection title="Self-Employment Income">
                <div className='space-y-6 pt-4'>
                  {/* Reporting Type Toggle — only MTD requires quarterly submissions.
                      SA Online is a one-time annual submission, so this is hidden there. */}
                  {!isSaFlow && (
                  <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                    <label className='mb-3 block text-sm font-semibold text-gray-900'>Submission Type</label>
                    <p className='text-sm text-gray-600 mb-3'>
                      Under Making Tax Digital, you must submit quarterly period summaries of your income and expenses, plus an annual submission with adjustments, allowances, and non-financial information.
                    </p>
                    <div className='flex gap-3'>
                      <button
                        type='button'
                        onClick={() => setSelfEmploymentReportingType('quarterly')}
                        className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                          selfEmploymentReportingType === 'quarterly'
                            ? 'bg-[#001B38] text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Quarterly Periods
                      </button>
                      <button
                        type='button'
                        onClick={() => setSelfEmploymentReportingType('annual')}
                        className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                          selfEmploymentReportingType === 'annual'
                            ? 'bg-[#001B38] text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Annual Submission
                      </button>
                    </div>
                  </div>
                  )}

                  {/* Quarterly Period Submission — MTD only */}
                  {!isSaFlow && selfEmploymentReportingType === 'quarterly' && (
                    <div className='space-y-4'>
                      <div className='flex items-center justify-between'>
                        <div>
                          <h4 className='text-base font-semibold text-gray-900'>Quarterly Period Summaries</h4>
                          <p className='text-sm text-gray-600 mt-1'>
                            Add your quarterly income and expense summaries for each reporting period.
                          </p>
                        </div>
                        <button
                          type='button'
                          onClick={() => {
                            const newPeriod: SelfEmploymentPeriodData = {
                              periodId: `se-period-${Date.now()}`,
                              fromDate: '',
                              toDate: '',
                              turnover: '',
                              otherIncome: '',
                              costOfGoods: '',
                              staffCosts: '',
                              travelCosts: '',
                              premisesCosts: '',
                            }
                            setSelfEmploymentPeriods([...selfEmploymentPeriods, newPeriod])
                          }}
                          className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors'
                        >
                          <span>+ Add Period</span>
                        </button>
                      </div>

                      {selfEmploymentPeriods.length === 0 && (
                        <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800'>
                          {validationErrors.selfEmployment || 'Please add at least one quarterly period to continue.'}
                        </div>
                      )}

                      {selfEmploymentPeriods.map((period, index) => (
                        <div key={period.periodId} className='space-y-6 rounded-xl border border-gray-200 bg-gray-50 p-6'>
                          <div className='flex items-center justify-between'>
                            <h4 className='text-base font-semibold text-gray-900'>Quarter {index + 1}</h4>
                            <button
                              type='button'
                              onClick={() => setSelfEmploymentPeriods(selfEmploymentPeriods.filter(p => p.periodId !== period.periodId))}
                              className='flex items-center gap-1 text-sm text-red-500 hover:text-red-600 transition'
                            >
                              <Trash2 className='h-4 w-4' />
                              <span className='font-medium'>Remove</span>
                            </button>
                          </div>

                          {/* Period Dates */}
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <div>
                              <label className='text-sm font-medium text-gray-900'>Period Start Date</label>
                              <HelpTooltip content="The start date of this quarterly reporting period (e.g. 2024-04-06 for Q1)." position="right" maxWidth="400px" />
                              <input
                                type='date'
                                value={period.fromDate}
                                onChange={(e) => {
                                  const updated = selfEmploymentPeriods.map(p =>
                                    p.periodId === period.periodId ? { ...p, fromDate: e.target.value } : p
                                  )
                                  setSelfEmploymentPeriods(updated)
                                }}
                                className='mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                              />
                            </div>
                            <div>
                              <label className='text-sm font-medium text-gray-900'>Period End Date</label>
                              <HelpTooltip content="The end date of this quarterly reporting period (e.g. 2024-07-05 for Q1)." position="right" maxWidth="400px" />
                              <input
                                type='date'
                                value={period.toDate}
                                onChange={(e) => {
                                  const updated = selfEmploymentPeriods.map(p =>
                                    p.periodId === period.periodId ? { ...p, toDate: e.target.value } : p
                                  )
                                  setSelfEmploymentPeriods(updated)
                                }}
                                className='mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                              />
                            </div>
                          </div>

                          {/* Income */}
                          <div>
                            <h5 className='mb-3 text-sm font-semibold text-gray-900'>Income</h5>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                              {([['turnover', 'Turnover'], ['otherIncome', 'Other Income']] as const).map(([field, label]) => (
                                <div key={field}>
                                  <label className='mb-2 block text-sm font-medium text-gray-900'>{label}</label>
                                  <input
                                    type='text'
                                    value={formatAmountDisplay(period[field])}
                                    onChange={(e) => {
                                      const updated = selfEmploymentPeriods.map(p =>
                                        p.periodId === period.periodId ? { ...p, [field]: stripCommas(e.target.value) } : p
                                      )
                                      setSelfEmploymentPeriods(updated)
                                    }}
                                    onBlur={(e) => {
                                      const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                                      if (formatted) {
                                        const updated = selfEmploymentPeriods.map(p =>
                                          p.periodId === period.periodId ? { ...p, [field]: stripCommas(formatted) } : p
                                        )
                                        setSelfEmploymentPeriods(updated)
                                      }
                                    }}
                                    placeholder='0.00'
                                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Expenses */}
                          <div>
                            <h5 className='mb-3 text-sm font-semibold text-gray-900'>Expenses</h5>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                              {([['costOfGoods', 'Cost of Goods'], ['staffCosts', 'Staff Costs'], ['travelCosts', 'Travel Costs'], ['premisesCosts', 'Premises Costs']] as const).map(([field, label]) => (
                                <div key={field}>
                                  <label className='mb-2 block text-sm font-medium text-gray-900'>{label}</label>
                                  <input
                                    type='text'
                                    value={formatAmountDisplay(period[field])}
                                    onChange={(e) => {
                                      const updated = selfEmploymentPeriods.map(p =>
                                        p.periodId === period.periodId ? { ...p, [field]: stripCommas(e.target.value) } : p
                                      )
                                      setSelfEmploymentPeriods(updated)
                                    }}
                                    onBlur={(e) => {
                                      const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                                      if (formatted) {
                                        const updated = selfEmploymentPeriods.map(p =>
                                          p.periodId === period.periodId ? { ...p, [field]: stripCommas(formatted) } : p
                                        )
                                        setSelfEmploymentPeriods(updated)
                                      }
                                    }}
                                    placeholder='0.00'
                                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Annual Submission Section — always shown for SA Online (annual only) */}
                  {(isSaFlow || selfEmploymentReportingType === 'annual') && (
                  <div className='space-y-6 rounded-xl border border-gray-200 bg-gray-50 p-6'>
                    {/* Income & expenses — SA Online captures these once for the whole year */}
                    {isSaFlow && (
                      <>
                        {/* Accounting Period */}
                        <div>
                          <h5 className='mb-3 text-sm font-semibold text-gray-900'>Accounting Period</h5>
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <div>
                              <label className='mb-2 block text-sm font-medium text-gray-900'>Start Date</label>
                              <input
                                type='date'
                                value={selfEmploymentPeriods[0]?.fromDate || ''}
                                onChange={(e) => updateAnnualSePeriod('fromDate', e.target.value)}
                                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                              />
                            </div>
                            <div>
                              <label className='mb-2 block text-sm font-medium text-gray-900'>End Date</label>
                              <input
                                type='date'
                                value={selfEmploymentPeriods[0]?.toDate || ''}
                                onChange={(e) => updateAnnualSePeriod('toDate', e.target.value)}
                                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                              />
                            </div>
                          </div>
                        </div>

                        {/* Business Income */}
                        <div>
                          <h5 className='mb-3 text-sm font-semibold text-gray-900'>Business Income</h5>
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            {([
                              ['turnover', 'Turnover', 'Total income from your business before expenses.'],
                              ['otherIncome', 'Other Income', 'Other income from your business — include grants, insurance receipts or any other business income not included in turnover.'],
                            ] as const).map(([field, label, help]) => (
                              <div key={field}>
                                <div className='mb-2 flex items-center gap-2'>
                                  <label className='text-sm font-medium text-gray-900'>{label}</label>
                                  <HelpTooltip content={help} position="right" maxWidth="400px" />
                                </div>
                                <input
                                  type='text'
                                  value={formatAmountDisplay(selfEmploymentPeriods[0]?.[field] || '')}
                                  onChange={(e) => updateAnnualSePeriod(field, stripCommas(e.target.value))}
                                  onBlur={(e) => {
                                    const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                                    if (formatted) updateAnnualSePeriod(field, stripCommas(formatted))
                                  }}
                                  placeholder='0.00'
                                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Business Expenses */}
                        <div>
                          <h5 className='mb-3 text-sm font-semibold text-gray-900'>Business Expenses</h5>
                          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            {([
                              ['costOfGoods', 'Cost of Goods Sold', 'The cost of goods you bought for resale or used in providing your services.'],
                              ['staffCosts', 'Staff Costs', 'Wages, salaries and other staff costs paid to employees.'],
                              ['travelCosts', 'Travel Costs', 'Car, van and travel expenses incurred for business purposes.'],
                              ['premisesCosts', 'Premises Costs', 'Rent, rates, power and insurance costs for your business premises.'],
                            ] as const).map(([field, label, help]) => (
                              <div key={field}>
                                <div className='mb-2 flex items-center gap-2'>
                                  <label className='text-sm font-medium text-gray-900'>{label}</label>
                                  <HelpTooltip content={help} position="right" maxWidth="400px" />
                                </div>
                                <input
                                  type='text'
                                  value={formatAmountDisplay(selfEmploymentPeriods[0]?.[field] || '')}
                                  onChange={(e) => updateAnnualSePeriod(field, stripCommas(e.target.value))}
                                  onBlur={(e) => {
                                    const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                                    if (formatted) updateAnnualSePeriod(field, stripCommas(formatted))
                                  }}
                                  placeholder='0.00'
                                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Estimated Business Profit — computed */}
                        {(() => {
                          const p = selfEmploymentPeriods[0]
                          const num = (v?: string) => parseFloat(stripCommas(v || '')) || 0
                          const profit = p
                            ? (num(p.turnover) + num(p.otherIncome)) - (num(p.costOfGoods) + num(p.staffCosts) + num(p.travelCosts) + num(p.premisesCosts))
                            : 0
                          return (
                            <div className='flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4'>
                              <div>
                                <p className='text-sm font-semibold text-gray-900'>Estimated Business Profit</p>
                                <p className='text-xs text-gray-500'>Automatically calculated from total turnover plus other income minus total expenses.</p>
                              </div>
                              <p className='shrink-0 text-lg font-bold text-gray-900'>£{profit.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                          )
                        })()}
                      </>
                    )}

                    {/* Business Adjustments Section */}
                    <div>
                      <h5 className='mb-1 text-sm font-semibold text-gray-900'>Business Adjustments</h5>
                      <p className='mb-3 text-sm text-gray-600'>Only complete this section if any of these adjustments apply to your business.</p>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Included Non-Taxable Profits</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.adjustments.includedNonTaxableProfits)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              adjustments: {
                                ...selfEmploymentAnnualData.adjustments,
                                includedNonTaxableProfits: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, adjustments: { ...prev.adjustments, includedNonTaxableProfits: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Basis Adjustment</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.adjustments.basisAdjustment)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              adjustments: {
                                ...selfEmploymentAnnualData.adjustments,
                                basisAdjustment: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, adjustments: { ...prev.adjustments, basisAdjustment: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Overlap Relief Used</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.adjustments.overlapReliefUsed)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              adjustments: {
                                ...selfEmploymentAnnualData.adjustments,
                                overlapReliefUsed: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, adjustments: { ...prev.adjustments, overlapReliefUsed: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Accounting Adjustment</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.adjustments.accountingAdjustment)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              adjustments: {
                                ...selfEmploymentAnnualData.adjustments,
                                accountingAdjustment: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, adjustments: { ...prev.adjustments, accountingAdjustment: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Averaging Adjustment</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.adjustments.averagingAdjustment)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              adjustments: {
                                ...selfEmploymentAnnualData.adjustments,
                                averagingAdjustment: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, adjustments: { ...prev.adjustments, averagingAdjustment: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                      </div>
                    </div>

                    {/* Capital Allowances Section */}
                    <div>
                      <h5 className='mb-1 text-sm font-semibold text-gray-900'>Capital Allowances</h5>
                      <p className='mb-3 text-sm text-gray-600'>Claim capital allowances for qualifying business assets.</p>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Annual Investment Allowance</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.allowances.annualInvestmentAllowance)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              allowances: {
                                ...selfEmploymentAnnualData.allowances,
                                annualInvestmentAllowance: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, allowances: { ...prev.allowances, annualInvestmentAllowance: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Other Capital Allowance</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.allowances.otherCapitalAllowance)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              allowances: {
                                ...selfEmploymentAnnualData.allowances,
                                otherCapitalAllowance: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, allowances: { ...prev.allowances, otherCapitalAllowance: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Trading Income Allowance</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.allowances.tradingIncomeAllowance)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              allowances: {
                                ...selfEmploymentAnnualData.allowances,
                                tradingIncomeAllowance: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, allowances: { ...prev.allowances, tradingIncomeAllowance: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Capital Allowance Main Pool</label>
                          <input
                            type='text'
                            value={formatAmountDisplay(selfEmploymentAnnualData.allowances.capitalAllowanceMainPool)}
                            onChange={(e) => setSelfEmploymentAnnualData({
                              ...selfEmploymentAnnualData,
                              allowances: {
                                ...selfEmploymentAnnualData.allowances,
                                capitalAllowanceMainPool: stripCommas(e.target.value)
                              }
                            })}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, allowances: { ...prev.allowances, capitalAllowanceMainPool: stripCommas(formatted) } }))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        {([
                          ['capitalAllowanceSpecialRatePool', 'Capital Allowance Special Rate Pool'],
                          ['businessPremisesRenovationAllowance', 'Business Premises Renovation Allowance'],
                          ['zeroEmissionGoodsVehicleAllowance', 'Zero-Emission Goods Vehicle Allowance'],
                          ['enhancedCapitalAllowance', 'Enhanced Capital Allowance'],
                          ['allowanceOnSales', 'Allowance on Sales'],
                          ['costOfReplacingDomesticGoods', 'Cost of Replacing Domestic Items'],
                        ] as const).map(([field, label]) => (
                          <div key={field}>
                            <label className='mb-2 block text-sm font-medium text-gray-900'>{label}</label>
                            <input
                              type='text'
                              value={formatAmountDisplay(selfEmploymentAnnualData.allowances[field])}
                              onChange={(e) => setSelfEmploymentAnnualData({
                                ...selfEmploymentAnnualData,
                                allowances: { ...selfEmploymentAnnualData.allowances, [field]: stripCommas(e.target.value) }
                              })}
                              onBlur={(e) => {
                                const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                                if (formatted) setSelfEmploymentAnnualData(prev => ({ ...prev, allowances: { ...prev.allowances, [field]: stripCommas(formatted) } }))
                              }}
                              placeholder='0.00'
                              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Business Information Section */}
                    <div>
                      <h5 className='mb-3 text-sm font-semibold text-gray-900'>Business Information</h5>
                      <div className='space-y-4'>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Are you exempt from paying Class 4 National Insurance contributions?</label>
                          <div className='flex items-center gap-6'>
                            <label className='flex items-center gap-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='class4NicExempt'
                                value='yes'
                                checked={selfEmploymentAnnualData.nonFinancials.class4NicExempt === 'yes'}
                                onChange={(e) => setSelfEmploymentAnnualData({
                                  ...selfEmploymentAnnualData,
                                  nonFinancials: {
                                    ...selfEmploymentAnnualData.nonFinancials,
                                    class4NicExempt: e.target.value as 'yes' | 'no'
                                  }
                                })}
                                className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                              />
                              <span className='text-sm font-medium text-gray-700'>Yes</span>
                            </label>
                            <label className='flex items-center gap-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='class4NicExempt'
                                value='no'
                                checked={selfEmploymentAnnualData.nonFinancials.class4NicExempt === 'no'}
                                onChange={(e) => setSelfEmploymentAnnualData({
                                  ...selfEmploymentAnnualData,
                                  nonFinancials: {
                                    ...selfEmploymentAnnualData.nonFinancials,
                                    class4NicExempt: e.target.value as 'yes' | 'no'
                                  }
                                })}
                                className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                              />
                              <span className='text-sm font-medium text-gray-700'>No</span>
                            </label>
                          </div>
                        </div>
                        {selfEmploymentAnnualData.nonFinancials.class4NicExempt === 'yes' && (
                          <div>
                            <label className='mb-2 block text-sm font-medium text-gray-900'>Exemption Code</label>
                            <input
                              type='text'
                              value={selfEmploymentAnnualData.nonFinancials.exemptionCode}
                              onChange={(e) => setSelfEmploymentAnnualData({
                                ...selfEmploymentAnnualData,
                                nonFinancials: {
                                  ...selfEmploymentAnnualData.nonFinancials,
                                  exemptionCode: e.target.value
                                }
                              })}
                              placeholder='Enter exemption code'
                              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                            />
                          </div>
                        )}
                        <div>
                          <label className='mb-2 block text-sm font-medium text-gray-900'>Were you also employed under PAYE during the tax year?</label>
                          <div className='flex items-center gap-6'>
                            <label className='flex items-center gap-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='payeEmployed'
                                value='yes'
                                checked={selfEmploymentAnnualData.nonFinancials.payeEmployed === 'yes'}
                                onChange={(e) => setSelfEmploymentAnnualData({
                                  ...selfEmploymentAnnualData,
                                  nonFinancials: {
                                    ...selfEmploymentAnnualData.nonFinancials,
                                    payeEmployed: e.target.value as 'yes' | 'no'
                                  }
                                })}
                                className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                              />
                              <span className='text-sm font-medium text-gray-700'>Yes</span>
                            </label>
                            <label className='flex items-center gap-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='payeEmployed'
                                value='no'
                                checked={selfEmploymentAnnualData.nonFinancials.payeEmployed === 'no'}
                                onChange={(e) => setSelfEmploymentAnnualData({
                                  ...selfEmploymentAnnualData,
                                  nonFinancials: {
                                    ...selfEmploymentAnnualData.nonFinancials,
                                    payeEmployed: e.target.value as 'yes' | 'no'
                                  }
                                })}
                                className='h-4 w-4 border-gray-300 text-gray-900 focus:ring-[#001B38]'
                              />
                              <span className='text-sm font-medium text-gray-700'>No</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </CollapsibleSection>
            )}

            {/* UK Property Section - Show if hasProperty === 'yes' */}
            {tailorReturn.hasProperty === 'yes' && (
              <CollapsibleSection title="UK Property Income">
                <div className='space-y-6 pt-4'>
                  <p className='text-sm text-gray-700'>
                    You have indicated that you received UK property income. Please add your property periods below.
                  </p>
                  <button
                    onClick={() => {
                      const newPeriod: PropertyPeriod = {
                        periodId: `period-${Date.now()}`,
                        fromDate: '',
                        toDate: '',
                        rentIncome: '',
                        premiums: '',
                        premisesRunningCosts: '',
                        repairsMaintenance: '',
                        legalManagementCosts: ''
                      }
                      setPropertyPeriods([...propertyPeriods, newPeriod])
                      if (validationErrors.property) {
                        setValidationErrors(prev => {
                          const next = { ...prev }
                          delete next.property
                          return next
                        })
                      }
                    }}
                    className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-semibold text-white hover:bg-[#002a50] transition'
                  >
                    <span>+ Add Period</span>
                  </button>

                {propertyPeriods.length === 0 && (
                  <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
                    <p className='text-sm text-amber-800'>
                      {validationErrors.property || 'Please add at least one property period to continue.'}
                    </p>
                  </div>
                )}

                {propertyPeriods.map((period, index) => (
                  <div key={period.periodId} className='space-y-6 rounded-xl border border-gray-200 bg-gray-50 p-6'>
                    <div className='flex items-center justify-between border-b border-gray-200 pb-4'>
                      <h4 className='text-base font-semibold text-gray-900'>Period {index + 1}</h4>
                      <button
                        onClick={() => {
                          setPropertyPeriods(propertyPeriods.filter(p => p.periodId !== period.periodId))
                        }}
                        className='flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition'
                      >
                        <span className='font-medium'>Delete This Period</span>
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </div>

                    {/* Period Dates */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                      <div>
                        <div className='mb-2 flex items-center gap-2'>
                          <label className='text-sm font-medium text-gray-900'>Period Start Date</label>
                          <HelpTooltip content="The start date of this property income period. This should match the start date of your property income reporting period." position="right" maxWidth="400px" />
                        </div>
                        <input
                          type='date'
                          value={period.fromDate}
                          onChange={(e) => {
                            const updated = propertyPeriods.map(p =>
                              p.periodId === period.periodId ? { ...p, fromDate: e.target.value } : p
                            )
                            setPropertyPeriods(updated)
                          }}
                          className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                        />
                      </div>
                      <div>
                        <div className='mb-2 flex items-center gap-2'>
                          <label className='text-sm font-medium text-gray-900'>Period End Date</label>
                          <HelpTooltip content="The end date of this property income period. This should match the end date of your property income reporting period." position="right" maxWidth="400px" />
                        </div>
                        <input
                          type='date'
                          value={period.toDate}
                          onChange={(e) => {
                            const updated = propertyPeriods.map(p =>
                              p.periodId === period.periodId ? { ...p, toDate: e.target.value } : p
                            )
                            setPropertyPeriods(updated)
                          }}
                          className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                        />
                      </div>
                    </div>

                    {/* Income Section */}
                    <div>
                      <h5 className='mb-3 text-sm font-semibold text-gray-900'>Income</h5>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <div className='mb-2 flex items-center gap-2'>
                            <label className='text-sm font-medium text-gray-900'>Rent Income</label>
                            <HelpTooltip content={getHelpText('rentIncome')} position="right" maxWidth="400px" />
                          </div>
                          <input
                            type='text'
                            value={formatAmountDisplay(period.rentIncome)}
                            onChange={(e) => {
                              const updated = propertyPeriods.map(p =>
                                p.periodId === period.periodId ? { ...p, rentIncome: stripCommas(e.target.value) } : p
                              )
                              setPropertyPeriods(updated)
                            }}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setPropertyPeriods(prev => prev.map(p =>
                                p.periodId === period.periodId ? { ...p, rentIncome: stripCommas(formatted) } : p
                              ))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <div className='mb-2 flex items-center gap-2'>
                            <label className='text-sm font-medium text-gray-900'>Premiums of Lease Grant</label>
                            <HelpTooltip content={getHelpText('premiums')} position="right" maxWidth="400px" />
                          </div>
                          <input
                            type='text'
                            value={formatAmountDisplay(period.premiums)}
                            onChange={(e) => {
                              const updated = propertyPeriods.map(p =>
                                p.periodId === period.periodId ? { ...p, premiums: stripCommas(e.target.value) } : p
                              )
                              setPropertyPeriods(updated)
                            }}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setPropertyPeriods(prev => prev.map(p =>
                                p.periodId === period.periodId ? { ...p, premiums: stripCommas(formatted) } : p
                              ))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                      </div>
                    </div>

                    {/* Expenses Section */}
                    <div>
                      <h5 className='mb-3 text-sm font-semibold text-gray-900'>Expenses</h5>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                          <div className='mb-2 flex items-center gap-2'>
                            <label className='text-sm font-medium text-gray-900'>Premises Running Costs</label>
                            <HelpTooltip content={getHelpText('premisesRunningCosts')} position="right" maxWidth="400px" />
                          </div>
                          <input
                            type='text'
                            value={formatAmountDisplay(period.premisesRunningCosts)}
                            onChange={(e) => {
                              const updated = propertyPeriods.map(p =>
                                p.periodId === period.periodId ? { ...p, premisesRunningCosts: stripCommas(e.target.value) } : p
                              )
                              setPropertyPeriods(updated)
                            }}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setPropertyPeriods(prev => prev.map(p =>
                                p.periodId === period.periodId ? { ...p, premisesRunningCosts: stripCommas(formatted) } : p
                              ))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <div className='mb-2 flex items-center gap-2'>
                            <label className='text-sm font-medium text-gray-900'>Repairs and Maintenance</label>
                            <HelpTooltip content={getHelpText('repairsMaintenance')} position="right" maxWidth="400px" />
                          </div>
                          <input
                            type='text'
                            value={formatAmountDisplay(period.repairsMaintenance)}
                            onChange={(e) => {
                              const updated = propertyPeriods.map(p =>
                                p.periodId === period.periodId ? { ...p, repairsMaintenance: stripCommas(e.target.value) } : p
                              )
                              setPropertyPeriods(updated)
                            }}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setPropertyPeriods(prev => prev.map(p =>
                                p.periodId === period.periodId ? { ...p, repairsMaintenance: stripCommas(formatted) } : p
                              ))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                        <div>
                          <div className='mb-2 flex items-center gap-2'>
                            <label className='text-sm font-medium text-gray-900'>Legal and Management Costs</label>
                            <HelpTooltip content={getHelpText('legalManagementCosts')} position="right" maxWidth="400px" />
                          </div>
                          <input
                            type='text'
                            value={formatAmountDisplay(period.legalManagementCosts)}
                            onChange={(e) => {
                              const updated = propertyPeriods.map(p =>
                                p.periodId === period.periodId ? { ...p, legalManagementCosts: stripCommas(e.target.value) } : p
                              )
                              setPropertyPeriods(updated)
                            }}
                            onBlur={(e) => {
                              const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                              if (formatted) setPropertyPeriods(prev => prev.map(p =>
                                p.periodId === period.periodId ? { ...p, legalManagementCosts: stripCommas(formatted) } : p
                              ))
                            }}
                            placeholder='0.00'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Dividends Section - Show if hasDividends === 'yes' */}
            {tailorReturn.hasDividends === 'yes' && (
              <CollapsibleSection title="Dividends Income">
                <div className='space-y-6 pt-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700'>UK Dividends</label>
                    <input
                      type='text'
                      value={formatAmountDisplay(dividendsIncome.ukDividends)}
                      onChange={(e) => setDividendsIncome({ ...dividendsIncome, ukDividends: stripCommas(e.target.value) })}
                      onBlur={(e) => {
                        const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                        if (formatted) setDividendsIncome(prev => ({ ...prev, ukDividends: stripCommas(formatted) }))
                      }}
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                    />
                  </div>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700'>Other UK Dividends</label>
                    <input
                      type='text'
                      value={formatAmountDisplay(dividendsIncome.otherUkDividends)}
                      onChange={(e) => setDividendsIncome({ ...dividendsIncome, otherUkDividends: stripCommas(e.target.value) })}
                      onBlur={(e) => {
                        const formatted = formatAmountOnBlur(stripCommas(e.target.value))
                        if (formatted) setDividendsIncome(prev => ({ ...prev, otherUkDividends: stripCommas(formatted) }))
                      }}
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-[#001B38] focus:outline-none focus:ring-1 focus:ring-[#001B38]'
                    />
                  </div>
                </div>
              </div>
              </CollapsibleSection>
            )}

            </div>
          </div>
        )

      case 4:
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Your Tax Calculation</h2>
            </div>

            {isLoading ? (
              <div className='flex items-center justify-center py-12'>
                <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
                <p className='ml-3 text-sm text-gray-600'>Calculating your tax...</p>
              </div>
            ) : calculationResults ? (
              <div className='space-y-6'>
                {/* Summary Cards */}
                <div className='grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4'>
                  <div className='min-w-0 rounded-lg border border-gray-200 bg-white p-4'>
                    <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>Total Income</p>
                    <p className='mt-2 text-xl font-bold text-gray-900 break-words tabular-nums'>
                      £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.totalIncomeReceivedFromAllSources || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  <div className='min-w-0 rounded-lg border border-gray-200 bg-white p-4'>
                    <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>Taxable Income</p>
                    <p className='mt-2 text-xl font-bold text-gray-900 break-words tabular-nums'>
                      £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.totalTaxableIncome || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  <div className='min-w-0 rounded-lg border border-gray-200 bg-white p-4'>
                    <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>Income Tax</p>
                    <p className='mt-2 text-xl font-bold text-orange-600 break-words tabular-nums'>
                      £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.incomeTaxCharged || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  <div className='min-w-0 rounded-lg border border-red-200 bg-red-50 p-4'>
                    <p className='text-xs font-medium text-red-700 uppercase tracking-wide'>Total Tax Due</p>
                    <p className='mt-2 text-xl font-bold text-red-600 break-words tabular-nums'>
                      £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                {/* Full SA tax breakdown (all sections from the calculate-tax endpoint) */}
                {isSaFlow && renderSaTaxBreakdown()}

                {/* Calculation Metadata */}
                {(calculationResults.response as any)?.metadata && (
                  <div className='rounded-lg border border-gray-200 bg-white p-6'>
                    <h3 className='text-base font-semibold text-gray-900 mb-4'>Calculation Information</h3>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm'>
                      <div>
                        <span className='font-medium text-gray-700'>Calculation ID:</span>
                        <span className='ml-2 text-gray-600 font-mono text-xs'>{(calculationResults.response as any).metadata.calculationId}</span>
                </div>
                      <div>
                        <span className='font-medium text-gray-700'>Tax Year:</span>
                        <span className='ml-2 text-gray-600'>{(calculationResults.response as any).metadata.taxYear}</span>
                      </div>
                      <div>
                        <span className='font-medium text-gray-700'>Calculation Type:</span>
                        <span className='ml-2 text-gray-600'>{(calculationResults.response as any).metadata.calculationType}</span>
                      </div>
                      <div>
                        <span className='font-medium text-gray-700'>Final Declaration:</span>
                        <span className='ml-2 text-gray-600'>{(calculationResults.response as any).metadata.finalDeclaration ? 'Yes' : 'No'}</span>
                      </div>
                      {(calculationResults.response as any).metadata.calculationTimestamp && (
                        <div className='col-span-2'>
                          <span className='font-medium text-gray-700'>Calculated:</span>
                          <span className='ml-2 text-gray-600'>{new Date((calculationResults.response as any).metadata.calculationTimestamp).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Income Tax Breakdown */}
                <div className='rounded-lg border border-gray-200 bg-white p-6'>
                  <h3 className='text-base font-semibold text-gray-900 mb-4'>Income Tax Calculation</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between py-2 border-b border-gray-100'>
                      <span className='text-sm text-gray-700'>Total Income Received</span>
                      <span className='text-sm font-semibold text-gray-900'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.totalIncomeReceivedFromAllSources || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className='flex justify-between py-2 border-b border-gray-100'>
                      <span className='text-sm text-gray-700'>Allowances & Deductions</span>
                      <span className='text-sm font-semibold text-green-600'>
                        -£{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.totalAllowancesAndDeductions || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className='flex justify-between py-2 border-b border-gray-100'>
                      <span className='text-sm font-medium text-gray-900'>Taxable Income</span>
                      <span className='text-sm font-bold text-gray-900'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.totalTaxableIncome || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className='flex justify-between py-2 border-b border-gray-100'>
                      <span className='text-sm text-gray-700'>Income Tax Charged</span>
                      <span className='text-sm font-semibold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.incomeTax?.incomeTaxCharged || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {(calculationResults.response as any)?.calculation?.taxCalculation?.incomeTax?.totalReliefs && (calculationResults.response as any).calculation.taxCalculation.incomeTax.totalReliefs > 0 && (
                      <div className='flex justify-between py-2 border-b border-gray-100'>
                        <span className='text-sm text-gray-700'>Total Reliefs</span>
                        <span className='text-sm font-semibold text-green-600'>
                          -£{((calculationResults.response as any).calculation.taxCalculation.incomeTax.totalReliefs).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    <div className='flex justify-between py-3 bg-orange-50 px-3 rounded'>
                      <span className='text-sm font-bold text-gray-900'>Total Income Tax Due</span>
                      <span className='text-sm font-bold text-orange-600'>
                        £{((calculationResults.response as any)?.calculation?.taxCalculation?.incomeTax?.totalIncomeTaxDue || calculationResults.response?.calculation?.taxCalculation?.incomeTax?.incomeTaxCharged || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* National Insurance Breakdown.
                    The SA/MTD service type exposes this as `taxCalculation.nationalInsurance`;
                    raw HMRC payloads use `taxCalculation.nics`. Accept either. */}
                {(() => {
                  const tc = (calculationResults.response as any)?.calculation?.taxCalculation
                  const ni = tc?.nationalInsurance || tc?.nics
                  if (!ni) return null

                  const class2Amount = ni.class2Nics?.amount ?? 0
                  const class4Amount = ni.class4Nics?.totalAmount ?? ni.class4Nics?.amount ?? 0
                  const totalNic = ni.totalNic ?? class2Amount + class4Amount
                  const money = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

                  return (
                    <div className='rounded-lg border border-gray-200 bg-white p-6'>
                      <h3 className='text-base font-semibold text-gray-900 mb-4'>National Insurance Contributions</h3>
                      <div className='space-y-2'>
                        {ni.class2Nics && (
                          <div className='flex justify-between py-2 border-b border-gray-100'>
                            <div>
                              <span className='text-sm text-gray-700'>Class 2 NICs</span>
                              {ni.class2Nics.weeklyRate && (
                                <span className='ml-2 text-xs text-gray-500'>
                                  (£{ni.class2Nics.weeklyRate.toFixed(2)}/week)
                                </span>
                              )}
                            </div>
                            <span className='text-sm font-semibold text-red-600'>£{money(class2Amount)}</span>
                          </div>
                        )}
                        {ni.class4Nics && (
                          <div className='flex justify-between py-2 border-b border-gray-100'>
                            <span className='text-sm text-gray-700'>Class 4 NICs</span>
                            <span className='text-sm font-semibold text-red-600'>£{money(class4Amount)}</span>
                          </div>
                        )}
                        <div className='flex justify-between py-3 bg-red-50 px-3 rounded'>
                          <span className='text-sm font-bold text-gray-900'>Total NICs</span>
                          <span className='text-sm font-bold text-red-600'>£{money(totalNic)}</span>
                        </div>
                      </div>
                    </div>
                  )
                })()}

                {/* Total Summary */}
                <div className='rounded-lg border-2 border-red-300 bg-red-50 p-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-lg font-bold text-gray-900'>Total Tax and NICs Due</h3>
                      <p className='text-sm text-gray-600 mt-1'>Amount payable to HMRC</p>
                    </div>
                    <div className='text-right'>
                      <p className='text-2xl sm:text-3xl font-bold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages - Errors, Warnings, Info */}
                {(calculationResults.response as any)?.messages && (
                  <div className='space-y-3'>
                    {(calculationResults.response as any).messages.errors && (calculationResults.response as any).messages.errors.length > 0 && (
                      <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
                        <div className='flex items-start gap-3'>
                          <AlertCircle className='h-5 w-5 text-red-600 flex-shrink-0 mt-0.5' />
                          <div className='flex-1'>
                            <h4 className='text-sm font-semibold text-red-900 mb-2'>Errors</h4>
                            <ul className='space-y-1'>
                              {(calculationResults.response as any).messages.errors.map((err: any, idx: number) => (
                                <li key={idx} className='text-sm text-red-700'>
                                  <span className='font-mono text-xs'>[{err.id}]</span> {err.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    {(calculationResults.response as any).messages.warnings && (calculationResults.response as any).messages.warnings.length > 0 && (
                      <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
                        <div className='flex items-start gap-3'>
                          <AlertCircle className='h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5' />
                          <div className='flex-1'>
                            <h4 className='text-sm font-semibold text-amber-900 mb-2'>Warnings</h4>
                            <ul className='space-y-1'>
                              {(calculationResults.response as any).messages.warnings.map((warn: any, idx: number) => (
                                <li key={idx} className='text-sm text-amber-700'>
                                  <span className='font-mono text-xs'>[{warn.id}]</span> {warn.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    {(calculationResults.response as any).messages.info && (calculationResults.response as any).messages.info.length > 0 && (
                      <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                        <div className='flex items-start gap-3'>
                          <HelpCircle className='h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5' />
                          <div className='flex-1'>
                            <h4 className='text-sm font-semibold text-blue-900 mb-2'>Information</h4>
                            <ul className='space-y-1'>
                              {(calculationResults.response as any).messages.info.map((info: any, idx: number) => (
                                <li key={idx} className='text-sm text-blue-700'>
                                  <span className='font-mono text-xs'>[{info.id}]</span> {info.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-8 text-center'>
                <p className='text-sm text-gray-600'>Click "Proceed" to calculate your tax.</p>
              </div>
            )}
          </div>
        )

      case 5:
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Save Your Return</h2>
              <p className='mt-1 text-sm text-gray-500'>Review what has been saved before submitting</p>
            </div>

            {/* Success banner */}
            <div className='rounded-xl border border-green-200 bg-green-50 p-5'>
              <div className='flex items-center gap-4'>
                <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-500'>
                  <Check className='h-5 w-5 text-white' />
                </div>
                <div>
                  <h3 className='text-base font-semibold text-green-900'>Your return has been saved</h3>
                  <p className='mt-0.5 text-sm text-green-700'>
                    All your information has been securely saved. You can return to complete your submission at any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Saved items summary */}
            <div className='rounded-xl border border-gray-200 bg-white p-6'>
              <h3 className='text-sm font-semibold text-gray-900 mb-4'>What has been saved</h3>
              <div className='space-y-3'>
                <div className='flex items-center gap-3'>
                  <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                  <span className='text-sm text-gray-700'>Personal details</span>
                </div>
                {tailorReturn.wasEmployee === 'yes' && (
                  <div className='flex items-center gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                    <span className='text-sm text-gray-700'>Employment income</span>
                  </div>
                )}
                {tailorReturn.wasSelfEmployed === 'yes' && (
                  <div className='flex items-center gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                    <span className='text-sm text-gray-700'>Self-employment income ({selfEmploymentReportingType === 'quarterly' ? 'quarterly periods' : 'annual submission'})</span>
                  </div>
                )}
                {tailorReturn.hasProperty === 'yes' && (
                  <div className='flex items-center gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                    <span className='text-sm text-gray-700'>UK property income</span>
                  </div>
                )}
                {tailorReturn.hasDividends === 'yes' && (
                  <div className='flex items-center gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                    <span className='text-sm text-gray-700'>Dividends income</span>
                  </div>
                )}
                <div className='flex items-center gap-3'>
                  <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                  <span className='text-sm text-gray-700'>Overpaid tax &amp; bank details</span>
                </div>
                {calculationId && (
                  <div className='flex items-center gap-3'>
                    <CheckCircle2 className='h-5 w-5 text-green-500 flex-shrink-0' />
                    <span className='text-sm text-gray-700'>Tax calculation</span>
                  </div>
                )}
              </div>
            </div>

            {/* Important notice */}
            <div className='rounded-xl border border-amber-300 bg-amber-50 px-5 py-4'>
              <p className='text-sm text-amber-800 leading-relaxed'>
                <span className='font-semibold'>Important:</span> Your return has been saved but not yet submitted to HMRC.
                Click &ldquo;Proceed&rdquo; to continue to the submission step, or &ldquo;Save &amp; Close&rdquo; to exit and return later.
              </p>
            </div>
          </div>
        )

      case 6:
        // Final step — Submit Your Return, for both flows.
        // SA collects Government Gateway credentials and submits via GovTalk;
        // Traditional MTD submits its final declaration via HMRC OAuth.
        if (isSaFlow) {
          return renderSaSubmitStep()
        }
        // Traditional MTD: Show submission receipt after successful submission
        if (!isSaFlow && currentStep === 6 && isSubmitted && submissionReceipt) {
          const submittedDate = new Date(submissionReceipt.submittedAt)
          const formattedDate = submittedDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })
          const formattedTime = submittedDate.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          })

          return (
            <div className='space-y-6'>
              <div className='text-gray-500 text-sm mb-2'>
                {submissionReceipt.taxpayerName}'s tax return: {taxYear.replace('-', ' to ')}
              </div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-8'>Submit your return</h1>

              <div className='space-y-6'>
                <h2 className='text-xl sm:text-2xl font-bold text-green-700'>Submission receipt</h2>

                <p className='text-lg text-gray-700'>
                  The {taxYear.replace('-', ' to ')} tax return for {submissionReceipt.taxpayerName} was successfully submitted and was received by HM Revenue and Customs at {formattedTime} on {formattedDate}.
                </p>

                <div className='border-l-4 border-gray-300 pl-4 py-2 bg-gray-50'>
                  <p className='text-gray-700'>
                    Your <span className='font-semibold'>submission receipt reference number</span> is:
                  </p>
                  <p className='text-lg font-mono font-semibold text-gray-900 mt-1'>
                    {submissionReceipt.referenceNumber}
                  </p>
                </div>

                <div className='rounded-lg border border-blue-200 bg-blue-50 p-6'>
                  <h3 className='text-lg font-semibold text-blue-900 mb-3'>What happens next?</h3>
                  <ul className='space-y-2 text-sm text-blue-800'>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>Your return has been received by HMRC and is being processed</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>Keep your submission receipt reference number safe for your records</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>If you have tax to pay, HMRC will send you payment instructions separately</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <CheckCircle2 className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                      <span>You can view your submission history in your dashboard</span>
                    </li>
                  </ul>
                </div>

                <div className='rounded-lg border border-gray-200 bg-white p-5'>
                  <div className='flex items-start gap-4'>
                    <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 shrink-0'>
                      <FileCheck className='h-5 w-5 text-gray-600' />
                    </div>
                    <div className='flex-1'>
                      <h3 className='text-sm font-semibold text-gray-900'>Submission Receipt</h3>
                      <p className='mt-0.5 text-xs text-gray-500'>
                        Download your Self Assessment submission receipt as a PDF document.
                      </p>
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      <button
                        onClick={handleDownloadReceipt}
                        disabled={isDownloadingReceipt}
                        className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50'
                      >
                        {isDownloadingReceipt ? (
                          <>
                            <Loader2 className='h-4 w-4 animate-spin' />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className='h-4 w-4' />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className='rounded-lg border border-amber-200 bg-amber-50 p-6 w-full'>
                  <h3 className='text-base font-semibold text-amber-900 mb-2'>Would you like to keep your draft?</h3>
                  <p className='text-sm text-amber-800 mb-4'>
                    Your return has been submitted. You can keep the draft for your records or delete it to clean up.
                  </p>
                  <div className='flex items-center gap-3 justify-center w-full'>
                    <button
                      onClick={() => {
                        toast.success('Draft kept for your records.')
                        navigate('/add-self-assessment?flow=traditional')
                      }}
                      className='flex items-center gap-2 rounded-lg bg-white border border-amber-300 px-5 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition'
                    >
                      Keep Draft
                    </button>
                    <button
                      onClick={() => {
                        toast.success('Draft deleted successfully.')
                        navigate('/add-self-assessment?flow=traditional')
                      }}
                      className='flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition'
                    >
                      Delete Draft
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Traditional MTD: Show submitted status view (loaded from history, no receipt)
        if (!isSaFlow && currentStep === 6 && isSubmitted && !submissionReceipt) {
          return (
            <div className='space-y-6'>
              <div className='rounded-xl border-2 border-blue-200 bg-blue-50 p-10 text-center'>
                <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-100'>
                  <Send className='h-10 w-10 text-blue-600' />
                </div>
                <h2 className='mt-5 text-xl sm:text-2xl font-bold text-blue-900'>Return Submitted to HMRC</h2>
                <p className='mx-auto mt-2 max-w-md text-sm text-blue-700'>
                  Your self assessment tax return for {taxYear.replace('-', ' to ')} has been submitted to HMRC.
                  HMRC typically takes some time to review and process submissions.
                </p>
                <div className='mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-100 px-5 py-2.5'>
                  <Clock className='h-4 w-4 text-blue-600' />
                  <span className='text-sm font-medium text-blue-800'>Awaiting HMRC response</span>
                </div>
                <p className='mx-auto mt-4 max-w-md text-xs text-blue-600'>
                  You can come back to check the status at any time, or you will be notified once HMRC processes your return.
                </p>
              </div>

              {calculationResults && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-6'>
                  <h3 className='text-base font-semibold text-gray-900 mb-4'>Summary</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Tax Year:</span>
                      <span className='text-sm font-semibold text-gray-900'>{taxYear.replace('-', ' to ')}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Total Tax Due:</span>
                      <span className='text-sm font-semibold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {userProfile.utr && (
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-600'>UTR:</span>
                        <span className='text-sm font-semibold text-gray-900'>{userProfile.utr}</span>
                      </div>
                    )}
                    {userProfile.nino && (
                      <div className='flex justify-between'>
                        <span className='text-sm text-gray-600'>NINO:</span>
                        <span className='text-sm font-semibold text-gray-900'>{userProfile.nino}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <div className='flex items-start gap-3'>
                  <FileCheck className='h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-blue-900'>Declaration confirmed</p>
                    <p className='mt-0.5 text-xs text-blue-700 leading-relaxed'>
                      The information in this return was declared correct and complete at the time of submission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        // Traditional MTD: Show "Submitting" state
        if (!isSaFlow && currentStep === 6 && isSubmitting && !isSubmitted) {
          return (
            <div className='space-y-6'>
              <div className='text-gray-500 text-sm mb-2'>
                {userProfile.fullName}'s tax return: {taxYear.replace('-', ' to ')}
              </div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-8'>Submit your return</h1>

              <div className='space-y-6'>
                <h2 className='text-xl sm:text-2xl font-bold text-gray-900'>Submitting</h2>

                <div className='flex justify-center py-8'>
                  <Loader2 className='h-12 w-12 animate-spin text-gray-400' />
                </div>

                <div className='space-y-4 text-gray-700'>
                  <p>
                    You may experience a short delay whilst your return is submitted. We are waiting for notification that your return has been successfully received by HM Revenue and Customs (HMRC).
                  </p>
                </div>
              </div>
            </div>
          )
        }

        if (!isSaFlow && currentStep === 6) {
          // Show Submit content for Traditional MTD
        return (
          <div className='space-y-6'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Submit Your Return</h2>
            </div>

            <div className='space-y-6'>

              {isPaidOrCovered ? (
                <div className='rounded-lg border border-green-200 bg-green-50 p-6'>
                  <div className='flex items-start gap-3'>
                    <CheckCircle2 className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-green-900'>Payment Complete - Ready to Submit</h3>
                      <p className='mt-2 text-sm text-green-700 leading-relaxed'>
                        Your platform payment has been successfully processed. You can now review your return details below and submit directly to HMRC.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='rounded-lg border border-blue-200 bg-blue-50 p-6'>
                  <div className='flex items-start gap-3'>
                    <AlertCircle className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
                    <div className='flex-1'>
                      <h3 className='text-lg font-semibold text-blue-900'>Next: Platform Service Payment Required</h3>
                      <p className='mt-2 text-sm text-blue-700 leading-relaxed'>
                        Before submitting your return to HMRC, you'll need to complete payment for your selected BritFiler service plan.
                        This payment covers our platform service fee for preparing and submitting your tax return.
                      </p>
                      <div className='mt-3 rounded-lg border border-green-200 bg-green-50 p-3'>
                        <p className='text-xs font-semibold text-green-900 mb-1'>Important: This is a service fee, not your tax payment</p>
                        <p className='text-xs text-green-800 leading-relaxed'>
                          The payment you're about to make is for the <strong>BritFiler platform service</strong> (filing assistance, calculation, and submission).
                          This is separate from any tax you may owe to HMRC. If you have tax due, HMRC will contact you separately with payment instructions after your return is processed.
                        </p>
                      </div>
                      <p className='mt-3 text-sm text-blue-700 leading-relaxed'>
                        Click "Proceed to Payment" below to securely pay via Stripe. After payment, your return will be ready for submission to HMRC.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className='rounded-lg border border-gray-200 bg-white p-6'>
                <h3 className='text-base font-semibold text-gray-900 mb-4'>Declaration</h3>
                <div className='space-y-4 text-sm text-gray-700 leading-relaxed'>
                  <p>I confirm that:</p>
                  <ul className='list-disc list-inside space-y-2 ml-4'>
                    <li>The information I have given is correct and complete to the best of my knowledge</li>
                    <li>I understand that giving false information can lead to penalties</li>
                    <li>I have reviewed all the information in my return</li>
                    <li>I understand that HMRC will use the information to calculate any tax due</li>
                  </ul>
                </div>
              </div>

              {calculationResults && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-6'>
                  <h3 className='text-base font-semibold text-gray-900 mb-4'>Summary</h3>
                  <div className='space-y-2'>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Tax Year:</span>
                      <span className='text-sm font-semibold text-gray-900'>{taxYear.replace('-', ' to ')}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>Total Tax Due:</span>
                      <span className='text-sm font-semibold text-red-600'>
                        £{(calculationResults.response?.calculation?.taxCalculation?.totalIncomeTaxAndNicsDue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-sm text-gray-600'>UTR:</span>
                      <span className='text-sm font-semibold text-gray-900'>{userProfile.utr}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className='flex items-center justify-center pt-4'>
                {isPaidOrCovered ? (
                  <button
                    onClick={handleSubmitToHmrc}
                    disabled={isLoading}
                    className='flex items-center gap-2 rounded-xl bg-green-600 px-8 py-4 text-base font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 transition shadow-lg'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        Submitting to HMRC...
                      </>
                    ) : (
                      <>
                        <ArrowRight className='h-5 w-5' />
                        Submit to HMRC
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubmitReturn()}
                    disabled={isLoading}
                    className='flex items-center gap-2 rounded-xl bg-blue-600 px-8 py-4 text-base font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 transition shadow-lg'
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ArrowRight className='h-5 w-5' />
                        Proceed to Payment
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )
        }
        // Case 7 ends here - either HMRC credentials (SA) or Submit (Traditional MTD)
        return null // Fallback - should never reach here

      default:
        return (
          <div className='flex min-h-[400px] items-center justify-center'>
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900'>Step {currentStep}</h3>
              <p className='mt-2 text-sm text-gray-600'>Content for this step is being prepared...</p>
            </div>
          </div>
        )
    }
  }

  const completedStepNumbers = new Set(
    steps.filter((s) => s.status === 'completed').map((s) => s.number)
  )

  const profileFullName =
    [personalDetails.firstName, personalDetails.lastName].filter(Boolean).join(' ') ||
    userProfile.fullName ||
    '—'
  const profileAddress =
    [
      personalDetails.address.line1,
      personalDetails.address.line2,
      personalDetails.address.line3,
      personalDetails.address.line4,
      personalDetails.address.postcode,
      personalDetails.address.country,
    ]
      .filter(Boolean)
      .join(', ') ||
    userProfile.address ||
    '—'
  const profileRows: WizardSidebarRow[] = [
    { label: 'Full Name', value: profileFullName },
    { label: 'Unique Taxpayer Number', value: userProfile.utr || '—' },
    { label: 'National Insurance Number', value: userProfile.nino || '—' },
    { label: 'Address', value: profileAddress },
  ]

  return (
    <AuthenticatedLayout activeTab='dashboard'>
      {isInitializing || !authCheckComplete ? (
        <div className='flex min-h-screen items-center justify-center bg-gray-50'>
          <div className='text-center'>
            <Loader2 className='mx-auto h-16 w-16 animate-spin text-gray-400' />
            <p className='mt-6 text-lg text-gray-600'>Initializing Self Assessment...</p>
          </div>
        </div>
      ) : !isSaFlow && !isHmrcAuthorized ? (
        <div className='flex min-h-screen items-center justify-center bg-gray-50'>
          <div className='max-w-2xl rounded-2xl border border-amber-200 bg-white p-10 shadow-xl'>
            <div className='mb-6 flex items-center justify-center'>
              <div className='flex h-16 w-16 items-center justify-center rounded-full bg-amber-100'>
                <AlertCircle className='h-10 w-10 text-amber-600' />
              </div>
            </div>
            <h2 className='mb-4 text-center text-xl sm:text-2xl font-bold text-gray-900'>HMRC Authorization Required</h2>
            <p className='mb-6 text-center text-gray-700 leading-relaxed'>
              You are using the <strong>Making Tax Digital (MTD)</strong> flow, which requires direct authorization with HMRC to access your tax information and submit returns.
            </p>
            
            <div className='mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4'>
              <h3 className='mb-2 text-sm font-semibold text-blue-900'>Why is this needed?</h3>
              <p className='text-sm text-blue-800 leading-relaxed'>
                The MTD flow integrates directly with HMRC's APIs for full Making Tax Digital capabilities. This requires OAuth authorization to ensure secure access to your tax records.
              </p>
            </div>

            <div className='mb-8 rounded-lg border border-green-200 bg-green-50 p-4'>
              <h3 className='mb-2 text-sm font-semibold text-green-900'>Alternative: Self Assessment</h3>
              <p className='text-sm text-green-800 leading-relaxed'>
                If you prefer a simpler process without HMRC OAuth, you can use the <strong>Self Assessment</strong> flow which only requires your regular account login and is ideal for straightforward returns.
              </p>
            </div>

            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <button
                onClick={() => {
                  // Initiate HMRC OAuth flow
                  window.location.href = '/hmrc/authorize'
                }}
                className='inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white hover:bg-blue-700 transition shadow-lg'
              >
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
                Authorize with HMRC
              </button>
              <button
                onClick={() => {
                  // Switch to SA Online flow
                  const params = new URLSearchParams(searchParams.toString())
                  params.set('flow', 'sa')
                  navigate(`/add-self-assessment?${params.toString()}`)
                }}
                className='inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-6 py-3 text-base font-semibold text-white hover:bg-green-700 transition shadow-lg'
              >
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                </svg>
                Switch to Self Assessment
              </button>
            </div>

            <div className='mt-6 text-center'>
              <button
                onClick={() => {
                  // Preserve flow parameter when going back
                  const backUrl = '/add-self-assessment?flow=traditional'
                  navigate(backUrl)
                }}
                className='text-sm text-gray-600 hover:text-gray-900 transition font-medium'
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      ) : error && !userProfile.nino ? (
        <div className='flex min-h-screen items-center justify-center bg-gray-50'>
          <div className='max-w-md rounded-lg border border-red-200 bg-red-50 p-8 text-center'>
            <AlertCircle className='mx-auto h-12 w-12 text-red-600' />
            <h2 className='mt-4 text-lg font-semibold text-red-900'>Unable to Load</h2>
            <p className='mt-2 text-sm text-red-700'>{error}</p>
            <button
              onClick={() => {
                // Preserve flow parameter when going back
                const backUrl = isSaFlow 
                  ? '/add-self-assessment?flow=sa'
                  : '/add-self-assessment?flow=traditional'
                navigate(backUrl)
              }}
              className='mt-6 rounded-lg bg-red-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition'
            >
              Go Back
            </button>
          </div>
        </div>
      ) : (
      <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
        {/* Header with Back Button */}
        <div className='mx-auto mb-8'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
            <div className='flex items-center gap-3'>
              <button
                onClick={() => {
                  // Preserve flow parameter when going back
                  const backUrl = isSaFlow
                    ? '/add-self-assessment?flow=sa'
                    : '/add-self-assessment?flow=traditional'
                  navigate(backUrl)
                }}
                className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm hover:bg-gray-100 hover:shadow-md transition'
              >
                <ArrowLeft className='h-5 w-5' />
              </button>

              <div className='flex items-center gap-2 flex-wrap'>
                <h1 className='text-lg sm:text-xl font-bold text-gray-900'>Self Assessment</h1>
                {/* Flow Indicator Badge — only shown for the MTD flow, since the SA
                    flow title already reads "Self Assessment" (avoids duplication) */}
                {!isSaFlow && (
                  <span className='inline-flex items-center rounded-full border border-green-200 bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800'>
                    MTD
                  </span>
                )}
              </div>
            </div>

            {/* Tax Year + taxpayer details trigger */}
            <div className='flex items-center gap-2 ml-13 sm:ml-0'>
              <button
                type='button'
                onClick={() => setDetailsOpen(true)}
                className='inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 transition'
              >
                <User className='h-4 w-4' />
                Details
              </button>
              <label className='text-sm font-medium text-gray-600'>Tax Year:</label>
              <span className='rounded-lg border border-gray-200 bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-500'>
                {taxYear}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='mx-auto'>
          {/* Steps as a full-width tab header */}
          <div className='mb-6'>
            <WizardStepNav
              steps={steps}
              currentStep={currentStep}
              completedSteps={completedStepNumbers}
              onStepClick={handleStepClick}
              variant='tabs'
            />
          </div>

          {/* Full-width content area */}
          <div className='min-w-0'>
              <div className='rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 xl:p-8 shadow-sm'>
                {renderStepContent()}

                {/* Action Buttons - hidden when filing is already submitted */}
                {!(isSubmitted && currentStep === 6) && (
                <div className='mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-6'>
                  <button
                    onClick={handlePrevious}
                    disabled={currentStep === 1 || isLoading}
                    className='flex items-center gap-2 rounded-xl bg-[#001B38] px-4 sm:px-6 py-3 text-sm font-semibold text-white hover:bg-[#002a50] disabled:cursor-not-allowed disabled:opacity-40 transition'
                  >
                    <ArrowLeft className='h-4 w-4' />
                    Previous
                  </button>

                  <button
                    onClick={handleSaveAndClose}
                    disabled={isLoading}
                    className='rounded-xl border-2 border-gray-300 px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition'
                  >
                    Save & Close
                  </button>

                  {/* Show Proceed button for all steps except the final submit step (7) */}
                  {currentStep < 6 ? (
                    <button
                      onClick={handleNext}
                      disabled={isLoading}
                      className='flex items-center gap-2 rounded-xl bg-[#001B38] px-6 py-3 text-sm font-semibold text-white hover:bg-[#002a50] disabled:cursor-not-allowed disabled:opacity-50 transition'
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className='h-4 w-4 animate-spin' />
                          Processing...
                        </>
                      ) : (
                        <>
                          {currentStep < 3 ? 'Next' : 'Proceed'}
                          <ArrowLeft className='h-4 w-4 rotate-180' />
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <WizardDetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} title='Your Details'>
        <WizardSidebar rows={profileRows} width='w-full' />
      </WizardDetailsModal>

      {/* Pricing plan picker — opened at the payment step when no plan was chosen
          upstream. On selection we set the plan and continue straight to Stripe
          checkout with the full SA context (mirrors the CT600 flow). */}
      <SelfAssessmentPlanSelectionModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        selectOnly
        onPlanSelected={(slug) => {
          setPlanSlug(slug)
          setIsPlanModalOpen(false)
          void handleSubmitReturn(slug)
        }}
      />
      <TrustpilotReviewModal
        open={showReviewPrompt}
        onClose={() => setShowReviewPrompt(false)}
        filingLabel='Self Assessment return'
      />
    </AuthenticatedLayout>
  )
}
